import React, { useEffect, useMemo, useState } from 'react';
import { calculateAnnualAttainedCii, getFinalCalculatedCiiRating } from '../core/ciiFinalRating';
import { useAuth } from '../../auth';
import { getGeneralInfoOfVessel, getPropulsionSystem } from '../../Management/core/_requests';
import {
    getAssignmentById,
    getCreatedReportById,
    getCargoOperations,
    getConsumptionByVoyageReport,
    getCiiVoyageRowsByReportIds,
    getCiiRuleSet,
    getCiiVesselSetting,
    getDprList,
    getSubmittedReportsFull,
    getVoyagesByVessel,
    saveCiiVesselSetting,
    upsertCiiFuelFactor,
    upsertCiiReductionFactor,
    upsertCiiShipTypeRule,
    upsertCiiVoyageRow,
    CiiVoyageRowDto,
    CiiRuleSetDto,
    CiiShipTypeRuleDto,
    CiiFuelFactorDto,
    CiiReductionFactorDto,
    CiiVesselSettingDto,
} from '../core/_requests';
import { VesselGeneralInfo, PropulsionSystem } from '../../Management/core/_models';
import { Vessel } from '../core/_models';
import { ToastContainer, toast } from 'react-toastify';
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels, NO_ACCESSIBLE_VESSELS_MESSAGE, formatAccessibleVesselError } from '../../../shared/access/accessibleVessels'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'

interface ReportFieldValueFullDto {
    field: {
        label: string;
    };
    valueText: string;
}

interface ReportDtoFull {
    id?: number;
    reportType?: string;
    status?: string;
    assignment?: { id?: number };
    voyage?: number;
    vesselId?: number;
    hasWarnings?: boolean;
    noOfWarnings?: number;
    createdDateTime: string;
    submittedDateTime?: string;
    values: ReportFieldValueFullDto[];
}

interface AggregatedVoyageData {
    fromDate: string;
    toDate: string;
    sourceDateTime?: string;
    distance: number;
    hoursAtSea: number;
    hoursAtAnchor: number;
    cargoCarried: number;
    cargoWeightProvided?: boolean;
    meHsfo: number;
    aeHsfo: number;
    boilerHsfo: number;
    meMgo: number;
    aeMgo: number;
    boilerMgo: number;
    totalFuel: number;
    totalCo2: number;
    bunkerReceivedHfo: number;
    bunkerReceivedMgo: number;
    fuelBreakdown?: FuelConsumptionMap;
    correctionBreakdown?: FuelCorrectionMap;
    correctionSourceByFuel?: FuelCorrectionSourceMap;
    unmappedFuelTypes?: string[];
    reportName?: string;
    bunkerReportName?: string;
    hfoRob?: number;
    mgoRob?: number;
    cargoHandled: number;
    fcVoyageHsfo?: number | string;
    fcVoyageMgo?: number | string;
    fcElectricalHsfo?: number | string;
    fcElectricalMgo?: number | string;
    fcOtherHsfo?: number | string;
    fcOtherMgo?: number | string;
    tfHsfo?: number | string;
    tfMgo?: number | string;
    distanceAdjustment?: number | string;
    fcBoilerHsfo?: number | string;
    fcBoilerMgo?: number | string;
    reeferKwh?: number | string;
    reeferSfoc?: number | string;
    reeferFuelCode?: string;
    reeferContainersOnboard?: boolean;
    cargoCoolingSystemInUse?: boolean;
    vesselCondition?: string;
    portNameLocation?: string;
    eventType?: string;
    stsVoyage?: string;
    boilerUsed?: string;
    remarks?: string;
    voyageNo?: string;
    leg?: string;
    avgSulphurHsfo?: number | string;
    avgSulphurMgo?: number | string;
    distanceDisplay?: string;
    reportKind?: ReportKind;
    reportDateTime?: string;
    departureDateTime?: string;
    arrivalDateTime?: string;
    departurePortLocation?: string;
    arrivalPortLocation?: string;
    anchorArrival?: boolean;
    departureFromState?: VoyageEventState | null;
    vesselId?: number;
    isBoundaryRow?: boolean;
    reportId?: number;
    reportAssignmentId?: number;
    reportVoyageId?: number;
    reportTypeName?: string;
    reportStatus?: string;
    reportHasWarnings?: boolean;
    reportNoOfWarnings?: number;
}

interface BunkerTabRow {
    date: string;
    reportName: string;
    hfoRob: number;
    mgoRob: number;
    bunkerReceivedHfo: number;
    bunkerReceivedMgo: number;
    fuelReceived?: Record<string, number>;
}

interface SummaryMetrics {
    totalFuel: number;
    totalCo2: number;
    totalDistance: number;
    totalCargo: number;
    totalBunker: number;
    transportWork: number;
    capacity: number;
    attainedCii: number;
}

interface QuarterlyMetrics extends SummaryMetrics {
    loadedDistance: number;
    ballastDistance: number;
    fuelConsumedHsfo: number;
    fuelConsumedMgo: number;
    totalDaysSea: number;
    totalDaysAnchor: number;
    cargoCarried: number;
    sox: number;
    pm: number;
    nox: number;
    co: number;
    eeoi: number;
    referenceCii: number;
    quarterlyRating: string;
}

interface ProgressiveCiiRowMetrics {
    attainedCii: number;
    ciiRating: string;
    ciiRatingClass: string;
    cumulativeDistance?: number;
}


interface AnnualOverviewMetrics {
    totalFcHsfo: number;
    totalFcMgo: number;
    totalFcVoyageHsfo: number;
    totalFcVoyageMgo: number;
    totalFcBoilerHsfo: number;
    totalFcBoilerMgo: number;
    totalFcOtherHsfo: number;
    totalFcOtherMgo: number;
    totalCo2: number;
    sox: number;
    nox: number;
    co: number;
    pm: number;
    totalDistance: number;
    totalDaysSea: number;
    totalDaysPort: number;
    transportWork: number;
    fuelPerDistance: number;
    fuelPerTransportWork: number;
    co2PerDistance: number;
    eeoi: number;
    attainedCii: number;
    ciiRating: string;
}

interface ConsumptionRobBreakdown {
    meHsfo: number;
    aeHsfo: number;
    boilerHsfo: number;
    meMgo: number;
    aeMgo: number;
    boilerMgo: number;
    hfoRob: number;
    mgoRob: number;
    fuels?: FuelConsumptionMap;
    correctionsByFuel?: FuelCorrectionMap;
    unmappedFuelTypes?: string[];
}

interface FuelConsumptionBreakdown {
    me: number;
    ae: number;
    boiler: number;
    other: number;
    rob: number;
}

type FuelConsumptionMap = Record<string, FuelConsumptionBreakdown>;

interface FuelCorrectionBreakdown {
    fcElectrical: number;
    fcBoiler: number;
    fcOther: number;
}

interface FuelCorrectionSource {
    fcElectrical?: 'Auto' | 'Saved';
    fcBoiler?: 'Auto' | 'Saved';
    fcOther?: 'Auto' | 'Saved';
}

type FuelCorrectionMap = Record<string, FuelCorrectionBreakdown>;
type FuelCorrectionSourceMap = Record<string, FuelCorrectionSource>;

type VoyageEventState = 'SAILING' | 'AT_ANCHOR' | 'AT_BERTH';
type ReportKind =
    | 'ARRIVAL'
    | 'DEPARTURE'
    | 'COSP'
    | 'EOSP'
    | 'NOON_SEA'
    | 'NOON_ANCHOR'
    | 'NOON_BERTH'
    | 'OTHER';

const CARBON_FACTORS = {
    HSFO: 3.114,
    MGO: 3.206,
    LFO: 3.1510,
};
const DEFAULT_REFERENCE_CII = 4.983;

const CiiSummary: React.FC = () => {
    const [activeTab, setActiveTab] = useState('shipData');
    const [vessels, setVessels] = useState<Vessel[]>([]);
    const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('');
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [generalInfo, setGeneralInfo] = useState<VesselGeneralInfo | null>(null);
    const [propulsion, setPropulsion] = useState<PropulsionSystem | null>(null);
    const [yearlyReports, setYearlyReports] = useState<ReportDtoFull[]>([]);
    const [cargoOps, setCargoOps] = useState<any[]>([]);
    const [, setDprRecords] = useState<any[]>([]);
    const [aggregatedData, setAggregatedData] = useState<AggregatedVoyageData[]>([]);
    const [ciiRowByReportId, setCiiRowByReportId] = useState<Record<number, CiiVoyageRowDto>>({});
    const [ciiRuleSet, setCiiRuleSet] = useState<CiiRuleSetDto | null>(null);
    const [vesselCiiSetting, setVesselCiiSetting] = useState<CiiVesselSettingDto | null>(null);
    const [selectedCiiShipType, setSelectedCiiShipType] = useState<string>('');
    const [isSavingShipData, setIsSavingShipData] = useState(false);
    const [ruleEditorType, setRuleEditorType] = useState<'fuel' | 'year' | 'ship'>('fuel');
    const [selectedRuleId, setSelectedRuleId] = useState<string>('');
    const [ruleEditorJson, setRuleEditorJson] = useState<string>('');
    const [isSavingRuleEditor, setIsSavingRuleEditor] = useState(false);
    const [bunkerTabRows, setBunkerTabRows] = useState<BunkerTabRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [voyagePage, setVoyagePage] = useState(1);
    const [dirtyRowIds, setDirtyRowIds] = useState<Set<number>>(new Set());
    const [isSavingVoyageData, setIsSavingVoyageData] = useState(false);

    const { currentUser, can } = useAuth();

    const isSuperAdmin = () => {
        const roleName = String((currentUser as any)?.role?.roleType || (currentUser as any)?.role?.roleName || '').toUpperCase();
        const roleId = Number((currentUser as any)?.role?.id || (currentUser as any)?.roleId || 0);
        return roleName === 'SUPER_ADMIN' || roleId === 1;
    };

    const hasUpdateVoyageRowPermission = can("UPDATE_CII_VOYAGE_ROW") || can("UPDATE_CII_ROW") || isSuperAdmin();

    const tabs = [
        { id: 'shipData', label: 'Ship Data' },
        { id: 'voyageData', label: 'Voyage Data' },
        { id: 'bunkerReport', label: 'Bunker Report' },
        { id: 'cargoReceived', label: 'Cargo Received' },
        { id: 'quarterlySummary', label: 'Quarterly Summary' },
        { id: 'annualSummary', label: 'Annual Summary' },
        { id: 'ciiRules', label: 'CII Rules' },
    ];

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const voyageRowsPerPage = 10;

    useEffect(() => {
        const fetchVessels = async () => {
            try {
                const list = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser());
                setVessels(list);

                // If ship login, auto-select the user's vessel
                if (currentUser?.vessel?.id) {
                    const exists = list.some((v) => Number(v.id) === Number(currentUser.vessel?.id))
                    setSelectedVesselId(exists ? currentUser.vessel.id : '');
                }
            } catch (error) {
                console.error('Error fetching vessels:', error);
                toast.error(formatAccessibleVesselError(error, 'Failed to fetch vessel list'));
            }
        };
        fetchVessels();
    }, [currentUser]);

    const { state: globalState, vessels: globalVessels } = useGlobalFilter();

    // Sync global header vessel filter → local selectedVesselId
    useEffect(() => {
        const gvId = globalState.vessel?.id;
        if (gvId != null) {
            setSelectedVesselId(Number(gvId));
        } else if (globalVessels && globalVessels.length > 0) {
            setSelectedVesselId(Number(globalVessels[0].id));
        } else if (vessels && vessels.length > 0) {
            setSelectedVesselId(Number(vessels[0].id));
        } else {
            setSelectedVesselId('');
        }
    }, [globalState.vessel, globalVessels, vessels]);

    useEffect(() => {
        getCiiRuleSet()
            .then(setCiiRuleSet)
            .catch((error) => {
                console.error('Error fetching CII rules:', error);
                toast.error(getApiErrorMessage(error, 'Failed to fetch CII rules'));
            });
    }, []);

    useEffect(() => {
        if (selectedVesselId) {
            fetchShipDetails(selectedVesselId);
            fetchYearlyData(selectedVesselId, selectedYear);
        } else {
            setGeneralInfo(null);
            setPropulsion(null);
            setVesselCiiSetting(null);
            setSelectedCiiShipType('');
            setYearlyReports([]);
            setCargoOps([]);
            setDprRecords([]);
            setAggregatedData([]);
            setBunkerTabRows([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVesselId, selectedYear, ciiRuleSet]);

    useEffect(() => {
        setVoyagePage(1);
    }, [selectedVesselId, selectedYear, aggregatedData.length]);

    useEffect(() => {
        setSelectedRuleId('');
        setRuleEditorJson('');
    }, [ruleEditorType]);

    const fetchShipDetails = async (vesselId: number) => {
        try {
            const [genInfo, propSystem, ciiSetting] = await Promise.all([
                getGeneralInfoOfVessel(vesselId).catch(() => null),
                getPropulsionSystem(vesselId).catch(() => null),
                getCiiVesselSetting(vesselId).catch(() => null)
            ]);
            setGeneralInfo(genInfo);
            setPropulsion(propSystem);
            setVesselCiiSetting(ciiSetting);
            setSelectedCiiShipType(ciiSetting?.shipTypeCode || normalizeShipTypeCode(vessels.find(v => v.id === vesselId)?.vesselType || 'TANKER'));
        } catch (error) {
            console.error('Error fetching ship details:', error);
        }
    };

    const assignmentCache = new Map<number, any>();

    const getFieldLabelMapFromAssignment = (assignment: any) => {
        const fieldLabelMap = new Map<number, string>();
        const template = assignment?.template || assignment?.customTemplate;
        const menus = Array.isArray(template?.menus) ? template.menus : [];

        menus.forEach((menu: any) => {
            (menu?.submenus || []).forEach((submenu: any) => {
                (submenu?.fields || []).forEach((field: any) => {
                    if (field?.id != null) {
                        fieldLabelMap.set(Number(field.id), field.label || '');
                    }
                });
            });
        });

        return fieldLabelMap;
    };


    const enrichCreatedReportsWithAssignmentLabels = async (reports: any[]) => {
        return Promise.all(
            reports.map(async (report: any) => {
                const assignmentId = Number(report?.assignment?.id);
                let assignment = assignmentId ? assignmentCache.get(assignmentId) : null;

                if (!assignment && assignmentId) {
                    assignment = await getAssignmentById(assignmentId).catch(() => null);
                    if (assignment) assignmentCache.set(assignmentId, assignment);
                }

                const fieldLabelMap = getFieldLabelMapFromAssignment(assignment);

                return {
                    id: report.id,
                    reportType: report.reportType,
                    status: report.status,
                    assignment: report.assignment,
                    voyage: report.voyage,
                    vesselId: report.vesselId,
                    hasWarnings: report.hasWarnings,
                    noOfWarnings: report.noOfWarnings,
                    createdDateTime: report.createdDateTime,
                    submittedDateTime: report.submittedDateTime,
                    values: Array.isArray(report.values)
                        ? report.values.map((value: any) => ({
                            field: {
                                label: fieldLabelMap.get(Number(value.field)) || value.label || '',
                            },
                            valueText: value.valueText || '',
                        }))
                        : [],
                } as ReportDtoFull;
            })
        );
    };

    const buildFallbackVoyageRows = (
        reports: ReportDtoFull[],
        voyages: any[] = [],
        ciiRowsByReportId: Record<number, CiiVoyageRowDto> = {}
    ) => {
        let activeVoyageNo = '';
        let currentEventState: VoyageEventState = 'SAILING';
        const { prevByVoyageNo } = buildVoyageSequence(voyages || []);
        const fallbackRows: AggregatedVoyageData[] = [];
        let pendingDepartureResolution = false;
        let lastReportDateTime = '';

        [...reports]
            .filter(r => !isBunkerReport(r))
            .sort((a, b) => {
                const aTime = toDateTimeMs(getReportSortDateTime(a));
                const bTime = toDateTimeMs(getReportSortDateTime(b));
                if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
                return (getReportSortDateTime(a) || '').localeCompare(getReportSortDateTime(b) || '');
            })
            .forEach((report) => {
                const reportDateTime = getReportSortDateTime(report);
                const reportDate = getReportDateOnly(report) || toDateOnly(reportDateTime);
                const voyageNumberFromReport = findFieldText(report.values || [], ['voyage no', 'voyage number']);
                const explicitVoyageNo = voyageNumberFromReport || '';
                const voyageByNumber = explicitVoyageNo
                    ? (voyages || []).find((v: any) =>
                        String(v?.voyageNumber || '').trim().toUpperCase() === String(explicitVoyageNo).trim().toUpperCase()
                    )
                    : null;
                const matchingVoyage = voyageByNumber || findMatchingVoyageByDate(reportDateTime || reportDate, voyages || []);

                const reportKind = getReportKindFromReport(report);
                const rawEventType = getEventTypeFromReport(report);
                const cargoWeight = getCargoWeightFromReport(report);
                const persistedCiiRow = report?.id != null ? ciiRowsByReportId[Number(report.id)] : undefined;
                const persistedStsVoyage = persistedCiiRow?.stsVoyage;
                const persistedBoilerUsed = persistedCiiRow?.boilerUsed;
                const persistedFcVoyageHsfo = parseOptionalNumber(persistedCiiRow?.fcVoyageHsfo);
                const persistedFcVoyageMgo = parseOptionalNumber(persistedCiiRow?.fcVoyageMgo);
                const persistedHsfoSulphur = parseOptionalNumber(persistedCiiRow?.hsfoSulphur);
                const persistedMgoSulphur = parseOptionalNumber(persistedCiiRow?.mgoSulphur);
                const persistedHsfoFuelDetail = getPersistedFuelDetail(persistedCiiRow, 'HSFO');
                const persistedMgoFuelDetail = getPersistedFuelDetail(persistedCiiRow, 'MGO');
                const persistedDistanceAdjustment = parseOptionalNumber(persistedCiiRow?.distanceAdjustment);
                const correctionData = buildCorrectionBreakdownForRow(undefined, persistedCiiRow);
                const persistedRemarks = persistedCiiRow?.remarks || '';
                const resolvedState = resolveRowAndNextEventState(
                    reportKind,
                    currentEventState,
                    report,
                    rawEventType
                );
                const primaryReportDateTime =
                    buildReportUtcDateTime(report) ||
                    reportDateTime ||
                    reportDate ||
                    '';
                const departureBoundaryDateTime = primaryReportDateTime;
                const arrivalBoundaryDateTime = primaryReportDateTime;
                let rowState: VoyageEventState =
                    reportKind === 'COSP'
                        ? getDeparturePrevStateFromEventFlags(report, currentEventState)
                        : resolvedState.rowState;
                let nextState: VoyageEventState =
                    reportKind === 'COSP'
                        ? 'SAILING'
                        : resolvedState.nextState;

                if (pendingDepartureResolution && reportKind !== 'DEPARTURE') {
                    if (reportKind === 'ARRIVAL') {
                        const arrivalState = getArrivalNextStateFromEventFlags(
                            report,
                            rawEventType,
                            currentEventState
                        );
                        rowState = arrivalState;
                        nextState = arrivalState;
                    } else {
                        rowState = 'SAILING';
                        nextState = 'SAILING';
                    }
                    pendingDepartureResolution = false;
                }

                if (reportKind === 'DEPARTURE') {
                    rowState = currentEventState;
                    nextState = 'SAILING';
                    pendingDepartureResolution = false;
                }

                const foundVoyageNo =
                    voyageNumberFromReport ||
                    activeVoyageNo ||
                    matchingVoyage?.voyageNumber ||
                    '';
                const reportVoyageNo = foundVoyageNo || activeVoyageNo || '';
                const previousVoyageFallback =
                    activeVoyageNo && activeVoyageNo !== reportVoyageNo
                        ? activeVoyageNo
                        : '';
                const previousVoyageNo = reportVoyageNo
                    ? (prevByVoyageNo.get(reportVoyageNo) || previousVoyageFallback || '')
                    : '';
                const rowVoyageNo =
                    reportKind === 'COSP'
                        ? (previousVoyageNo || '')
                        : (reportVoyageNo || '');

                if (reportKind === 'COSP') {
                    if (reportVoyageNo) {
                        activeVoyageNo = reportVoyageNo;
                    }
                } else if (reportVoyageNo) {
                    activeVoyageNo = reportVoyageNo;
                }

                const isBoundaryReport = reportKind === 'COSP' || reportKind === 'ARRIVAL' || reportKind === 'DEPARTURE';
                const metricsAllowed = !(reportKind === 'COSP' && !previousVoyageNo);
                if (metricsAllowed && rowVoyageNo) {
                    const rowToDateTime = primaryReportDateTime || reportDateTime || reportDate;
                    let metricsDateTime = lastReportDateTime || rowToDateTime;
                    let metricsToDateTime = rowToDateTime;

                    if (reportKind === 'ARRIVAL' && arrivalBoundaryDateTime) {
                        metricsToDateTime = arrivalBoundaryDateTime;
                    } else if ((reportKind === 'DEPARTURE' || reportKind === 'COSP' || reportKind === 'EOSP') && departureBoundaryDateTime) {
                        metricsToDateTime = departureBoundaryDateTime;
                    }

                    const computedHours = getHoursByEventState(rowState, metricsDateTime, metricsToDateTime);
                    fallbackRows.push({
                        fromDate: metricsDateTime || '',
                        toDate: metricsToDateTime || '',
                        sourceDateTime: metricsDateTime || '',
                        distance: getDistanceTravelledFromNoonReport(report) ?? 0,
                        hoursAtSea: computedHours.hoursAtSea,
                        hoursAtAnchor: computedHours.hoursAtAnchor,
                        cargoCarried: isBoundaryReport ? 0 : (cargoWeight ?? 0),
                        cargoWeightProvided: !isBoundaryReport && cargoWeight !== null,
                        meHsfo: 0,
                        aeHsfo: 0,
                        boilerHsfo: 0,
                        meMgo: 0,
                        aeMgo: 0,
                        boilerMgo: 0,
                        totalFuel: 0,
                        totalCo2: 0,
                        bunkerReceivedHfo: 0,
                        bunkerReceivedMgo: 0,
                        reportName: report.reportType || '--',
                        bunkerReportName: '--',
                        hfoRob: 0,
                        mgoRob: 0,
                        cargoHandled: 0,
                        correctionBreakdown: correctionData.corrections,
                        correctionSourceByFuel: correctionData.sources,
                        fcVoyageHsfo:
                            persistedFcVoyageHsfo ??
                            (report ? (parseFloat(findFieldTextExact(report.values || [], ['FC voyage HSFO'])) || undefined) : undefined),
                        fcVoyageMgo:
                            persistedFcVoyageMgo ??
                            (report ? (parseFloat(findFieldTextExact(report.values || [], ['FC voyage MGO'])) || undefined) : undefined),
                        fcElectricalHsfo: correctionData.corrections.HSFO?.fcElectrical,
                        fcElectricalMgo: correctionData.corrections.MGO?.fcElectrical,
                        fcBoilerHsfo: correctionData.corrections.HSFO?.fcBoiler,
                        fcBoilerMgo: correctionData.corrections.MGO?.fcBoiler,
                        fcOtherHsfo: correctionData.corrections.HSFO?.fcOther,
                        fcOtherMgo: correctionData.corrections.MGO?.fcOther,
                        tfHsfo: parseOptionalNumber(persistedHsfoFuelDetail?.fcVoyageAdjustment),
                        tfMgo: parseOptionalNumber(persistedMgoFuelDetail?.fcVoyageAdjustment),
                        distanceAdjustment: persistedDistanceAdjustment,
                        reeferKwh: parseOptionalNumber(persistedCiiRow?.reeferKwh),
                        reeferSfoc: parseOptionalNumber(persistedCiiRow?.reeferSfoc),
                        reeferFuelCode: persistedCiiRow?.reeferFuelCode || '',
                        reeferContainersOnboard: hasFieldWithExactLabel(report?.values || [], ['Refrigerated Containers Onboard'])
                            ? getCheckboxFieldState(report, ['Refrigerated Containers Onboard'])
                            : true,
                        cargoCoolingSystemInUse: hasFieldWithExactLabel(report?.values || [], ['Cargo Cooling System In Use'])
                            ? getCheckboxFieldState(report, ['Cargo Cooling System In Use'])
                            : false,
                        vesselCondition: getVesselConditionFromReport(report, matchingVoyage?.loadingCondition),
                        portNameLocation:
                            rowState === 'SAILING'
                                ? (getPortValueFromReport(report.values || [], ['departure port', 'port name', 'location']) ||
                                    matchingVoyage?.departurePort ||
                                    '')
                                : (getPortValueFromReport(report.values || [], ['arrival port', 'port name', 'location']) ||
                                    matchingVoyage?.arrivalPort ||
                                    ''),
                        eventType: toVoyageEventLabel(rowState),
                        reportKind,
                        stsVoyage: persistedStsVoyage || findFieldText(report.values || [], ['sts voyage']) || '',
                        boilerUsed: persistedBoilerUsed || '',
                        remarks: persistedRemarks,
                        voyageNo: rowVoyageNo || '',
                        leg: resolveLegDisplay(report, matchingVoyage, persistedCiiRow),
                        avgSulphurHsfo: persistedHsfoSulphur,
                        avgSulphurMgo: persistedMgoSulphur,
                        reportId: report.id,
                        reportAssignmentId: report.assignment?.id,
                        reportVoyageId: report.voyage,
                        reportTypeName: report.reportType,
                        reportStatus: report.status,
                        reportHasWarnings: report.hasWarnings,
                        reportNoOfWarnings: report.noOfWarnings,
                    } as AggregatedVoyageData);
                }

                const shouldCreateNextEventRow =
                    (reportKind === 'COSP' || reportKind === 'ARRIVAL' || reportKind === 'DEPARTURE') &&
                    rowState !== nextState;

                if (shouldCreateNextEventRow) {
                    const nextVoyageNo = reportVoyageNo || '';
                    if (nextVoyageNo) {
                        const boundaryState = nextState;
                        const boundaryDateTime =
                            (boundaryState === 'SAILING' ? departureBoundaryDateTime : arrivalBoundaryDateTime) ||
                            primaryReportDateTime ||
                            reportDateTime ||
                            reportDate ||
                            '';
                        const boundarySourceTime = boundaryDateTime || primaryReportDateTime || reportDateTime || reportDate;

                        fallbackRows.push({
                            fromDate: boundaryDateTime,
                            toDate: '',
                            sourceDateTime: boundarySourceTime || '',
                            distance: 0,
                            hoursAtSea: 0,
                            hoursAtAnchor: 0,
                            cargoCarried: cargoWeight ?? 0,
                            cargoWeightProvided: cargoWeight !== null,
                            meHsfo: 0,
                            aeHsfo: 0,
                            boilerHsfo: 0,
                            meMgo: 0,
                            aeMgo: 0,
                            boilerMgo: 0,
                            totalFuel: 0,
                            totalCo2: 0,
                            bunkerReceivedHfo: 0,
                            bunkerReceivedMgo: 0,
                            reportName: report.reportType || '--',
                            bunkerReportName: '--',
                            hfoRob: 0,
                            mgoRob: 0,
                            cargoHandled: 0,
                            vesselCondition: getVesselConditionFromReport(report, matchingVoyage?.loadingCondition),
                            portNameLocation:
                                boundaryState === 'SAILING'
                                    ? (getPortValueFromReport(report.values || [], ['departure port', 'port name', 'location']) ||
                                        matchingVoyage?.departurePort ||
                                        '')
                                    : (getPortValueFromReport(report.values || [], ['arrival port', 'port name', 'location']) ||
                                        matchingVoyage?.arrivalPort ||
                                        ''),
                            eventType: toVoyageEventLabel(boundaryState),
                            reportKind,
                            stsVoyage: '',
                            boilerUsed: '',
                            voyageNo: nextVoyageNo,
                            leg: resolveLegDisplay(report, matchingVoyage, persistedCiiRow),
                            avgSulphurHsfo: undefined,
                            avgSulphurMgo: undefined,
                            isBoundaryRow: true,
                        } as AggregatedVoyageData);
                    }
                }

                if (reportDateTime) {
                    lastReportDateTime = reportDateTime;
                } else if (primaryReportDateTime) {
                    lastReportDateTime = primaryReportDateTime;
                }

                currentEventState = nextState;
            });

        return mergeVoyageRowsByEvent(fallbackRows);
    };

    const fetchYearlyData = async (vesselId: number, year: number) => {
        setLoading(true);
        try {
            const vId = vesselId;
            const from = `${year}-01-01`;
            const to = `${year}-12-31`;

            const [createdReports, bunkerReports, cargoData, dprData, fetchedVoyages] = await Promise.all([
                getCreatedReportById(vId).catch((error) => {
                    console.error('Error fetching created reports:', error);
                    return [];
                }),
                getSubmittedReportsFull(from, to, vId).catch((error) => {
                    console.error('Error fetching submitted reports full:', error);
                    return [];
                }),
                getCargoOperations({
                    vesselId: vId,
                    fromDate: `${from}T00:00:00`,
                    toDate: `${to}T23:59:59`
                }).catch((error) => {
                    console.error('Error fetching cargo operations:', error);
                    return { content: [] };
                }),
                getDprList().catch((error) => {
                    console.error('Error fetching DPR list:', error);
                    return [];
                }),
                getVoyagesByVessel(vId).catch(() => [])
            ]);

            const fullReportById = new Map<number, ReportDtoFull>();
            (bunkerReports || []).forEach((report: any) => {
                if (report?.id != null) {
                    fullReportById.set(Number(report.id), report);
                }
            });

            const createdReportsForVessel = (createdReports || [])
                .filter((report: any) => Number(report?.vesselId) === Number(vId))
                .filter((report: any) => String(report?.status || '').toUpperCase() === 'SUBMITTED');

            const enrichedCreatedReports = await enrichCreatedReportsWithAssignmentLabels(createdReportsForVessel);

            const normalizedCreatedReports: ReportDtoFull[] = enrichedCreatedReports
                .map((report: ReportDtoFull) => {
                    const matchingFullReport = fullReportById.get(Number(report.id));
                    return {
                        ...report,
                        values: matchingFullReport?.values?.length
                            ? matchingFullReport.values
                            : report.values,
                    };
                })
                .filter((report: ReportDtoFull) => {
                    const rawDate = buildReportUtcDateTime(report) || report.submittedDateTime || report.createdDateTime;
                    const dateOnly = toIsoDateOnly(rawDate);
                    return Boolean(dateOnly) && dateOnly >= from && dateOnly <= to;
                })
                .sort((a, b) => {
                    const aTime = toDateTimeMs(getReportSortDateTime(a));
                    const bTime = toDateTimeMs(getReportSortDateTime(b));
                    if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
                    return (getReportSortDateTime(a) || '').localeCompare(getReportSortDateTime(b) || '');
                });

            let ciiRows: CiiVoyageRowDto[] = [];
            try {
                const reportIds = normalizedCreatedReports
                    .map((report) => Number(report.id))
                    .filter((id) => Number.isFinite(id) && id > 0);
                ciiRows = await getCiiVoyageRowsByReportIds(reportIds);
            } catch (error) {
                console.error('Error fetching CII voyage rows:', error);
                ciiRows = [];
            }

            const ciiRowMap: Record<number, CiiVoyageRowDto> = {};
            ciiRows.forEach((row) => {
                if (row?.reportId != null) {
                    ciiRowMap[Number(row.reportId)] = row;
                }
            });

            setCiiRowByReportId(ciiRowMap);
            setYearlyReports(normalizedCreatedReports);
            setCargoOps(cargoData.content);
            const selectedVessel = vessels.find(v => v.id === vId);
            const filteredDprs = dprData.filter((d: any) =>
                (d.vesselId && d.vesselId === vId) ||
                (selectedVessel && d.vesselName === selectedVessel.fleet_name)
            );
            setDprRecords(filteredDprs);
            try {
                await processReportData(
                    normalizedCreatedReports,
                    bunkerReports,
                    cargoData.content || [],
                    filteredDprs,
                    fetchedVoyages,
                    ciiRowMap
                );
            } catch (error) {
                console.error('Error processing CII voyage data, using fallback rows:', error);
                const fallbackRows = buildFallbackVoyageRows(normalizedCreatedReports, fetchedVoyages, ciiRowMap);
                setAggregatedData(splitRowsOnQuarterBoundaries(fallbackRows, normalizedCreatedReports, selectedYear));
            }
            const bunkerRowsSource = Array.from(
                new Map(
                    [...bunkerReports, ...normalizedCreatedReports]
                        .filter(Boolean)
                        .map((report: ReportDtoFull) => [Number(report.id || `${report.reportType}-${report.createdDateTime}`), report])
                ).values()
            );
            setBunkerTabRows(extractBunkerTabRows(bunkerRowsSource));
        } catch (error) {
            console.error('Error fetching yearly report data:', error);
            toast.error('Failed to fetch voyage data for the selected year');
        } finally {
            setLoading(false);
        }
    };

    const getCargoWeightFromReport = (report?: ReportDtoFull): number | null => {
        if (!report?.values?.length) return null;

        const exact =
            findFieldTextExact(report.values, ['Cargo Weight (MT)']) ||
            findFieldTextExact(report.values, ['Cargo Weight (MTs)']) ||
            findFieldTextExact(report.values, ["Cargo Weight (MT's)"]);

        if (exact !== '') {
            const parsed = parseFloat(exact);
            return Number.isFinite(parsed) ? parsed : null;
        }

        const fuzzy = findFieldText(report.values, [
            'cargo weight',
            'cargo wt',
            'cargo quantity',
            "cargo carried",
        ]);

        if (fuzzy !== '') {
            const parsed = parseFloat(fuzzy);
            return Number.isFinite(parsed) ? parsed : null;
        }

        return null;
    };

    const toDateOnly = (value?: string) => {
        if (!value) return '';
        return value.includes('T') ? value.split('T')[0] : value;
    };

    const toIsoDateOnly = (value?: string) => {
        if (!value) return '';

        const raw = String(value).trim();
        if (!raw) return '';

        const directDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (directDateMatch) {
            return `${directDateMatch[1]}-${directDateMatch[2]}-${directDateMatch[3]}`;
        }

        const nativeParsed = new Date(raw);
        if (!Number.isNaN(nativeParsed.getTime())) {
            return nativeParsed.toISOString().split('T')[0];
        }

        const dmyMatch = raw.match(/^(\d{1,2})[/-](\d{1,2}|[A-Za-z]{3})[/-](\d{2,4})/);
        if (!dmyMatch) return '';

        const [, dayText, monthText, yearText] = dmyMatch;
        const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        const day = Number(dayText);
        const month = /^\d+$/.test(monthText) ? Number(monthText) - 1 : monthNames.indexOf(monthText.toLowerCase());
        let year = Number(yearText);
        if (yearText.length === 2) year += year >= 70 ? 1900 : 2000;
        if (month < 0 || month > 11 || !day || !year) return '';

        const parsed = new Date(Date.UTC(year, month, day));
        if (Number.isNaN(parsed.getTime())) return '';
        return parsed.toISOString().split('T')[0];
    };

    const parseBunkerRows = (raw?: string) => {
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            console.error('Failed to parse bunker rows', error);
            return [];
        }
    };

    const findFieldText = (values: ReportFieldValueFullDto[] = [], labelParts: string[]) => {
        const match = values.find(v =>
            labelParts.some(labelPart =>
                v.field.label.toLowerCase().includes(labelPart.toLowerCase())
            )
        );
        return match?.valueText?.trim() || '';
    };

    const findFieldTextExact = (values: ReportFieldValueFullDto[] = [], labels: string[]) => {
        const normalizeExactLabel = (label: string) =>
            label.trim().toLowerCase().replace(/:$/, '');

        const normalizedLabels = labels.map(normalizeExactLabel);
        const match = values.find(v =>
            normalizedLabels.includes(normalizeExactLabel(v.field.label || ''))
        );
        return match?.valueText?.trim() || '';
    };

    const hasFieldWithExactLabel = (values: ReportFieldValueFullDto[] = [], labels: string[]) => {
        const normalizeExactLabel = (label: string) =>
            label.trim().toLowerCase().replace(/:$/, '');

        const normalizedLabels = labels.map(normalizeExactLabel);
        return values.some((value) => normalizedLabels.includes(normalizeExactLabel(value.field.label || '')));
    };

    const getCheckboxFieldState = (report: ReportDtoFull | undefined, labels: string[]) =>
        hasFieldWithExactLabel(report?.values || [], labels) && Boolean(findFieldTextExact(report?.values || [], labels));

    const normalizeVesselCondition = (value?: string) => {
        const normalized = (value || '').trim().toLowerCase();
        if (!normalized) return '';

        if (normalized === 'b' || normalized.includes('ballast')) {
            return 'B';
        }

        if (
            normalized === 'l' ||
            normalized.includes('loaded') ||
            normalized.includes('laden')
        ) {
            return 'L';
        }

        return '';
    };

    const getVesselConditionFromReport = (report?: ReportDtoFull, fallback?: string) => {
        const storedVesselCondition =
            findFieldTextExact(report?.values || [], ['Vessel Condition']) ||
            findFieldText(report?.values || [], [
                'vessel condition',
                'loading condition',
                'ballast / laden',
                'ballast/laden',
            ]) ||
            fallback;

        return normalizeVesselCondition(storedVesselCondition);
    };

    const getEventTypeFromReport = (report?: ReportDtoFull, fallback?: string) =>
        findFieldTextExact(report?.values || [], ['Event Name']) ||
        findFieldText(report?.values || [], ['event name']) ||
        fallback ||
        '';

    const normalizeTextForMatch = (value?: string) =>
        String(value || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();

    const normalizeShipTypeCode = (value?: string) => {
        const normalized = normalizeTextForMatch(value).replace(/\s+/g, '_').toUpperCase();
        if (!normalized) return 'TANKER';
        if (normalized.includes('BULK')) return 'BULK_CARRIER';
        if (normalized.includes('GAS') && !normalized.includes('LNG')) return 'GAS_CARRIER';
        if (normalized.includes('TANK')) return 'TANKER';
        if (normalized.includes('CONTAINER')) return 'CONTAINER_SHIP';
        if (normalized.includes('GENERAL') && normalized.includes('CARGO')) return 'GENERAL_CARGO_SHIP';
        if (normalized.includes('REFRIGERATED') || normalized.includes('REEFER')) return 'REFRIGERATED_CARGO_CARRIER';
        if (normalized.includes('COMBINATION')) return 'COMBINATION_CARRIER';
        if (normalized.includes('LNG')) return 'LNG_CARRIER';
        if (normalized.includes('VEHICLE')) return 'RO_RO_CARGO_SHIP_VEHICLE_CARRIER';
        if (normalized.includes('RO') && normalized.includes('PASSENGER')) return 'RO_RO_PASSENGER_SHIP';
        if (normalized.includes('RO') && normalized.includes('CARGO')) return 'RO_RO_CARGO_SHIP';
        if (normalized.includes('CRUISE')) return 'CRUISE_PASSENGER_SHIP';
        return normalized;
    };

    const parseBooleanLike = (value?: string) => {
        const normalized = normalizeTextForMatch(value);
        if (!normalized) return false;
        return (
            normalized === 'true' ||
            normalized === 'yes' ||
            normalized === 'y' ||
            normalized === '1' ||
            normalized === 'on' ||
            normalized === 'checked' ||
            normalized === 'selected'
        );
    };

    const getPortValueFromReport = (values: ReportFieldValueFullDto[] = [], labelParts: string[]) => {
        const matches = values.filter((v) =>
            labelParts.some((labelPart) =>
                v.field.label.toLowerCase().includes(labelPart.toLowerCase())
            )
        );

        for (const match of matches) {
            const valueText = String(match.valueText || '').trim();
            if (!valueText) continue;

            if (parseBooleanLike(valueText)) continue;

            const valueNormalized = normalizeTextForMatch(valueText);
            if (!valueNormalized) continue;

            const labelNormalized = normalizeTextForMatch(match.field.label || '');
            if (labelNormalized && valueNormalized === labelNormalized) continue;

            return valueText;
        }

        return '';
    };

    const getReportKindFromReport = (report?: ReportDtoFull): ReportKind => {
        const reportTypeText = normalizeTextForMatch(report?.reportType || '');
        const eventType = getEventTypeFromReport(report);
        const eventTypeText = normalizeTextForMatch(eventType);

        if (reportTypeText.includes('cosp')) return 'COSP';
        if (reportTypeText.includes('eosp')) return 'EOSP';
        if (reportTypeText.includes('arrival')) return 'ARRIVAL';
        if (reportTypeText.includes('departure')) return 'DEPARTURE';

        if (reportTypeText.includes('noon') || eventTypeText.includes('noon')) {
            // Explicit report type wins.
            if (
                reportTypeText.includes('noon at sea') ||
                reportTypeText.includes('noon sea')
            ) {
                return 'NOON_SEA';
            }

            if (
                reportTypeText.includes('noon at anchor') ||
                reportTypeText.includes('noon anchor')
            ) {
                return 'NOON_ANCHOR';
            }

            if (
                reportTypeText.includes('noon at port') ||
                reportTypeText.includes('noon port') ||
                reportTypeText.includes('noon at berth') ||
                reportTypeText.includes('noon berth')
            ) {
                return 'NOON_BERTH';
            }

            // Prefer explicit checkbox/state flags first (exclude drifting to avoid forcing anchor).
            if (isCheckedEventFlag(report, ['anchor arrival', 'at anchor', 'anchorage'])) {
                return 'NOON_ANCHOR';
            }

            // Keep berth detection narrow here: generic location fields like
            // "Port Name / Location" should not force a noon report into berth.
            if (isCheckedEventFlag(report, ['port arrival']) || eventTypeText.includes('berth') || isBerthEventType(eventType)) {
                return 'NOON_BERTH';
            }

            // Fallback to event name text
            if (eventTypeText.includes('anchor') || isAnchorEventType(eventType)) return 'NOON_ANCHOR';
            if (eventTypeText.includes('berth') || eventTypeText.includes('port') || isBerthEventType(eventType)) {
                return 'NOON_BERTH';
            }

            return 'NOON_SEA';
        }

        return 'OTHER';
    };

    const isCheckedEventFlag = (report: ReportDtoFull | undefined, tokens: string[]) => {
        if (!report?.values?.length) return false;

        return report.values.some((value) => {
            const fieldLabel = normalizeTextForMatch(value?.field?.label || '');
            const fieldMatches = tokens.some((token) => fieldLabel.includes(normalizeTextForMatch(token)));
            if (!fieldMatches) return false;

            const valueText = String(value?.valueText || '').trim();
            if (parseBooleanLike(valueText)) return true;

            // Some templates store checkbox-like values as text arrays/labels.
            const normalizedValueText = normalizeTextForMatch(valueText);
            const normalizedParts = normalizedValueText.split(' ').filter(Boolean);
            if (
                normalizedValueText === '0' ||
                normalizedValueText === 'no' ||
                normalizedValueText === 'false' ||
                normalizedParts.includes('false') ||
                normalizedParts.includes('no')
            ) {
                return false;
            }
            return (
                normalizedValueText.includes('true') ||
                normalizedValueText.includes('yes') ||
                tokens.some((token) => normalizedValueText.includes(normalizeTextForMatch(token)))
            );
        });
    };

    const getArrivalNextStateFromEventFlags = (
        report: ReportDtoFull | undefined,
        fallbackEventType?: string,
        fallbackState: VoyageEventState = 'SAILING'
    ): VoyageEventState => {
        if (
            isCheckedEventFlag(report, ['anchor arrival', 'at anchor'])
        ) {
            return 'AT_ANCHOR';
        }

        if (isCheckedEventFlag(report, ['port arrival'])) {
            return 'AT_BERTH';
        }

        if (isCheckedEventFlag(report, ['sts arrival'])) {
            return 'SAILING';
        }

        // Fallback for legacy data where checkboxes may not exist.
        if (isAnchorEventType(fallbackEventType)) return 'AT_ANCHOR';
        if (isBerthEventType(fallbackEventType)) return 'AT_BERTH';
        if (isSailingEventType(fallbackEventType)) return 'SAILING';
        return fallbackState;
    };

    const getDeparturePrevStateFromEventFlags = (
        report: ReportDtoFull | undefined,
        fallbackState: VoyageEventState = 'SAILING'
    ): VoyageEventState => {
        if (isCheckedEventFlag(report, ['departure anchor', 'at anchor'])) return 'AT_ANCHOR';
        if (isCheckedEventFlag(report, ['departure port'])) return 'AT_BERTH';
        if (isCheckedEventFlag(report, ['departure for berth shifting'])) return 'AT_BERTH';
        return fallbackState;
    };

    const getNoonEventStateFromEventFlags = (
        report: ReportDtoFull | undefined,
        fallbackState: VoyageEventState,
        defaultState: VoyageEventState
    ): VoyageEventState => {
        if (!report) return fallbackState || defaultState;

        if (isCheckedEventFlag(report, ['anchor arrival', 'at anchor', 'anchorage'])) return 'AT_ANCHOR';
        if (isCheckedEventFlag(report, ['sts'])) return 'AT_ANCHOR';
        if (isCheckedEventFlag(report, ['port arrival'])) return 'AT_BERTH';

        return fallbackState || defaultState;
    };

    const resolveRowAndNextEventState = (
        reportKind: ReportKind,
        currentState: VoyageEventState,
        report?: ReportDtoFull,
        rawEventType?: string
    ): { rowState: VoyageEventState; nextState: VoyageEventState } => {
        if (reportKind === 'NOON_SEA') {
            const hasAnchorFlag = isCheckedEventFlag(report, ['anchor arrival', 'at anchor']);

            if (hasAnchorFlag) {
                return { rowState: 'AT_ANCHOR', nextState: 'AT_ANCHOR' };
            }

            if (isCheckedEventFlag(report, ['port arrival'])) {
                return { rowState: 'AT_BERTH', nextState: 'AT_BERTH' };
            }

            return { rowState: 'SAILING', nextState: 'SAILING' };
        }

        if (reportKind === 'NOON_ANCHOR' || reportKind === 'NOON_BERTH') {
            const defaultState: VoyageEventState =
                reportKind === 'NOON_ANCHOR'
                    ? 'AT_ANCHOR'
                    : 'AT_BERTH';
            const noonState = getNoonEventStateFromEventFlags(report, defaultState, defaultState);
            return { rowState: noonState, nextState: noonState };
        }

        if (reportKind === 'ARRIVAL') {
            const arrivalNextState = getArrivalNextStateFromEventFlags(report, rawEventType, currentState);
            const arrivalRowState = currentState === 'SAILING' ? 'SAILING' : arrivalNextState;
            return {
                rowState: arrivalRowState,
                nextState: arrivalNextState,
            };
        }

        if (reportKind === 'DEPARTURE') {
            return {
                rowState: currentState === 'SAILING' ? 'SAILING' : currentState,
                nextState: 'SAILING',
            };
        }

        if (reportKind === 'EOSP') {
    // EOSP belongs to the current voyage's last active event.
    // If vessel is at berth after arrival + noon at port flow,
    // EOSP should stay in AT_BERTH and must not create sailing.
    return {
        rowState: currentState,
        nextState: currentState,
    };
}

        if (reportKind === 'COSP') {
            // COSP belongs to previous voyage's last event, but it also starts the next voyage flow.
            return {
                rowState: currentState,
                nextState: 'SAILING' as VoyageEventState,
            };
        }

        return {
            rowState: currentState,
            nextState: currentState,
        };
    };

    const isAnchorEventType = (value?: string) => {
        const normalized = normalizeTextForMatch(value);
        return normalized.includes('anchor') || normalized.includes('anchorage');
    };

    const isBerthEventType = (value?: string) => {
        const normalized = normalizeTextForMatch(value);
        return (
            normalized === 'b' ||
            normalized.includes('berth') ||
            normalized.includes('in port') ||
            normalized.includes('at port') ||
            /\bport\b/.test(normalized)
        );
    };

    const isSailingEventType = (value?: string) => {
        const normalized = normalizeTextForMatch(value);
        return (
            normalized.includes('sailing') ||
            normalized.includes('at sea') ||
            normalized.includes('sea passage') ||
            normalized.includes('underway') ||
            normalized.includes('under way') ||
            normalized.includes('drifting') ||
            normalized.includes('departure') ||
            normalized.includes('cosp') ||
            normalized.includes('eosp')
        );
    };

    const resolveVoyageEventState = (
        row: Pick<AggregatedVoyageData, 'reportName' | 'eventType' | 'vesselCondition'>,
        previousState?: VoyageEventState
    ): VoyageEventState => {
        const reportName = normalizeTextForMatch(row.reportName);
        const eventType = normalizeTextForMatch(row.eventType);
        const vesselCondition = normalizeTextForMatch(row.vesselCondition);
        const combinedEventText = `${eventType} ${vesselCondition}`.trim();

        if (isAnchorEventType(combinedEventText)) return 'AT_ANCHOR';
        if (isBerthEventType(combinedEventText)) return 'AT_BERTH';
        if (isSailingEventType(combinedEventText)) return 'SAILING';

        if (reportName.includes('arrival')) {
            if (isAnchorEventType(`${reportName} ${combinedEventText}`)) return 'AT_ANCHOR';
            if (isBerthEventType(`${reportName} ${combinedEventText}`)) return 'AT_BERTH';
            return previousState === 'AT_ANCHOR' ? 'AT_ANCHOR' : 'AT_BERTH';
        }

        if (reportName.includes('cosp')) {
    return 'SAILING';
}

        if (isAnchorEventType(reportName)) return 'AT_ANCHOR';
        if (isBerthEventType(reportName)) return 'AT_BERTH';
        if (isSailingEventType(reportName) || reportName.includes('noon')) return 'SAILING';

        return previousState || 'SAILING';
    };

    const toVoyageEventLabel = (state: VoyageEventState) => {
        if (state === 'AT_ANCHOR') return 'AT ANCHOR';
        if (state === 'AT_BERTH') return 'AT BERTH / STS TRANSFER';
        return 'SAILING';
    };

    const isReportNameLike = (reportName: string | undefined, token: string) =>
        normalizeTextForMatch(reportName).includes(token);

    const isArrivalReportName = (reportName?: string) => isReportNameLike(reportName, 'arrival');
    const isDepartureOrCospReportName = (reportName?: string) =>
        isReportNameLike(reportName, 'departure') ||
        isReportNameLike(reportName, 'cosp') ||
        isReportNameLike(reportName, 'eosp');

    const isArrivalKind = (row?: Pick<AggregatedVoyageData, 'reportName' | 'reportKind'>) =>
        row?.reportKind === 'ARRIVAL' || isArrivalReportName(row?.reportName);

    const isDepartureOrCospKind = (row?: Pick<AggregatedVoyageData, 'reportName' | 'reportKind'>) =>
        row?.reportKind === 'DEPARTURE' ||
        row?.reportKind === 'COSP' ||
        row?.reportKind === 'EOSP' ||
        isDepartureOrCospReportName(row?.reportName);

    const getDateTimeFieldValueFromReport = (report?: ReportDtoFull, _labelParts: string[] = []) => {
        if (!report) return '';
        return buildReportUtcDateTime(report);
    };

    const formatEventTypeDisplay = (value?: string) => {
        if (!value) return '--';
        if (isBerthEventType(value)) return 'AT BERTH / STS TRANSFER';
        if (isAnchorEventType(value)) return 'AT ANCHOR';
        if (isSailingEventType(value)) return 'SAILING';
        return String(value).toUpperCase();
    };

    const formatLegNumberDisplay = (seq?: number | string | null) => {
        if (seq == null || seq === '') return '';
        const trimmed = String(seq).trim();
        if (!trimmed || trimmed === '--') return '';
        if (/^L\d+$/i.test(trimmed)) return trimmed.toUpperCase();
        if (/^\d+$/.test(trimmed)) return `L${trimmed}`;
        return trimmed;
    };

    const resolveLegDisplay = (
        report?: ReportDtoFull,
        matchingVoyage?: any,
        persistedCiiRow?: CiiVoyageRowDto
    ): string => {
        const repSeq =
            (report as any)?.legDto?.sequenceNumber ??
            (report as any)?.legSequenceNumber ??
            (report as any)?.leg?.sequenceNumber;
        if (repSeq != null) {
            const formatted = formatLegNumberDisplay(repSeq);
            if (formatted) return formatted;
        }

        const reportLegId =
            (report as any)?.legId ??
            (report as any)?.legDto?.id ??
            (report as any)?.leg?.id ??
            (persistedCiiRow as any)?.legId ??
            matchingVoyage?.legID ??
            matchingVoyage?.legId;

        if (reportLegId != null && matchingVoyage?.legs?.length) {
            const matchedLeg = matchingVoyage.legs.find(
                (l: any) => Number(l.id) === Number(reportLegId)
            );
            if (matchedLeg?.sequenceNumber != null) {
                const formatted = formatLegNumberDisplay(matchedLeg.sequenceNumber);
                if (formatted) return formatted;
            }
        }

        const dynamicLegField = findFieldText(report?.values || [], ['leg no', 'leg number', 'leg id', 'leg']);
        if (dynamicLegField) {
            const formatted = formatLegNumberDisplay(dynamicLegField);
            if (formatted) return formatted;
        }

        if (matchingVoyage?.legs?.length === 1 && matchingVoyage.legs[0]?.sequenceNumber != null) {
            const formatted = formatLegNumberDisplay(matchingVoyage.legs[0].sequenceNumber);
            if (formatted) return formatted;
        }

        return '';
    };

    const normalizeDateTimeForParse = (value?: string) => {
        if (!value) return '';
        const trimmed = String(value).trim();
        if (!trimmed) return '';

        const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
        if (hasTimezone) return trimmed;

        // Date-only: YYYY-MM-DD
        const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
        if (dateOnlyMatch) {
            return `${trimmed}T00:00:00Z`;
        }

        // Date with 12-hour AM/PM: YYYY-MM-DD hh:mm(:ss)? AM/PM
        const ampmMatch = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i.exec(trimmed);
        if (ampmMatch) {
            const [, y, m, d, hStr, minStr, secStr = '00', meridiem] = ampmMatch;
            let hour = parseInt(hStr, 10);
            if (meridiem.toUpperCase() === 'PM' && hour < 12) hour += 12;
            if (meridiem.toUpperCase() === 'AM' && hour === 12) hour = 0;
            const hourPad = String(hour).padStart(2, '0');
            return `${y}-${m}-${d}T${hourPad}:${minStr}:${secStr}Z`;
        }

        // Date with 24-hour: YYYY-MM-DD HH:mm(:ss)?
        const dateTimeMatch = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
        if (dateTimeMatch) {
            const [, y, m, d, hStr, minStr, secStr = '00'] = dateTimeMatch;
            const hourPad = String(parseInt(hStr, 10)).padStart(2, '0');
            return `${y}-${m}-${d}T${hourPad}:${minStr}:${secStr}Z`;
        }

        // Day-first: DD/MM/YYYY or DD-MM-YYYY HH:mm(:ss)? (with optional AM/PM)
        const dmyMatch = /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i.exec(trimmed);
        if (dmyMatch) {
            const [, dStr, mStr, yStr, hStr = '0', minStr = '00', secStr = '00', meridiem] = dmyMatch;
            let hour = parseInt(hStr, 10);
            if (meridiem && meridiem.toUpperCase() === 'PM' && hour < 12) hour += 12;
            if (meridiem && meridiem.toUpperCase() === 'AM' && hour === 12) hour = 0;
            const dayPad = String(parseInt(dStr, 10)).padStart(2, '0');
            const monthPad = String(parseInt(mStr, 10)).padStart(2, '0');
            const hourPad = String(hour).padStart(2, '0');
            return `${yStr}-${monthPad}-${dayPad}T${hourPad}:${minStr}:${secStr}Z`;
        }

        return trimmed;
    };

    const toDateTimeMs = (value?: string) => {
        if (!value) return Number.NaN;
        const normalized = normalizeDateTimeForParse(value);
        const parsed = new Date(normalized).getTime();
        return Number.isNaN(parsed) ? Number.NaN : parsed;
    };

    const findMatchingVoyageByDate = (reportDate: string, voyages: any[]) => {
        if (!reportDate) return null;
        const reportTime = toDateTimeMs(reportDate);
        if (Number.isNaN(reportTime)) return null;

        const exactEndMatch = (voyages || [])
            .filter((v: any) => v?.endDate && toDateTimeMs(v.endDate) === reportTime)
            .sort((a: any, b: any) => {
                const aTime = toDateTimeMs(a?.startDate);
                const bTime = toDateTimeMs(b?.startDate);
                if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
                return String(a?.startDate || '').localeCompare(String(b?.startDate || ''));
            });

        if (exactEndMatch.length) {
            return exactEndMatch[0];
        }

        const candidates = (voyages || [])
            .filter((v: any) => v?.startDate)
            .map((v: any) => {
                const startTime = toDateTimeMs(v.startDate);
                const endTime = v?.endDate ? toDateTimeMs(v.endDate) : Number.POSITIVE_INFINITY;
                return { voyage: v, startTime, endTime };
            })
            .filter((v) => !Number.isNaN(v.startTime) && reportTime >= v.startTime && reportTime <= v.endTime)
            .sort((a, b) => b.startTime - a.startTime);

        return candidates.length ? candidates[0].voyage : null;
    };

    const buildVoyageSequence = (voyages: any[]) => {
        const sorted = [...(voyages || [])]
            .filter((v: any) => v?.voyageNumber)
            .sort((a: any, b: any) => {
                const aTime = toDateTimeMs(a?.startDate);
                const bTime = toDateTimeMs(b?.startDate);
                if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
                return String(a?.startDate || '').localeCompare(String(b?.startDate || ''));
            });

        const prevByVoyageNo = new Map<string, string>();
        for (let i = 1; i < sorted.length; i += 1) {
            const prevNo = String(sorted[i - 1]?.voyageNumber || '').trim();
            const currNo = String(sorted[i]?.voyageNumber || '').trim();
            if (prevNo && currNo && !prevByVoyageNo.has(currNo)) {
                prevByVoyageNo.set(currNo, prevNo);
            }
        }

        return { sorted, prevByVoyageNo };
    };

    const pickLaterDateTime = (current?: string, candidate?: string) => {
        if (!current) return candidate || '';
        if (!candidate) return current;

        const currentMs = toDateTimeMs(current);
        const candidateMs = toDateTimeMs(candidate);

        if (Number.isNaN(currentMs) && Number.isNaN(candidateMs)) {
            return candidate > current ? candidate : current;
        }
        if (Number.isNaN(currentMs)) return candidate;
        if (Number.isNaN(candidateMs)) return current;
        return candidateMs >= currentMs ? candidate : current;
    };

    const toFiniteNumber = (value: unknown) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

        const parseOptionalNumber = (value?: string | number | null) => {
            if (value === null || value === undefined || value === '') return undefined;
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : undefined;
        };

    const numericValue = (value?: string | number | null) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const resolveStsVoyageValue = (value?: string | null) => {
        const normalized = (value ?? '').trim().toUpperCase();
        return normalized === 'Y' || normalized === 'N' ? normalized : 'N';
    };

    const normalizeStsVoyageInput = (value?: string | null) => {
        const normalized = (value ?? '').trim().toUpperCase();
        return normalized === 'Y' || normalized === 'N' ? normalized : '';
    };

    const sanitizeNumericInput = (value: string) => {
        let cleaned = value.replace(/[^0-9.]/g, '');
        const firstDot = cleaned.indexOf('.');
        if (firstDot !== -1) {
            cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
        }
        if (cleaned.startsWith('.')) {
            cleaned = `0${cleaned}`;
        }
        return cleaned;
    };

    const normalizeNumericString = (value?: string | number | null) => {
        if (value === null || value === undefined) return '';
        const cleaned = sanitizeNumericInput(String(value));
        if (cleaned === '' || cleaned === '.') return '';
        return cleaned;
    };

    const getPersistedFuelDetail = (row: CiiVoyageRowDto | undefined, fuelCode: string) =>
        (row?.fuelDetails || []).find((detail) => String(detail.fuelCode || '').toUpperCase() === fuelCode);

    const hasPersistedCorrectionValue = (value?: string | number | null) =>
        value !== null && value !== undefined && String(value).trim() !== '';

    const getReeferElectricalForFuel = (row: AggregatedVoyageData, fuelCode: string) => {
        if (!row.reeferContainersOnboard && !row.cargoCoolingSystemInUse) return 0;
        const selectedFuelCode = getConfiguredFuelCode(row.reeferFuelCode);
        if (!selectedFuelCode || selectedFuelCode !== fuelCode) return 0;
        return numericValue(row.reeferKwh) * numericValue(row.reeferSfoc);
    };

    const getCorrectionValueForFuel = (
        row: AggregatedVoyageData,
        fuelCode: string,
        field: keyof FuelCorrectionBreakdown
    ) => {
        const baseValue =
            fuelCode === 'HSFO' && field === 'fcElectrical' ? (hasPersistedCorrectionValue(row.fcElectricalHsfo) ? numericValue(row.fcElectricalHsfo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'MGO' && field === 'fcElectrical' ? (hasPersistedCorrectionValue(row.fcElectricalMgo) ? numericValue(row.fcElectricalMgo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'HSFO' && field === 'fcBoiler' ? (hasPersistedCorrectionValue(row.fcBoilerHsfo) ? numericValue(row.fcBoilerHsfo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'MGO' && field === 'fcBoiler' ? (hasPersistedCorrectionValue(row.fcBoilerMgo) ? numericValue(row.fcBoilerMgo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'HSFO' && field === 'fcOther' ? (hasPersistedCorrectionValue(row.fcOtherHsfo) ? numericValue(row.fcOtherHsfo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'MGO' && field === 'fcOther' ? (hasPersistedCorrectionValue(row.fcOtherMgo) ? numericValue(row.fcOtherMgo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            numericValue(row.correctionBreakdown?.[fuelCode]?.[field]);
        return baseValue + (field === 'fcElectrical' ? getReeferElectricalForFuel(row, fuelCode) : 0);
    };

    const getCorrectionSource = (
        row: AggregatedVoyageData,
        fuelCode: string,
        field: keyof FuelCorrectionBreakdown
    ) => row.correctionSourceByFuel?.[fuelCode]?.[field] || (getCorrectionValueForFuel(row, fuelCode, field) > 0 ? 'Auto' : '');

    const getPersistedCorrectionNumber = (
        persistedRow: CiiVoyageRowDto | undefined,
        fuelCode: string,
        field: keyof FuelCorrectionBreakdown
    ) => {
        const detail = getPersistedFuelDetail(persistedRow, fuelCode);
        const rawValue =
            field === 'fcElectrical' ? detail?.fcElectrical :
            field === 'fcBoiler' ? detail?.fcBoiler :
            detail?.fcOther;
        return hasPersistedCorrectionValue(rawValue) ? parseOptionalNumber(rawValue) : undefined;
    };

    const buildCorrectionBreakdownForRow = (
        autoCorrections: FuelCorrectionMap | undefined,
        persistedRow?: CiiVoyageRowDto
    ) => {
        const result: FuelCorrectionMap = {};
        const sources: FuelCorrectionSourceMap = {};
        const fuelCodes = new Set([
            ...Object.keys(autoCorrections || {}),
            ...(persistedRow?.fuelDetails || []).map((detail) => String(detail.fuelCode || '').toUpperCase()).filter(Boolean),
        ]);

        fuelCodes.forEach((fuelCode) => {
            const auto = autoCorrections?.[fuelCode] || { fcElectrical: 0, fcBoiler: 0, fcOther: 0 };
            const target = getOrCreateFuelCorrection(result, fuelCode);
            const source = sources[fuelCode] || {};

            (['fcElectrical', 'fcBoiler', 'fcOther'] as Array<keyof FuelCorrectionBreakdown>).forEach((field) => {
                const persistedValue = getPersistedCorrectionNumber(persistedRow, fuelCode, field);
                if (persistedValue !== undefined) {
                    target[field] = persistedValue;
                    source[field] = 'Saved';
                } else {
                    target[field] = auto[field] || 0;
                    if (target[field] > 0) source[field] = 'Auto';
                }
            });

            sources[fuelCode] = source;
        });

        return { corrections: result, sources };
    };

    const getCorrectionDisplay = (
        row: AggregatedVoyageData,
        fuelCode: string,
        field: keyof FuelCorrectionBreakdown
    ) => {
        const value = getCorrectionValueForFuel(row, fuelCode, field);
        const source = getCorrectionSource(row, fuelCode, field);
        return { value, source };
    };

    const getShipTypeIndexByYear = (year: number) => {
        const configuredIndex = ciiRuleSet?.reductionFactors?.find((item) => Number(item.reportingYear) === Number(year))?.correctionYearIndex;
        if (configuredIndex !== undefined && configuredIndex !== null) return Number(configuredIndex) || 0;
        if (!year || Number.isNaN(year)) return 0;
        return Math.max(0, year - 2023);
    };

    const getUniqueShipTypeRules = () => {
        const byCode = new Map<string, CiiShipTypeRuleDto>();
        (ciiRuleSet?.shipTypeRules || []).forEach((rule) => {
            if (!byCode.has(rule.shipTypeCode)) {
                byCode.set(rule.shipTypeCode, rule);
            }
        });
        return Array.from(byCode.values());
    };

    const getReductionFactorForYear = (year: number) =>
        (ciiRuleSet?.reductionFactors || []).find((factor) => Number(factor.reportingYear) === Number(year));

    const getRuleCapacity = (rule?: CiiShipTypeRuleDto | null) => {
        if (!rule) return 0;
        const overrideCapacity = Number(vesselCiiSetting?.capacityOverride);
        if (overrideCapacity > 0) return overrideCapacity;
        if (rule.capacityBasis === 'GT') return Number(generalInfo?.grossTonnage) || 0;
        if (rule.capacityBasis === 'FIXED') return Number(rule.fixedCapacity) || Number(generalInfo?.summerDeadweight) || 0;
        return Number(generalInfo?.summerDeadweight) || 0;
    };

    const findActiveCiiRule = () => {
        const shipTypeCode = selectedCiiShipType || normalizeShipTypeCode(vessels.find(v => v.id === selectedVesselId)?.vesselType || 'TANKER');
        const dwt = Number(generalInfo?.summerDeadweight) || 0;
        const candidates = (ciiRuleSet?.shipTypeRules || []).filter((rule) => rule.shipTypeCode === shipTypeCode);
        if (!candidates.length) return null;

        return candidates.find((rule) => {
            const minOk = rule.minDwt === undefined || rule.minDwt === null || dwt >= Number(rule.minDwt);
            const maxOk = rule.maxDwt === undefined || rule.maxDwt === null || dwt < Number(rule.maxDwt);
            return minOk && maxOk;
        }) || candidates[0];
    };

    const getCiiRuleMetrics = () => {
        const rule = findActiveCiiRule();
        const capacity = getRuleCapacity(rule);
        const reduction = getReductionFactorForYear(selectedYear);
        const referenceCii = rule && capacity > 0
            ? Number(rule.referenceA) * Math.pow(capacity, -Number(rule.referenceC))
            : 0;
        const reductionPercent = Number(reduction?.reductionPercent);
        const requiredCii = referenceCii > 0 && Number.isFinite(reductionPercent)
            ? referenceCii * (1 - (reductionPercent / 100))
            : 0;
        const boundaries = rule && requiredCii > 0
            ? {
                a: requiredCii * Number(rule.ratingD1),
                b: requiredCii * Number(rule.ratingD2),
                c: requiredCii * Number(rule.ratingD3),
                d: requiredCii * Number(rule.ratingD4),
            }
            : null;

        return { rule, capacity, reduction, referenceCii, requiredCii, boundaries };
    };

    const getCiiRatingFromRequired = getFinalCalculatedCiiRating;

    const computeRowEmissions = (row: AggregatedVoyageData) => {
        const fcHsfo = row.meHsfo + row.aeHsfo + row.boilerHsfo;
        const fcMgo = row.meMgo + row.aeMgo + row.boilerMgo;
        const avgHsfo = numericValue(row.avgSulphurHsfo);
        const avgMgo = numericValue(row.avgSulphurMgo);
        const sox = (fcHsfo * avgHsfo + fcMgo * avgMgo) * 20 * 0.001;
        const pm = (fcHsfo * 6.7 + fcMgo * 1.1) * 0.001;
        const nox =
            ((row.meHsfo + row.meMgo) * 85 +
                (row.aeHsfo + row.aeMgo) * 56 +
                (row.boilerHsfo + row.boilerMgo) * 7) *
            0.001;
        const co = (fcHsfo + fcMgo) * 7.4 * 0.001;

        return { sox, pm, nox, co };
    };

    const getDurationHours = (fromDateTime?: string, toDateTime?: string) => {
        if (!fromDateTime || !toDateTime) return 0;
        const fromMs = toDateTimeMs(fromDateTime);
        const toMs = toDateTimeMs(toDateTime);
        if (Number.isNaN(fromMs) || Number.isNaN(toMs) || toMs <= fromMs) return 0;
        return (toMs - fromMs) / (1000 * 60 * 60);
    };

    const getHoursByEventState = (
        state: VoyageEventState,
        fromDateTime?: string,
        toDateTime?: string
    ) => {
        const durationHours = getDurationHours(fromDateTime, toDateTime);
        if (durationHours <= 0) {
            return { hoursAtSea: 0, hoursAtAnchor: 0 };
        }
        if (state === 'SAILING') {
            return { hoursAtSea: durationHours, hoursAtAnchor: 0 };
        }
        if (state === 'AT_ANCHOR' || state === 'AT_BERTH') {
            return { hoursAtSea: 0, hoursAtAnchor: durationHours };
        }
        return { hoursAtSea: 0, hoursAtAnchor: 0 };
    };

    const yearlyReportById = useMemo(() => {
        const map = new Map<number, ReportDtoFull>();
        yearlyReports.forEach((report) => {
            if (report?.id != null) {
                map.set(Number(report.id), report);
            }
        });
        return map;
    }, [yearlyReports]);

    const markRowDirty = (row?: AggregatedVoyageData) => {
        if (!row?.reportId) return;
        setDirtyRowIds((prev) => {
            const next = new Set(prev);
            next.add(Number(row.reportId));
            return next;
        });
    };

    const clearDirtyRows = () => {
        setDirtyRowIds(new Set());
    };

    const saveShipDataSettings = async () => {
        if (!selectedVesselId || !selectedCiiShipType) {
            toast.error('Select a vessel and CII ship type first.');
            return;
        }

        setIsSavingShipData(true);
        try {
            const saved = await saveCiiVesselSetting(Number(selectedVesselId), {
                vesselId: Number(selectedVesselId),
                shipTypeCode: selectedCiiShipType,
                capacityOverride: vesselCiiSetting?.capacityOverride,
                factorFi: vesselCiiSetting?.factorFi,
                factorFm: vesselCiiSetting?.factorFm,
                factorFc: vesselCiiSetting?.factorFc,
                factorFiVse: vesselCiiSetting?.factorFiVse,
                updatedBy: (currentUser as any)?.id,
            });
            setVesselCiiSetting(saved);
            toast.success('CII ship data settings saved.');
        } catch (error) {
            console.error('Failed to save CII ship data settings', error);
            toast.error(getApiErrorMessage(error, 'Failed to save CII ship data settings.'));
        } finally {
            setIsSavingShipData(false);
        }
    };

    const refreshCiiRules = async () => {
        const latest = await getCiiRuleSet();
        setCiiRuleSet(latest);
    };

    const getRuleEditorItems = (): any[] => {
        if (ruleEditorType === 'fuel') return ciiRuleSet?.fuelFactors || [];
        if (ruleEditorType === 'year') return ciiRuleSet?.reductionFactors || [];
        return ciiRuleSet?.shipTypeRules || [];
    };

    const loadRuleEditorItem = (id: string) => {
        setSelectedRuleId(id);
        const item = getRuleEditorItems().find((entry: any) => String(entry.id) === String(id));
        setRuleEditorJson(item ? JSON.stringify(item, null, 2) : '');
    };

    const getRuleEditorObject = () => {
        try {
            return ruleEditorJson.trim() ? JSON.parse(ruleEditorJson) : {};
        } catch {
            return {};
        }
    };

    const setRuleEditorField = (field: string, value: string | boolean) => {
        const current = getRuleEditorObject();
        const numericFields = new Set([
            'co2Factor', 'pmFactor', 'reportingYear', 'reductionPercent', 'correctionYearIndex',
            'minDwt', 'maxDwt', 'fixedCapacity', 'referenceA', 'referenceC',
            'ratingD1', 'ratingD2', 'ratingD3', 'ratingD4',
        ]);
        setRuleEditorJson(JSON.stringify({
            ...current,
            [field]: typeof value === 'boolean'
                ? value
                : numericFields.has(field)
                    ? (value === '' ? null : Number(value))
                    : value,
        }, null, 2));
    };

    const startNewRule = () => {
        setSelectedRuleId('');
        const template =
            ruleEditorType === 'fuel'
                ? { fuelCode: '', displayName: '', aliases: '', co2Factor: 0, pmFactor: 0, active: true }
                : ruleEditorType === 'year'
                    ? { reportingYear: selectedYear + 1, reductionPercent: 0, correctionYearIndex: 0, active: true }
                    : {
                        shipTypeCode: '',
                        displayName: '',
                        bandKey: 'ALL',
                        bandLabel: 'All',
                        capacityBasis: 'DWT',
                        referenceA: 0,
                        referenceC: 0,
                        ratingD1: 0,
                        ratingD2: 0,
                        ratingD3: 0,
                        ratingD4: 0,
                        active: true,
                    };
        setRuleEditorJson(JSON.stringify(template, null, 2));
    };

    const duplicateSelectedShipRuleBand = () => {
        const current = getRuleEditorObject();
        if (!current.shipTypeCode || !current.displayName) {
            toast.error('Select an existing ship type rule first.');
            return;
        }
        const template = {
            ...current,
            id: undefined,
            bandKey: '',
            bandLabel: '',
            minDwt: null,
            maxDwt: null,
            fixedCapacity: null,
            active: true,
        };
        setSelectedRuleId('');
        setRuleEditorJson(JSON.stringify(template, null, 2));
    };

    const saveRuleEditorItem = async () => {
        if (!ruleEditorJson.trim()) {
            toast.error('Select an existing rule or click New Rule first.');
            return;
        }

        setIsSavingRuleEditor(true);
        try {
            const payload = { ...JSON.parse(ruleEditorJson), active: true };
            if (ruleEditorType === 'fuel') {
                await upsertCiiFuelFactor(payload as CiiFuelFactorDto);
            } else if (ruleEditorType === 'year') {
                await upsertCiiReductionFactor(payload as CiiReductionFactorDto);
            } else {
                await upsertCiiShipTypeRule(payload as CiiShipTypeRuleDto);
            }
            await refreshCiiRules();
            toast.success('CII rule saved.');
        } catch (error) {
            console.error('Failed to save CII rule', error);
            toast.error(getApiErrorMessage(error, 'Failed to save CII rule. Check required fields and values.'));
        } finally {
            setIsSavingRuleEditor(false);
        }
    };

    const saveDirtyVoyageRows = async () => {
        if (isSavingVoyageData) return;
        if (!dirtyRowIds.size) {
            toast.info('No unsaved voyage data changes.');
            return;
        }

        const rowsToSave = aggregatedData.filter((row) => row.reportId && dirtyRowIds.has(Number(row.reportId)));
        if (!rowsToSave.length) {
            toast.info('No valid voyage rows to save.');
            clearDirtyRows();
            return;
        }

        setIsSavingVoyageData(true);
        try {
            await Promise.all(rowsToSave.map((row) => persistVoyageRow(row, false)));
            toast.success('Voyage data saved.');
            clearDirtyRows();
        } catch (error) {
            console.error('Failed to save voyage data rows', error);
            toast.error(getApiErrorMessage(error, 'Failed to save voyage data.'));
        } finally {
            setIsSavingVoyageData(false);
        }
    };

    const persistVoyageRow = async (row: AggregatedVoyageData, showToast = true) => {
        if (!row.reportId) return;
        const report = yearlyReportById.get(Number(row.reportId));
        const voyageId = Number(row.reportVoyageId || report?.voyage || 0);
        if (!voyageId) {
            if (showToast) toast.error('Missing voyage data to save.');
            return;
        }

        const vesselId = Number(row.vesselId || report?.vesselId || selectedVesselId || 0) || undefined;

        const inputStsVoyage = normalizeStsVoyageInput(row.stsVoyage);
        const existing = ciiRowByReportId[Number(row.reportId)];
        const existingStsVoyage = normalizeStsVoyageInput(existing?.stsVoyage);
        const nextStsVoyage = inputStsVoyage || existingStsVoyage || '';
        const nextBoilerUsed = String(row.boilerUsed || '');
        const nextFcVoyageHsfo = normalizeNumericString(row.fcVoyageHsfo);
        const nextFcVoyageMgo = normalizeNumericString(row.fcVoyageMgo);
        const nextHsfoSulphur = normalizeNumericString(row.avgSulphurHsfo);
        const nextMgoSulphur = normalizeNumericString(row.avgSulphurMgo);
        const nextFcElectricalHsfo = normalizeNumericString(row.fcElectricalHsfo);
        const nextFcElectricalMgo = normalizeNumericString(row.fcElectricalMgo);
        const nextFcBoilerHsfo = normalizeNumericString(row.fcBoilerHsfo);
        const nextFcBoilerMgo = normalizeNumericString(row.fcBoilerMgo);
        const nextFcOtherHsfo = normalizeNumericString(row.fcOtherHsfo);
        const nextFcOtherMgo = normalizeNumericString(row.fcOtherMgo);
        const nextTfHsfo = normalizeNumericString(row.tfHsfo);
        const nextTfMgo = normalizeNumericString(row.tfMgo);
        const nextDistanceAdjustment = normalizeNumericString(row.distanceAdjustment);
        const nextReeferKwh = normalizeNumericString(row.reeferKwh);
        const nextReeferSfoc = normalizeNumericString(row.reeferSfoc);
        const nextReeferFuelCode = getConfiguredFuelCode(row.reeferFuelCode) || String(row.reeferFuelCode || '').trim().toUpperCase();
        const nextRemarks = String(row.remarks || '');
        if (existing) {
            const existingSts = existingStsVoyage || '';
            const existingBoiler = String(existing.boilerUsed || '');
            const existingHsfo = existing.fcVoyageHsfo != null ? String(existing.fcVoyageHsfo) : '';
            const existingMgo = existing.fcVoyageMgo != null ? String(existing.fcVoyageMgo) : '';
            const existingHsfoSulphur = existing.hsfoSulphur != null ? String(existing.hsfoSulphur) : '';
            const existingMgoSulphur = existing.mgoSulphur != null ? String(existing.mgoSulphur) : '';
            const existingRemarks = String(existing.remarks || '');
            const existingDistanceAdjustment = existing.distanceAdjustment != null ? String(existing.distanceAdjustment) : '';
            const existingHsfoDetail = getPersistedFuelDetail(existing, 'HSFO');
            const existingMgoDetail = getPersistedFuelDetail(existing, 'MGO');
            if (
                existingSts === nextStsVoyage &&
                existingBoiler === nextBoilerUsed &&
                existingHsfo === nextFcVoyageHsfo &&
                existingMgo === nextFcVoyageMgo &&
                existingHsfoSulphur === nextHsfoSulphur &&
                existingMgoSulphur === nextMgoSulphur &&
                String(existingHsfoDetail?.fcElectrical || '') === nextFcElectricalHsfo &&
                String(existingMgoDetail?.fcElectrical || '') === nextFcElectricalMgo &&
                String(existingHsfoDetail?.fcBoiler || '') === nextFcBoilerHsfo &&
                String(existingMgoDetail?.fcBoiler || '') === nextFcBoilerMgo &&
                String(existingHsfoDetail?.fcOther || '') === nextFcOtherHsfo &&
                String(existingMgoDetail?.fcOther || '') === nextFcOtherMgo &&
                String(existingHsfoDetail?.fcVoyageAdjustment || '') === nextTfHsfo &&
                String(existingMgoDetail?.fcVoyageAdjustment || '') === nextTfMgo &&
                existingDistanceAdjustment === nextDistanceAdjustment &&
                String(existing.reeferKwh || '') === nextReeferKwh &&
                String(existing.reeferSfoc || '') === nextReeferSfoc &&
                String(existing.reeferFuelCode || '') === nextReeferFuelCode &&
                existingRemarks === nextRemarks
            ) {
                return;
            }
        }

        const detailFuelCodes = Array.from(new Set([
            'HSFO',
            'MGO',
            ...Object.keys(row.fuelBreakdown || {}),
            ...Object.keys(row.correctionBreakdown || {}),
            ...(nextReeferFuelCode ? [nextReeferFuelCode] : []),
        ])).filter(Boolean);

        const fuelDetails = detailFuelCodes.map((fuelCode) => ({
            fuelCode,
            fcVoyage: fuelCode === 'HSFO' ? nextFcVoyageHsfo : fuelCode === 'MGO' ? nextFcVoyageMgo : '',
            avgSulphur: fuelCode === 'HSFO' ? nextHsfoSulphur : fuelCode === 'MGO' ? nextMgoSulphur : '',
            fcBoiler: fuelCode === 'HSFO' ? nextFcBoilerHsfo : fuelCode === 'MGO' ? nextFcBoilerMgo : normalizeNumericString(row.correctionBreakdown?.[fuelCode]?.fcBoiler),
            fcElectrical: fuelCode === 'HSFO' ? nextFcElectricalHsfo : fuelCode === 'MGO' ? nextFcElectricalMgo : normalizeNumericString(row.correctionBreakdown?.[fuelCode]?.fcElectrical),
            fcOther: fuelCode === 'HSFO' ? nextFcOtherHsfo : fuelCode === 'MGO' ? nextFcOtherMgo : normalizeNumericString(row.correctionBreakdown?.[fuelCode]?.fcOther),
            fcVoyageAdjustment: fuelCode === 'HSFO' ? nextTfHsfo : fuelCode === 'MGO' ? nextTfMgo : '',
        }));

        const payload: CiiVoyageRowDto = {
            voyageId,
            reportId: Number(row.reportId),
            vesselId,
            stsVoyage: nextStsVoyage,
            fcVoyageHsfo: nextFcVoyageHsfo,
            fcVoyageMgo: nextFcVoyageMgo,
            boilerUsed: nextBoilerUsed,
            hsfoSulphur: nextHsfoSulphur,
            mgoSulphur: nextMgoSulphur,
            distanceAdjustment: nextDistanceAdjustment,
            reeferKwh: nextReeferKwh,
            reeferSfoc: nextReeferSfoc,
            reeferFuelCode: nextReeferFuelCode,
            remarks: nextRemarks,
            fuelDetails,
        };

        try {
            const saved = await upsertCiiVoyageRow(payload);
            if (saved?.reportId != null) {
                setCiiRowByReportId((prev) => ({
                    ...prev,
                    [Number(saved.reportId)]: saved,
                }));
            }
            if (showToast) toast.success('Saved');
        } catch (error) {
            console.error('Failed to save CII voyage row:', error);
            if (showToast) toast.error('Save failed');
        }
    };

    const mergeVoyageRowsByEvent = (rows: AggregatedVoyageData[]) => {
        if (!rows.length) return rows;

        const sortedRows = [...rows].sort((a, b) => {
            const left = toDateTimeMs(a.sourceDateTime || a.fromDate);
            const right = toDateTimeMs(b.sourceDateTime || b.fromDate);

            if (!Number.isNaN(left) && !Number.isNaN(right)) {
                return left - right;
            }

            return (a.sourceDateTime || a.fromDate || '').localeCompare(b.sourceDateTime || b.fromDate || '');
        });

        const grouped: Array<
            AggregatedVoyageData & {
                __state: VoyageEventState;
                __voyageKey: string;
                __rows: AggregatedVoyageData[];
            }
        > = [];
        let previousState: VoyageEventState | undefined;

        sortedRows.forEach((row) => {
            const eventState = resolveVoyageEventState(row, previousState);
            const voyageKey = String(row.voyageNo || '').trim().toUpperCase() || '__NO_VOYAGE__';
            const lastGroup = grouped[grouped.length - 1];
            const shouldMerge =
                Boolean(lastGroup) &&
                lastGroup.__state === eventState &&
                lastGroup.__voyageKey === voyageKey;

            if (!shouldMerge) {
                grouped.push({
                    ...row,
                    eventType: toVoyageEventLabel(eventState),
                    __state: eventState,
                    __voyageKey: voyageKey,
                    __rows: [row],
                });
                previousState = eventState;
                return;
            }

            const current = lastGroup;
            current.__rows.push(row);
            current.toDate = pickLaterDateTime(current.toDate, row.toDate || row.fromDate);
            current.distance += toFiniteNumber(row.distance);
            current.hoursAtSea += toFiniteNumber(row.hoursAtSea);
            current.hoursAtAnchor += toFiniteNumber(row.hoursAtAnchor);
            if (row.cargoWeightProvided) {
                // Cargo weight is a point-in-time state from the report, not an additive quantity.
                current.cargoCarried = toFiniteNumber(row.cargoCarried);
                current.cargoWeightProvided = true;
            }
            current.meHsfo += toFiniteNumber(row.meHsfo);
            current.aeHsfo += toFiniteNumber(row.aeHsfo);
            current.boilerHsfo += toFiniteNumber(row.boilerHsfo);
            current.meMgo += toFiniteNumber(row.meMgo);
            current.aeMgo += toFiniteNumber(row.aeMgo);
            current.boilerMgo += toFiniteNumber(row.boilerMgo);
            current.totalFuel += toFiniteNumber(row.totalFuel);
            current.totalCo2 += toFiniteNumber(row.totalCo2);
            current.bunkerReceivedHfo += toFiniteNumber(row.bunkerReceivedHfo);
            current.bunkerReceivedMgo += toFiniteNumber(row.bunkerReceivedMgo);
            if (row.fuelBreakdown) {
                current.fuelBreakdown = current.fuelBreakdown || {};
                Object.entries(row.fuelBreakdown).forEach(([fuelCode, fuel]) => {
                    const target = getOrCreateFuelBreakdown(current.fuelBreakdown || {}, fuelCode);
                    target.me += toFiniteNumber(fuel.me);
                    target.ae += toFiniteNumber(fuel.ae);
                    target.boiler += toFiniteNumber(fuel.boiler);
                    target.other += toFiniteNumber(fuel.other);
                    target.rob = toFiniteNumber(fuel.rob) || target.rob;
                });
            }
            if (row.correctionBreakdown) {
                current.correctionBreakdown = current.correctionBreakdown || {};
                current.correctionSourceByFuel = current.correctionSourceByFuel || {};
                Object.entries(row.correctionBreakdown).forEach(([fuelCode, correction]) => {
                    const target = getOrCreateFuelCorrection(current.correctionBreakdown || {}, fuelCode);
                    target.fcElectrical += toFiniteNumber(correction.fcElectrical);
                    target.fcBoiler += toFiniteNumber(correction.fcBoiler);
                    target.fcOther += toFiniteNumber(correction.fcOther);
                    const source = row.correctionSourceByFuel?.[fuelCode];
                    if (source) {
                        current.correctionSourceByFuel![fuelCode] = {
                            ...(current.correctionSourceByFuel![fuelCode] || {}),
                            ...source,
                        };
                    }
                });
            }
            if (row.unmappedFuelTypes?.length) {
                current.unmappedFuelTypes = Array.from(new Set([...(current.unmappedFuelTypes || []), ...row.unmappedFuelTypes]));
            }
            current.cargoHandled += toFiniteNumber(row.cargoHandled);

            if (row.fcVoyageHsfo !== undefined) {
                current.fcVoyageHsfo = toFiniteNumber(current.fcVoyageHsfo) + numericValue(row.fcVoyageHsfo);
            }
            if (row.fcVoyageMgo !== undefined) {
                current.fcVoyageMgo = toFiniteNumber(current.fcVoyageMgo) + numericValue(row.fcVoyageMgo);
            }
            if (row.fcElectricalHsfo !== undefined) {
                current.fcElectricalHsfo = toFiniteNumber(current.fcElectricalHsfo) + numericValue(row.fcElectricalHsfo);
            }
            if (row.fcElectricalMgo !== undefined) {
                current.fcElectricalMgo = toFiniteNumber(current.fcElectricalMgo) + numericValue(row.fcElectricalMgo);
            }
            if (row.fcBoilerHsfo !== undefined) {
                current.fcBoilerHsfo = toFiniteNumber(current.fcBoilerHsfo) + numericValue(row.fcBoilerHsfo);
            }
            if (row.fcBoilerMgo !== undefined) {
                current.fcBoilerMgo = toFiniteNumber(current.fcBoilerMgo) + numericValue(row.fcBoilerMgo);
            }
            if (row.fcOtherHsfo !== undefined) {
                current.fcOtherHsfo = toFiniteNumber(current.fcOtherHsfo) + numericValue(row.fcOtherHsfo);
            }
            if (row.fcOtherMgo !== undefined) {
                current.fcOtherMgo = toFiniteNumber(current.fcOtherMgo) + numericValue(row.fcOtherMgo);
            }
            if (row.tfHsfo !== undefined) {
                current.tfHsfo = toFiniteNumber(current.tfHsfo) + numericValue(row.tfHsfo);
            }
            if (row.tfMgo !== undefined) {
                current.tfMgo = toFiniteNumber(current.tfMgo) + numericValue(row.tfMgo);
            }
            if (row.distanceAdjustment !== undefined) {
                current.distanceAdjustment = toFiniteNumber(current.distanceAdjustment) + numericValue(row.distanceAdjustment);
            }
            if (row.reeferKwh !== undefined) current.reeferKwh = toFiniteNumber(current.reeferKwh) + numericValue(row.reeferKwh);
            if (row.reeferSfoc !== undefined && current.reeferSfoc === undefined) current.reeferSfoc = row.reeferSfoc;
            if (row.reeferFuelCode && !current.reeferFuelCode) current.reeferFuelCode = row.reeferFuelCode;
            current.reeferContainersOnboard = Boolean(current.reeferContainersOnboard || row.reeferContainersOnboard);
            current.cargoCoolingSystemInUse = Boolean(current.cargoCoolingSystemInUse || row.cargoCoolingSystemInUse);

            if (row.hfoRob !== undefined) current.hfoRob = row.hfoRob;
            if (row.mgoRob !== undefined) current.mgoRob = row.mgoRob;
            if (row.avgSulphurHsfo !== undefined) current.avgSulphurHsfo = row.avgSulphurHsfo;
            if (row.avgSulphurMgo !== undefined) current.avgSulphurMgo = row.avgSulphurMgo;

            if (!current.vesselCondition && row.vesselCondition) current.vesselCondition = row.vesselCondition;
            if (!current.portNameLocation && row.portNameLocation) current.portNameLocation = row.portNameLocation;
            if (!current.stsVoyage && row.stsVoyage) current.stsVoyage = row.stsVoyage;
            if (!current.boilerUsed && row.boilerUsed) current.boilerUsed = row.boilerUsed;
            if (!current.remarks && row.remarks) current.remarks = row.remarks;

            if (row.reportId) current.reportId = row.reportId;
            if (row.reportAssignmentId) current.reportAssignmentId = row.reportAssignmentId;
            if (row.reportVoyageId) current.reportVoyageId = row.reportVoyageId;
            if (row.reportTypeName) current.reportTypeName = row.reportTypeName;
            if (row.reportStatus) current.reportStatus = row.reportStatus;
            if (row.reportHasWarnings !== undefined) current.reportHasWarnings = row.reportHasWarnings;
            if (row.reportNoOfWarnings !== undefined) current.reportNoOfWarnings = row.reportNoOfWarnings;

            previousState = eventState;
        });

        grouped.forEach((group) => {
            const arrivalRow = group.__rows.find((row) => isArrivalKind(row));
            const cospOrDepartureRow = group.__rows.find((row) => isDepartureOrCospKind(row));

            if (group.__state === 'SAILING') {
                if (cospOrDepartureRow?.fromDate) {
                    group.fromDate = cospOrDepartureRow.fromDate;
                    group.sourceDateTime = cospOrDepartureRow.sourceDateTime || cospOrDepartureRow.fromDate;
                }
                if (cospOrDepartureRow?.portNameLocation) {
                    group.portNameLocation = cospOrDepartureRow.portNameLocation;
                }
            }

            if ((group.__state === 'AT_BERTH' || group.__state === 'AT_ANCHOR') && arrivalRow) {
                if (arrivalRow.fromDate) {
                    group.fromDate = arrivalRow.fromDate;
                    group.sourceDateTime = arrivalRow.sourceDateTime || arrivalRow.fromDate;
                }
                if (arrivalRow.portNameLocation) {
                    group.portNameLocation = arrivalRow.portNameLocation;
                }
            }

            const computedHours = getHoursByEventState(group.__state, group.fromDate, group.toDate);
            group.hoursAtSea = computedHours.hoursAtSea;
            group.hoursAtAnchor = computedHours.hoursAtAnchor;

            if (group.reportId) {
                const reportMatch = group.__rows.find(
                    (row) => Number(row.reportId) === Number(group.reportId)
                );
                if (reportMatch?.stsVoyage !== undefined) {
                    group.stsVoyage = reportMatch.stsVoyage;
                }
            }
        });

        return grouped.map(({ __state, __voyageKey, __rows, cargoWeightProvided, ...row }) => row);
    };

    const getReportDateOnly = (report?: ReportDtoFull) => {
        const reportDateTime = buildReportUtcDateTime(report);
        return toDateOnly(reportDateTime || report?.submittedDateTime || report?.createdDateTime);
    };

    const getReportSortDateTime = (report?: ReportDtoFull) =>
        buildReportUtcDateTime(report) || report?.submittedDateTime || report?.createdDateTime || '';

    const isBunkerReport = (report?: ReportDtoFull) =>
        (report?.reportType || '').toLowerCase().includes('bunker') ||
        Boolean(findFieldTextExact(report?.values || [], ['Bunker Received Rows'])) ||
        (
            Boolean(findFieldTextExact(report?.values || [], ['Fuel Type'])) &&
            (
                Boolean(findFieldTextExact(report?.values || [], ["HFO (MT's)"])) ||
                Boolean(findFieldTextExact(report?.values || [], ["MGO (MT's)"]))
            )
        );

    const hasMatchingField = (report: ReportDtoFull | undefined, labelParts: string[]) =>
        Boolean(findFieldText(report?.values || [], labelParts));

    const isPositionReportCandidate = (report?: ReportDtoFull) => {
        if (!report || isBunkerReport(report)) return false;

        return (
            hasMatchingField(report, ['report date time']) ||
            hasMatchingField(report, ['hours at sea', 'hours at anchor']) ||
            hasMatchingField(report, ['distance travelled', 'distance traveled']) ||
            hasMatchingField(report, ['distance made good']) ||
            hasMatchingField(report, ['vessel condition', 'loading condition', 'ballast / laden', 'ballast/laden']) ||
            hasMatchingField(report, ['event type', 'port name', 'location', 'voyage no', 'voyage number'])
        );
    };

    const buildReportUtcDateTime = (report?: ReportDtoFull) => {
        if (!report) return '';

        const reportDateTime =
            findFieldTextExact(report.values || [], ['Report Date Time']) ||
            findFieldText(report.values, ['report date time']);
        if (reportDateTime) return reportDateTime;

        const dateTimeUtc =
            findFieldTextExact(report.values || [], ['Date Time (UTC)', 'DateTime (UTC)']) ||
            findFieldText(report.values, ['date time (utc)', 'datetime (utc)', 'date time utc']);
        if (dateTimeUtc) return dateTimeUtc;

        const dateTimeLocal =
            findFieldTextExact(report.values || [], ['Date Time (Local)', 'DateTime (Local)']) ||
            findFieldText(report.values, ['date time (local)', 'datetime (local)', 'date time local']);
        if (dateTimeLocal) return dateTimeLocal;

        const utcDate = findFieldText(report.values, ['date (utc)', 'date utc']);
        const utcTime = findFieldText(report.values, ['time (utc)', 'time utc']);
        if (utcDate && utcTime) return `${utcDate} ${utcTime}`;
        if (utcDate) return utcDate;

        return report.submittedDateTime || report.createdDateTime || '';
    };

    const getQuarterBoundaryMidnightMs = (year: number) => [
        new Date(year, 3, 1, 0, 0, 0, 0).getTime(),
        new Date(year, 6, 1, 0, 0, 0, 0).getTime(),
        new Date(year, 9, 1, 0, 0, 0, 0).getTime(),
        new Date(year + 1, 0, 1, 0, 0, 0, 0).getTime(),
    ].filter((time) => !Number.isNaN(time));

    const getBoundaryMidnightMsFromReports = (reports: ReportDtoFull[], year: number) => {
        if (!reports.length) return [];
        const boundaries = getQuarterBoundaryMidnightMs(year);
        const boundarySet = new Set(boundaries);
        const matched = new Set<number>();

        reports.forEach((report) => {
            const reportDateTime = buildReportUtcDateTime(report);
            const departureDateTime = getDateTimeFieldValueFromReport(report, ['departure']);
            [reportDateTime, departureDateTime].forEach((value) => {
                const time = toDateTimeMs(value);
                if (boundarySet.has(time)) {
                    matched.add(time);
                }
            });
        });

        return boundaries.filter((time) => matched.has(time));
    };

    const formatLocalDateTime = (timeMs: number) => {
        const date = new Date(timeMs);
        const pad = (value: number) => String(value).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const scaleNumeric = (value: number | string | undefined, ratio: number) => {
        if (value === undefined || value === null || value === '') return undefined;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed * ratio : undefined;
    };

    const splitRowByBoundaryMidnights = (row: AggregatedVoyageData, boundaryMs: number[]) => {
        const startMs = toDateTimeMs(row.fromDate);
        const endMs = toDateTimeMs(row.toDate);
        if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return [row];

        const cuts = boundaryMs
            .filter((time) => time > startMs && time < endMs)
            .sort((a, b) => a - b);
        if (!cuts.length) return [row];

        const totalDuration = endMs - startMs;
        const segments: Array<{ start: number; end: number }> = [];
        let segmentStart = startMs;
        cuts.forEach((cut) => {
            segments.push({ start: segmentStart, end: cut });
            segmentStart = cut;
        });
        segments.push({ start: segmentStart, end: endMs });

        return segments.map((segment) => {
            const ratio = (segment.end - segment.start) / totalDuration;
            const scaledFuelBreakdown: FuelConsumptionMap | undefined = row.fuelBreakdown
                ? Object.entries(row.fuelBreakdown).reduce((acc, [fuelCode, fuel]) => {
                    acc[fuelCode] = {
                        me: fuel.me * ratio,
                        ae: fuel.ae * ratio,
                        boiler: fuel.boiler * ratio,
                        other: fuel.other * ratio,
                        rob: fuel.rob,
                    };
                    return acc;
                }, {} as FuelConsumptionMap)
                : undefined;
            const scaledCorrectionBreakdown: FuelCorrectionMap | undefined = row.correctionBreakdown
                ? Object.entries(row.correctionBreakdown).reduce((acc, [fuelCode, correction]) => {
                    acc[fuelCode] = {
                        fcElectrical: correction.fcElectrical * ratio,
                        fcBoiler: correction.fcBoiler * ratio,
                        fcOther: correction.fcOther * ratio,
                    };
                    return acc;
                }, {} as FuelCorrectionMap)
                : undefined;
            return {
                ...row,
                fromDate: formatLocalDateTime(segment.start),
                toDate: formatLocalDateTime(segment.end),
                sourceDateTime: formatLocalDateTime(segment.start),
                distance: row.distance * ratio,
                hoursAtSea: row.hoursAtSea * ratio,
                hoursAtAnchor: row.hoursAtAnchor * ratio,
                  cargoCarried: (row.vesselCondition || '').trim().toUpperCase() === 'L' ? row.cargoCarried : 0,
                cargoHandled: row.cargoHandled * ratio,
                meHsfo: row.meHsfo * ratio,
                aeHsfo: row.aeHsfo * ratio,
                boilerHsfo: row.boilerHsfo * ratio,
                meMgo: row.meMgo * ratio,
                aeMgo: row.aeMgo * ratio,
                boilerMgo: row.boilerMgo * ratio,
                totalFuel: row.totalFuel * ratio,
                totalCo2: row.totalCo2 * ratio,
                bunkerReceivedHfo: row.bunkerReceivedHfo * ratio,
                bunkerReceivedMgo: row.bunkerReceivedMgo * ratio,
                fuelBreakdown: scaledFuelBreakdown,
                correctionBreakdown: scaledCorrectionBreakdown,
                fcVoyageHsfo: scaleNumeric(row.fcVoyageHsfo, ratio),
                fcVoyageMgo: scaleNumeric(row.fcVoyageMgo, ratio),
                fcElectricalHsfo: scaleNumeric(row.fcElectricalHsfo, ratio),
                fcElectricalMgo: scaleNumeric(row.fcElectricalMgo, ratio),
                fcBoilerHsfo: scaleNumeric(row.fcBoilerHsfo, ratio),
                fcBoilerMgo: scaleNumeric(row.fcBoilerMgo, ratio),
                fcOtherHsfo: scaleNumeric(row.fcOtherHsfo, ratio),
                fcOtherMgo: scaleNumeric(row.fcOtherMgo, ratio),
                tfHsfo: scaleNumeric(row.tfHsfo, ratio),
                tfMgo: scaleNumeric(row.tfMgo, ratio),
                distanceAdjustment: scaleNumeric(row.distanceAdjustment, ratio),
                reeferKwh: scaleNumeric(row.reeferKwh, ratio),
                reeferContainersOnboard: row.reeferContainersOnboard,
                cargoCoolingSystemInUse: row.cargoCoolingSystemInUse,
            };
        });
    };

    const splitRowsOnQuarterBoundaries = (
        rows: AggregatedVoyageData[],
        reports: ReportDtoFull[],
        year: number
    ) => {
        const boundaryMs = getBoundaryMidnightMsFromReports(reports, year);
        if (!boundaryMs.length) return rows;
        return rows.flatMap((row) => splitRowByBoundaryMidnights(row, boundaryMs));
    };


    const getDistanceTravelledFromNoonReport = (report?: ReportDtoFull) => {
        if (!report) return null;

        const exactObservedDistance =
            findFieldTextExact(report.values, ['Distance Made Good (24 Hours) (NM)']) ||
            findFieldTextExact(report.values, ['Total Distance Made Good (NM)']) ||
            findFieldTextExact(report.values, ['Distance Observed (nm)']) ||
            findFieldTextExact(report.values, ['Distance Observed (NM)']) ||
            findFieldTextExact(report.values, ['Distance Observed (Nm)']);
        if (exactObservedDistance) {
            return parseFloat(exactObservedDistance) || 0;
        }

        const observedDistanceField = report.values.find((v) => {
            const label = v.field.label.toLowerCase().trim();
            return label.includes('distance observed') || label.includes('distance made good');
        });
        if (observedDistanceField) {
            return parseFloat(observedDistanceField.valueText) || 0;
        }

        const exactStoredDistance = findFieldTextExact(report.values, ['Distance Travelled']);
        if (exactStoredDistance) {
            return parseFloat(exactStoredDistance) || 0;
        }

        const exactDistanceField = report.values.find(v =>
            [
                'distance travelled',
                'distance traveled',
                'total distance travelled',
                'total distance traveled',
                'distance made good (24 hours) (nm)',
                'total distance made good (nm)',
            ]
                .includes(v.field.label.trim().toLowerCase().replace(/:$/, ''))
        );
        if (exactDistanceField) {
            return parseFloat(exactDistanceField.valueText) || 0;
        }

        const distanceField = report.values.find(v => {
            const label = v.field.label.toLowerCase().trim();
            return (
                label.includes('distance travelled') ||
                label.includes('distance traveled') ||
                label.includes('distance made good') ||
                label.includes('distance run') ||
                label.includes('run distance') ||
                label.includes('distance covered') ||
                (label.includes('distance') && label.includes('nm'))
            );
        });

        return distanceField ? parseFloat(distanceField.valueText) || 0 : null;
    };

    const getDistanceTravelledForDay = (reportsForDay: ReportDtoFull[]) => {
        for (const dayReport of reportsForDay) {
            const distance = getDistanceTravelledFromNoonReport(dayReport);
            if (distance !== null) {
                return distance;
            }
        }

        return 0;
    };

    const parseJsonArray = (raw?: string) => {
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const getMachineryBucket = (machineryRaw: unknown) => {
        const machinery = String(machineryRaw || '').trim().toLowerCase().replace(/[\s/_()-]+/g, '');
        if (!machinery) return '';
        if (machinery.startsWith('me')) return 'me';
        if (machinery.startsWith('ae')) return 'ae';
        if (machinery.startsWith('boiler')) return 'boiler';
        return '';
    };

    const normalizeFuelToken = (value: unknown) =>
        String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, ' ');

    const getConfiguredFuelCode = (fuelRaw: unknown) => {
        const fuel = normalizeFuelToken(fuelRaw);
        if (!fuel) return '';

        for (const factor of ciiRuleSet?.fuelFactors || []) {
            const aliases = String(factor.aliases || factor.fuelCode || '')
                .split(',')
                .map((alias) => normalizeFuelToken(alias))
                .filter(Boolean);
            if (normalizeFuelToken(factor.fuelCode) === fuel || aliases.includes(fuel)) {
                return factor.fuelCode;
            }
        }

        if (fuel === 'HFO' || fuel === 'HSFO') return 'HSFO';
        if (fuel === 'MGO' || fuel === 'MDO') return 'MGO';
        return '';
    };

    const getFuelBucket = (fuelRaw: unknown) => {
        const fuelCode = getConfiguredFuelCode(fuelRaw);
        if (fuelCode === 'HSFO') return 'hsfo';
        if (fuelCode === 'MGO') return 'mgo';
        return '';
    };

    const getOrCreateFuelBreakdown = (fuels: FuelConsumptionMap, fuelCode: string) => {
        if (!fuels[fuelCode]) {
            fuels[fuelCode] = { me: 0, ae: 0, boiler: 0, other: 0, rob: 0 };
        }
        return fuels[fuelCode];
    };

    const getOrCreateFuelCorrection = (corrections: FuelCorrectionMap, fuelCode: string) => {
        if (!corrections[fuelCode]) {
            corrections[fuelCode] = { fcElectrical: 0, fcBoiler: 0, fcOther: 0 };
        }
        return corrections[fuelCode];
    };

    const normalizeOperationType = (value: unknown) => {
        const normalized = normalizeTextForMatch(String(value || ''));
        if (!normalized) return '';
        if (normalized.includes('routine')) return 'routine';
        if ((normalized.includes('inert') || normalized.includes('intert')) && normalized.includes('heat')) return 'inerting_heating';
        if (normalized.includes('discharge') && normalized.includes('pump')) return 'discharge_pumps';
        if (normalized.includes('discharge')) return 'discharge_operations';
        return normalized.replace(/\s+/g, '_');
    };

    const normalizeMachineryForCorrection = (value: unknown) =>
        normalizeTextForMatch(String(value || '')).replace(/\s+/g, '');

    const isBoilerCorrectionMachinery = (machinery: unknown) => {
        const normalized = normalizeMachineryForCorrection(machinery);
        return normalized === 'boiler1' || normalized === 'boiler2';
    };

    const isAeMachinery = (machinery: unknown) => {
        const normalized = normalizeMachineryForCorrection(machinery);
        return ['ae1', 'ae2', 'ae3', 'ae4'].includes(normalized);
    };

    const isBoilerCorrectionOperation = (operationType: unknown) => {
        const normalized = normalizeOperationType(operationType);
        return normalized === 'inerting_heating' || normalized === 'discharge_operations';
    };

    const isAeDischargePumpCorrection = (machinery: unknown, operationType: unknown) =>
        isAeMachinery(machinery) && normalizeOperationType(operationType) === 'discharge_pumps';

    const isStandalonePumpOtherCorrection = (machinery: unknown, operationType: unknown) => {
        const normalizedMachinery = normalizeTextForMatch(String(machinery || ''));
        const normalizedOperation = normalizeOperationType(operationType);
        const isPump = normalizedMachinery.includes('pump') && (normalizedMachinery.includes('cargo') || normalizedMachinery.includes('discharge'));
        return isPump && (normalizedOperation === 'discharge_pumps' || normalizedOperation === 'discharge_operations');
    };

    const getNestedArrayValue = (rows: unknown[], rowIndex: number, itemIndex: number) => {
        const row = rows[rowIndex];
        return Array.isArray(row) ? row[itemIndex] : undefined;
    };

    const getFuelFactor = (fuelCode: string): CiiFuelFactorDto | undefined =>
        (ciiRuleSet?.fuelFactors || []).find((factor) => factor.fuelCode === fuelCode);

    const getFuelTotal = (fuel?: FuelConsumptionBreakdown) =>
        fuel ? fuel.me + fuel.ae + fuel.boiler + fuel.other : 0;

    const computeCo2FromFuelBreakdown = (fuels?: FuelConsumptionMap) => {
        if (!fuels) return 0;
        return Object.entries(fuels).reduce((acc, [fuelCode, fuel]) => {
            const factor = getFuelFactor(fuelCode);
            if (!factor) return acc;
            return acc + (getFuelTotal(fuel) * Number(factor.co2Factor || 0));
        }, 0);
    };

    const getFuelCodesInRows = (rows: AggregatedVoyageData[]) =>
        Array.from(new Set(rows.flatMap((row) => Object.keys(row.fuelBreakdown || {}))));

    const sumFuelConsumptionForRows = (rows: AggregatedVoyageData[], fuelCode: string) =>
        rows.reduce((acc, row) => acc + getFuelTotal(row.fuelBreakdown?.[fuelCode]), 0);

    const getCorrectionFactorProduct = () => {
        const factorValues = [
            Number(vesselCiiSetting?.factorFi || 1),
            Number(vesselCiiSetting?.factorFm || 1),
            Number(vesselCiiSetting?.factorFc || 1),
            Number(vesselCiiSetting?.factorFiVse || 1),
        ].map((value) => (Number.isFinite(value) && value > 0 ? value : 1));
        return factorValues.reduce((acc, value) => acc * value, 1);
    };

    const getCorrectedDistanceForRows = (rows: AggregatedVoyageData[]) => {
        const totalDistance = rows.reduce((acc, row) => acc + toFiniteNumber(row.distance), 0);
        const dx = rows.reduce((acc, row) => acc + numericValue(row.distanceAdjustment), 0);
        return Math.max(0, totalDistance - dx);
    };

    const computeCorrectedCo2ForRow = (row: AggregatedVoyageData) => {
        const stsVoyageIsY = resolveStsVoyageValue(row.stsVoyage) === 'Y';
        // Statutory SEEMP EF-026: When STS Voyage is 'Y', voyage event is exempt from Attained CII calculation (Corrected CO2 = 0)
        if (stsVoyageIsY) {
            return 0;
        }

        const boilerFactor = 0.75 - (0.03 * getShipTypeIndexByYear(selectedYear));

        if (row.fuelBreakdown && Object.keys(row.fuelBreakdown).length > 0) {
            return Object.entries(row.fuelBreakdown).reduce((acc, [fuelCode, fuel]) => {
                const factor = getFuelFactor(fuelCode);
                if (!factor) return acc;
                const fuelTotal = getFuelTotal(fuel);
                const fcVoyage =
                    fuelCode === 'HSFO'
                        ? numericValue(row.fcVoyageHsfo)
                        : fuelCode === 'MGO'
                            ? numericValue(row.fcVoyageMgo)
                            : 0;
                const tfExplicit =
                    fuelCode === 'HSFO'
                        ? numericValue(row.tfHsfo)
                        : fuelCode === 'MGO'
                            ? numericValue(row.tfMgo)
                            : 0;
                const fcElectrical = getCorrectionValueForFuel(row, fuelCode, 'fcElectrical');
                const fcOther = getCorrectionValueForFuel(row, fuelCode, 'fcOther');
                const fcBoiler = getCorrectionValueForFuel(row, fuelCode, 'fcBoiler');
                const correctedFuel = Math.max(0, fuelTotal - (fcVoyage + tfExplicit + (boilerFactor * (fcBoiler + fcElectrical + fcOther))));
                return acc + (correctedFuel * Number(factor.co2Factor || 0));
            }, 0);
        }

        const fcHsfo = row.meHsfo + row.aeHsfo + row.boilerHsfo;
        const fcMgo = row.meMgo + row.aeMgo + row.boilerMgo;
        const fcVoyageHsfo = numericValue(row.fcVoyageHsfo);
        const fcVoyageMgo = numericValue(row.fcVoyageMgo);
        const tfHsfo = numericValue(row.tfHsfo);
        const tfMgo = numericValue(row.tfMgo);
        const fcBoilerHsfo = getCorrectionValueForFuel(row, 'HSFO', 'fcBoiler');
        const fcBoilerMgo = getCorrectionValueForFuel(row, 'MGO', 'fcBoiler');
        const fcElectricalHsfo = getCorrectionValueForFuel(row, 'HSFO', 'fcElectrical');
        const fcElectricalMgo = getCorrectionValueForFuel(row, 'MGO', 'fcElectrical');
        const fcOtherHsfo = getCorrectionValueForFuel(row, 'HSFO', 'fcOther');
        const fcOtherMgo = getCorrectionValueForFuel(row, 'MGO', 'fcOther');
        const correctedHsfo = Math.max(0, fcHsfo - (fcVoyageHsfo + tfHsfo + (boilerFactor * (fcBoilerHsfo + fcElectricalHsfo + fcOtherHsfo))));
        const correctedMgo = Math.max(0, fcMgo - (fcVoyageMgo + tfMgo + (boilerFactor * (fcBoilerMgo + fcElectricalMgo + fcOtherMgo))));
        return (correctedHsfo * CARBON_FACTORS.HSFO) + (correctedMgo * CARBON_FACTORS.MGO);
    };


    const getConsumptionBreakdownForDay = (reportsForDay: ReportDtoFull[]): ConsumptionRobBreakdown | null => {
        let hasFuelRows = false;
        const breakdown: ConsumptionRobBreakdown = {
            meHsfo: 0,
            aeHsfo: 0,
            boilerHsfo: 0,
            meMgo: 0,
            aeMgo: 0,
            boilerMgo: 0,
            hfoRob: 0,
            mgoRob: 0,
            fuels: {},
            correctionsByFuel: {},
            unmappedFuelTypes: [],
        };

        for (const dayReport of reportsForDay) {
            if (isBunkerReport(dayReport)) continue;
            const fuelTypes = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'fuel type')?.valueText
            );
            const machineries = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'machinery')?.valueText
            );
            const consumedRows = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'consumed')?.valueText
            );
            const operationTypeRows = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase().includes('operation type'))?.valueText
            );
            const outerRobs = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'last rob')?.valueText ||
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'rob')?.valueText
            );

            if (!fuelTypes.length) {
                continue;
            }

            hasFuelRows = true;

            fuelTypes.forEach((fuelTypeRaw, fuelIndex) => {
                const fuelCode = getConfiguredFuelCode(fuelTypeRaw);
                const fuelBucket = getFuelBucket(fuelTypeRaw);
                if (!fuelCode) {
                    const rawFuel = String(fuelTypeRaw || '').trim();
                    if (rawFuel && !breakdown.unmappedFuelTypes?.includes(rawFuel)) {
                        breakdown.unmappedFuelTypes?.push(rawFuel);
                    }
                    return;
                }

                const machineryRow = Array.isArray(machineries[fuelIndex]) ? machineries[fuelIndex] : [];
                const consumedRow = Array.isArray(consumedRows[fuelIndex]) ? consumedRows[fuelIndex] : [];
                const outerRobValue = parseFloat(String(outerRobs[fuelIndex] || '0')) || 0;
                const fuelDetail = getOrCreateFuelBreakdown(breakdown.fuels || {}, fuelCode);
                fuelDetail.rob = outerRobValue;

                if (fuelBucket === 'hsfo' && outerRobValue > 0) breakdown.hfoRob = outerRobValue;
                if (fuelBucket === 'mgo' && outerRobValue > 0) breakdown.mgoRob = outerRobValue;

                machineryRow.forEach((machineryRaw: unknown, machineryIndex: number) => {
                    const machineryBucket = getMachineryBucket(machineryRaw);
                    const consumedValue = parseFloat(String(consumedRow[machineryIndex] || '0')) || 0;
                    const operationType = getNestedArrayValue(operationTypeRows, fuelIndex, machineryIndex);

                    if (machineryBucket) {
                        if (machineryBucket === 'me') fuelDetail.me += consumedValue;
                        if (machineryBucket === 'ae') fuelDetail.ae += consumedValue;
                        if (machineryBucket === 'boiler') fuelDetail.boiler += consumedValue;
                        if (fuelBucket === 'hsfo' && machineryBucket === 'me') breakdown.meHsfo += consumedValue;
                        if (fuelBucket === 'hsfo' && machineryBucket === 'ae') breakdown.aeHsfo += consumedValue;
                        if (fuelBucket === 'hsfo' && machineryBucket === 'boiler') breakdown.boilerHsfo += consumedValue;
                        if (fuelBucket === 'mgo' && machineryBucket === 'me') breakdown.meMgo += consumedValue;
                        if (fuelBucket === 'mgo' && machineryBucket === 'ae') breakdown.aeMgo += consumedValue;
                        if (fuelBucket === 'mgo' && machineryBucket === 'boiler') breakdown.boilerMgo += consumedValue;
                    } else if (isStandalonePumpOtherCorrection(machineryRaw, operationType)) {
                        fuelDetail.other += consumedValue;
                    }

                    const correction = getOrCreateFuelCorrection(breakdown.correctionsByFuel || {}, fuelCode);
                    if (isBoilerCorrectionMachinery(machineryRaw) && isBoilerCorrectionOperation(operationType)) {
                        correction.fcBoiler += consumedValue;
                    }
                    if (isAeDischargePumpCorrection(machineryRaw, operationType)) {
                        correction.fcElectrical += consumedValue;
                    }
                    if (isStandalonePumpOtherCorrection(machineryRaw, operationType)) {
                        correction.fcOther += consumedValue;
                    }
                });
            });
        }

        return hasFuelRows ? breakdown : null;
    };

    const getConsumptionBreakdownFromApi = (apiData: any): ConsumptionRobBreakdown | null => {
        const rows = Array.isArray(apiData?.rows)
            ? apiData.rows
            : Array.isArray(apiData?.details)
                ? apiData.details
                : Array.isArray(apiData)
                    ? apiData
                    : [];

        if (!rows.length) return null;

        const breakdown: ConsumptionRobBreakdown = {
            meHsfo: 0,
            aeHsfo: 0,
            boilerHsfo: 0,
            meMgo: 0,
            aeMgo: 0,
            boilerMgo: 0,
            hfoRob: 0,
            mgoRob: 0,
            fuels: {},
            correctionsByFuel: {},
            unmappedFuelTypes: [],
        };

        rows.forEach((row: any) => {
            const fuelCode = getConfiguredFuelCode(row?.fieldType);
            const fuelBucket = getFuelBucket(row?.fieldType);
            if (!fuelCode) {
                const rawFuel = String(row?.fieldType || '').trim();
                if (rawFuel && !breakdown.unmappedFuelTypes?.includes(rawFuel)) {
                    breakdown.unmappedFuelTypes?.push(rawFuel);
                }
                return;
            }

            const outerRobValue = parseFloat(String(row?.lastRob ?? row?.rob ?? 0)) || 0;
            const fuelDetail = getOrCreateFuelBreakdown(breakdown.fuels || {}, fuelCode);
            fuelDetail.rob = outerRobValue;
            if (fuelBucket === 'hsfo' && outerRobValue > 0) breakdown.hfoRob = outerRobValue;
            if (fuelBucket === 'mgo' && outerRobValue > 0) breakdown.mgoRob = outerRobValue;

            const machineries = Array.isArray(row?.machineries) ? row.machineries : [];
            machineries.forEach((mach: any) => {
                const machineryBucket = getMachineryBucket(mach?.machinery);
                const consumedValue = parseFloat(String(mach?.consumed ?? 0)) || 0;
                const operationType = mach?.operationType;

                if (machineryBucket) {
                    if (machineryBucket === 'me') fuelDetail.me += consumedValue;
                    if (machineryBucket === 'ae') fuelDetail.ae += consumedValue;
                    if (machineryBucket === 'boiler') fuelDetail.boiler += consumedValue;
                    if (fuelBucket === 'hsfo' && machineryBucket === 'me') breakdown.meHsfo += consumedValue;
                    if (fuelBucket === 'hsfo' && machineryBucket === 'ae') breakdown.aeHsfo += consumedValue;
                    if (fuelBucket === 'hsfo' && machineryBucket === 'boiler') breakdown.boilerHsfo += consumedValue;
                    if (fuelBucket === 'mgo' && machineryBucket === 'me') breakdown.meMgo += consumedValue;
                    if (fuelBucket === 'mgo' && machineryBucket === 'ae') breakdown.aeMgo += consumedValue;
                    if (fuelBucket === 'mgo' && machineryBucket === 'boiler') breakdown.boilerMgo += consumedValue;
                } else if (isStandalonePumpOtherCorrection(mach?.machinery, operationType)) {
                    fuelDetail.other += consumedValue;
                }

                const correction = getOrCreateFuelCorrection(breakdown.correctionsByFuel || {}, fuelCode);
                if (isBoilerCorrectionMachinery(mach?.machinery) && isBoilerCorrectionOperation(operationType)) {
                    correction.fcBoiler += consumedValue;
                }
                if (isAeDischargePumpCorrection(mach?.machinery, operationType)) {
                    correction.fcElectrical += consumedValue;
                }
                if (isStandalonePumpOtherCorrection(mach?.machinery, operationType)) {
                    correction.fcOther += consumedValue;
                }
            });
        });

        return breakdown;
    };

    // Average sulphur values are stored directly in CII voyage rows.

    const extractBunkerTabRows = (reports: ReportDtoFull[]): BunkerTabRow[] => {
        return reports
            .filter(report => isBunkerReport(report))
            .flatMap((report) => {
                const findText = (label: string) =>
                    report.values.find(v => v.field.label.trim().toLowerCase() === label.trim().toLowerCase())?.valueText || '';

                const receivedRows = parseBunkerRows(findText('Bunker Received Rows'));
                if (receivedRows.length > 0) {
                    const perRowEntries = receivedRows.map((row: any, index: number) => {
                        const fuelTypeCode = getConfiguredFuelCode(row?.fuelType);
                        const quantity = parseFloat(String(row?.quantity ?? '')) || 0;
                        const fallbackHfo = parseFloat(String(row?.hfo ?? '')) || 0;
                        const fallbackMgo = parseFloat(String(row?.mgo ?? '')) || 0;
                        const bunkerReceivedHfo =
                            fuelTypeCode === 'HSFO'
                                ? quantity
                                : fallbackHfo;
                        const bunkerReceivedMgo =
                            fuelTypeCode === 'MGO'
                                ? quantity
                                : fallbackMgo;
                        const fuelReceived = fuelTypeCode && quantity > 0
                            ? { [fuelTypeCode]: quantity }
                            : {};
                        const dateValue =
                            toIsoDateOnly(row?.dateUtc) ||
                            toIsoDateOnly(findText('Date (UTC)')) ||
                            toDateOnly(report.submittedDateTime || report.createdDateTime);

                        return {
                            date: dateValue,
                            reportName: report.reportType || `Bunker Report ${index + 1}`,
                            hfoRob: bunkerReceivedHfo,
                            mgoRob: bunkerReceivedMgo,
                            bunkerReceivedHfo,
                            bunkerReceivedMgo,
                            fuelReceived,
                        };
                    });

                    const meaningfulEntries = perRowEntries.filter((row) =>
                        Boolean(row.date) ||
                        row.hfoRob > 0 ||
                        row.mgoRob > 0 ||
                        row.bunkerReceivedHfo > 0 ||
                        row.bunkerReceivedMgo > 0
                    );

                    if (meaningfulEntries.length > 0) {
                        return meaningfulEntries;
                    }
                }

                const dateValue =
                    toIsoDateOnly(findText('Date (UTC)')) ||
                    toDateOnly(report.submittedDateTime || report.createdDateTime);
                const bunkerReceivedHfo = parseFloat(findText("HFO (MT's)")) || 0;
                const bunkerReceivedMgo = parseFloat(findText("MGO (MT's)")) || 0;

                return [{
                    date: dateValue,
                    reportName: report.reportType || 'Bunker Report',
                    hfoRob: bunkerReceivedHfo,
                    mgoRob: bunkerReceivedMgo,
                    bunkerReceivedHfo,
                    bunkerReceivedMgo,
                    fuelReceived: {
                        ...(bunkerReceivedHfo > 0 ? { HSFO: bunkerReceivedHfo } : {}),
                        ...(bunkerReceivedMgo > 0 ? { MGO: bunkerReceivedMgo } : {}),
                    },
                }];
            })
            .filter(row =>
                Boolean(row.date) ||
                row.hfoRob > 0 ||
                row.mgoRob > 0 ||
                row.bunkerReceivedHfo > 0 ||
                row.bunkerReceivedMgo > 0
            )
            .sort((a, b) => (toIsoDateOnly(a.date) || a.date).localeCompare(toIsoDateOnly(b.date) || b.date));
    };

    const buildSummaryMetrics = (fromDate: string, toDate: string): SummaryMetrics => {
        const voyageRows = aggregatedData.filter(row => row.fromDate >= fromDate && row.fromDate <= toDate);
        const bunkerRows = bunkerTabRows.filter(row => row.date >= fromDate && row.date <= toDate);
        const cargoRows = cargoOps.filter((op: any) => {
            const date = toDateOnly(op.createdDateTime);
            return date >= fromDate && date <= toDate;
        });

        const totalFuel = voyageRows.reduce((acc, row) => acc + row.totalFuel, 0);
        const totalCo2 = voyageRows.reduce((acc, row) => acc + computeCorrectedCo2ForRow(row), 0);
        const totalDistance = voyageRows.reduce((acc, row) => acc + row.distance, 0);
        const totalCargo = cargoRows.reduce((acc: number, row: any) => acc + (Number(row.totalCargoQtyMt) || 0), 0);
        const totalBunker = bunkerRows.reduce((acc, row) => acc + row.bunkerReceivedHfo + row.bunkerReceivedMgo, 0);
        const { capacity: configuredCapacity } = getCiiRuleMetrics();
        const capacity = configuredCapacity || Number(generalInfo?.summerDeadweight) || 50000;

        const transportWork = voyageRows.reduce((acc, row) => {
            const isLoaded = (row.vesselCondition || '').trim().toUpperCase() === 'L';
            if (!isLoaded) return acc;
            return acc + (row.distance * row.cargoCarried);
        }, 0);
        const correctedDistance = getCorrectedDistanceForRows(voyageRows);
        const attainedCii = calculateAnnualAttainedCii(totalCo2, capacity, correctedDistance, getCorrectionFactorProduct());

        return {
            totalFuel,
            totalCo2,
            totalDistance,
            totalCargo,
            totalBunker,
            transportWork,
            capacity,
            attainedCii,
        };
    };

    const getCiiRatingFromAttained = (attainedCii: number, referenceCii: number) => {
        const { rule, requiredCii } = getCiiRuleMetrics();
        return getCiiRatingFromRequired(attainedCii, requiredCii || referenceCii, rule);
    };

    const buildQuarterlyMetrics = (fromDate: string, toDate: string): QuarterlyMetrics => {
        const summary = buildSummaryMetrics(fromDate, toDate);
        const voyageRows = aggregatedData.filter(row => row.fromDate >= fromDate && row.fromDate <= toDate);

        const loadedRows = voyageRows.filter(row => (row.vesselCondition || '').trim().toUpperCase() === 'L');
        const ballastRows = voyageRows.filter(row => (row.vesselCondition || '').trim().toUpperCase() === 'B');

        const loadedDistance = loadedRows.reduce((acc, row) => acc + row.distance, 0);
        const ballastDistance = ballastRows.reduce((acc, row) => acc + row.distance, 0);

        const fuelConsumedHsfo = voyageRows.reduce((acc, row) => acc + row.meHsfo + row.aeHsfo + row.boilerHsfo, 0);
        const fuelConsumedMgo = voyageRows.reduce((acc, row) => acc + row.meMgo + row.aeMgo + row.boilerMgo, 0);
        const totalDaysSea = voyageRows.reduce((acc, row) => acc + (row.hoursAtSea / 24), 0);
        const totalDaysAnchor = voyageRows.reduce((acc, row) => acc + (row.hoursAtAnchor / 24), 0);
        const cargoCarried = voyageRows.reduce((acc, row) => acc + row.cargoCarried, 0);

        const emissionsTotals = voyageRows.reduce(
            (acc, row) => {
                const { sox, pm, nox, co } = computeRowEmissions(row);
                acc.sox += sox;
                acc.pm += pm;
                acc.nox += nox;
                acc.co += co;
                return acc;
            },
            { sox: 0, pm: 0, nox: 0, co: 0 }
        );
        const eeoi = summary.transportWork > 0 ? (summary.totalCo2 * 1000000) / summary.transportWork : 0;
        const { referenceCii: configuredReferenceCii, requiredCii } = getCiiRuleMetrics();
        const referenceCii = configuredReferenceCii || Number((propulsion as any)?.referenceCii) || Number((propulsion as any)?.requiredCii) || DEFAULT_REFERENCE_CII;
        const quarterlyRating = getCiiRatingFromAttained(summary.attainedCii, requiredCii || referenceCii);

        return {
            ...summary,
            loadedDistance,
            ballastDistance,
            fuelConsumedHsfo,
            fuelConsumedMgo,
            totalDaysSea,
            totalDaysAnchor,
            cargoCarried,
            sox: emissionsTotals.sox,
            pm: emissionsTotals.pm,
            nox: emissionsTotals.nox,
            co: emissionsTotals.co,
            eeoi,
            referenceCii,
            quarterlyRating,
        };
    };

    const buildAnnualOverviewMetrics = (fromDate: string, toDate: string): AnnualOverviewMetrics => {
        const summary = buildSummaryMetrics(fromDate, toDate);
        const voyageRows = aggregatedData.filter(row => row.fromDate >= fromDate && row.fromDate <= toDate);
        const { referenceCii: configuredReferenceCii, requiredCii } = getCiiRuleMetrics();
        const referenceCii = configuredReferenceCii || Number((propulsion as any)?.referenceCii) || Number((propulsion as any)?.requiredCii) || DEFAULT_REFERENCE_CII;

        const totals = voyageRows.reduce(
            (acc, row) => {
                const fcHsfo = row.meHsfo + row.aeHsfo + row.boilerHsfo;
                const fcMgo = row.meMgo + row.aeMgo + row.boilerMgo;
                const stsVoyageIsY = resolveStsVoyageValue(row.stsVoyage) === 'Y';
                const fcOtherDisabled = isSailingEventType(row.eventType) || isAnchorEventType(row.eventType);

                const { sox, pm, nox, co } = computeRowEmissions(row);

                acc.totalFcHsfo += fcHsfo;
                acc.totalFcMgo += fcMgo;
                acc.totalFcVoyageHsfo += stsVoyageIsY ? 0 : numericValue(row.fcVoyageHsfo);
                acc.totalFcVoyageMgo += stsVoyageIsY ? 0 : numericValue(row.fcVoyageMgo);
                acc.totalFcBoilerHsfo += stsVoyageIsY ? 0 : getCorrectionValueForFuel(row, 'HSFO', 'fcBoiler');
                acc.totalFcBoilerMgo += stsVoyageIsY ? 0 : getCorrectionValueForFuel(row, 'MGO', 'fcBoiler');
                acc.totalFcOtherHsfo += fcOtherDisabled || stsVoyageIsY ? 0 : getCorrectionValueForFuel(row, 'HSFO', 'fcOther') + getCorrectionValueForFuel(row, 'HSFO', 'fcElectrical');
                acc.totalFcOtherMgo += fcOtherDisabled || stsVoyageIsY ? 0 : getCorrectionValueForFuel(row, 'MGO', 'fcOther') + getCorrectionValueForFuel(row, 'MGO', 'fcElectrical');

                acc.totalDaysSea += row.hoursAtSea / 24;
                acc.totalDaysPort += row.hoursAtAnchor / 24;
                acc.totalSox += sox;
                acc.totalPm += pm;
                acc.totalNox += nox;
                acc.totalCo += co;
                return acc;
            },
            {
                totalFcHsfo: 0,
                totalFcMgo: 0,
                totalFcVoyageHsfo: 0,
                totalFcVoyageMgo: 0,
                totalFcBoilerHsfo: 0,
                totalFcBoilerMgo: 0,
                totalFcOtherHsfo: 0,
                totalFcOtherMgo: 0,
                totalDaysSea: 0,
                totalDaysPort: 0,
                totalSox: 0,
                totalPm: 0,
                totalNox: 0,
                totalCo: 0,
            }
        );

        const totalFuel = totals.totalFcHsfo + totals.totalFcMgo;
        const fuelPerDistance = summary.totalDistance > 0 ? (totalFuel * 1000) / summary.totalDistance : 0;
        const fuelPerTransportWork = summary.transportWork > 0 ? (totalFuel * 1000000) / summary.transportWork : 0;
        const co2PerDistance = summary.totalDistance > 0 ? (summary.totalCo2 * 1000) / summary.totalDistance : 0;
        const eeoi = summary.transportWork > 0 ? (summary.totalCo2 * 1000000) / summary.transportWork : 0;
        const ciiRating = getCiiRatingFromAttained(summary.attainedCii, requiredCii || referenceCii);

        return {
            totalFcHsfo: totals.totalFcHsfo,
            totalFcMgo: totals.totalFcMgo,
            totalFcVoyageHsfo: totals.totalFcVoyageHsfo,
            totalFcVoyageMgo: totals.totalFcVoyageMgo,
            totalFcBoilerHsfo: totals.totalFcBoilerHsfo,
            totalFcBoilerMgo: totals.totalFcBoilerMgo,
            totalFcOtherHsfo: totals.totalFcOtherHsfo,
            totalFcOtherMgo: totals.totalFcOtherMgo,
            totalCo2: summary.totalCo2,
            sox: totals.totalSox,
            nox: totals.totalNox,
            co: totals.totalCo,
            pm: totals.totalPm,
            totalDistance: summary.totalDistance,
            totalDaysSea: totals.totalDaysSea,
            totalDaysPort: totals.totalDaysPort,
            transportWork: summary.transportWork,
            fuelPerDistance,
            fuelPerTransportWork,
            co2PerDistance,
            eeoi,
            attainedCii: summary.attainedCii,
            ciiRating,
        };
    };

    const processReportData = async (
        reports: ReportDtoFull[],
        bunkerReports: ReportDtoFull[],
        cargoData: any[],
        dprRecs: any[],
        voyages: any[] = [],
        ciiRowsByReportId: Record<number, CiiVoyageRowDto> = {}
    ) => {
        let activeVoyageNo = '';
        let currentEventState: VoyageEventState = 'SAILING';
        let pendingDepartureResolution = false;
        let lastReportDateTime = '';
        const { prevByVoyageNo } = buildVoyageSequence(voyages || []);
        const combinedBunkerReports = Array.from(
            new Map(
                [...bunkerReports, ...reports]
                    .filter((report) => isBunkerReport(report))
                    .map((report) => [String(report.id ?? `${report.reportType}-${report.createdDateTime}`), report])
            ).values()
        );
        const sourceReports = [...reports]
            .filter(r => !isBunkerReport(r) && isPositionReportCandidate(r))
            .sort((a, b) => {
                const aTime = toDateTimeMs(getReportSortDateTime(a));
                const bTime = toDateTimeMs(getReportSortDateTime(b));
                if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
                return (getReportSortDateTime(a) || '').localeCompare(getReportSortDateTime(b) || '');
            });

        const processed: AggregatedVoyageData[] = [];

          for (const voyageSourceReport of sourceReports) {
              const reportDateTime =
                  getDateTimeFieldValueFromReport(voyageSourceReport, ['report date time']) ||
                  getDateTimeFieldValueFromReport(voyageSourceReport) ||
                  getReportSortDateTime(voyageSourceReport);
              const reportDate = getReportDateOnly(voyageSourceReport) || toDateOnly(reportDateTime);
              const fullReportsForDay = combinedBunkerReports.filter(r => getReportDateOnly(r) === reportDate);
            const dayReports = fullReportsForDay.length
                ? Array.from(
                    new Map(
                        [voyageSourceReport, ...fullReportsForDay]
                            .filter(Boolean)
                            .map((report) => [String(report.id ?? `${report.reportType}-${report.createdDateTime}`), report])
                    ).values()
                )
                : [voyageSourceReport];
            const dprDay = dprRecs.find((d: any) => d.reportDate === reportDate);
              const voyageNumberFromReport = findFieldText(voyageSourceReport.values || [], ['voyage no', 'voyage number']);
              const explicitVoyageNo = voyageNumberFromReport || dprDay?.voyageNo || '';
            const voyageByNumber = explicitVoyageNo
                ? (voyages || []).find((v: any) =>
                    String(v?.voyageNumber || '').trim().toUpperCase() === String(explicitVoyageNo).trim().toUpperCase()
                )
                : null;
            const matchingVoyage = voyageByNumber || findMatchingVoyageByDate(reportDateTime || reportDate, voyages || []);

            const consumptionApiData = voyageSourceReport?.id && matchingVoyage?.id
                ? await getConsumptionByVoyageReport(matchingVoyage.id, voyageSourceReport.id).catch(() => null)
                : null;
              const consumptionBreakdown =
                  getConsumptionBreakdownFromApi(consumptionApiData) ||
                  getConsumptionBreakdownForDay(dayReports);
              const persistedCiiRow =
                  voyageSourceReport?.id != null
                      ? ciiRowsByReportId[Number(voyageSourceReport.id)]
                      : undefined;
              const persistedStsVoyage = persistedCiiRow?.stsVoyage;
              const persistedBoilerUsed = persistedCiiRow?.boilerUsed;
              const persistedFcVoyageHsfo = parseOptionalNumber(persistedCiiRow?.fcVoyageHsfo);
              const persistedFcVoyageMgo = parseOptionalNumber(persistedCiiRow?.fcVoyageMgo);
                const persistedHsfoSulphur = parseOptionalNumber(persistedCiiRow?.hsfoSulphur);
                const persistedMgoSulphur = parseOptionalNumber(persistedCiiRow?.mgoSulphur);
              const persistedHsfoFuelDetail = getPersistedFuelDetail(persistedCiiRow, 'HSFO');
              const persistedMgoFuelDetail = getPersistedFuelDetail(persistedCiiRow, 'MGO');
              const persistedDistanceAdjustment = parseOptionalNumber(persistedCiiRow?.distanceAdjustment);
              const correctionData = buildCorrectionBreakdownForRow(consumptionBreakdown?.correctionsByFuel, persistedCiiRow);
              const persistedRemarks = persistedCiiRow?.remarks || '';

            const findValue = (labels: string[]) => {
                const match = voyageSourceReport.values.find(v => labels.some(l => v.field.label.toLowerCase().includes(l.toLowerCase())));
                return match ? parseFloat(match.valueText) || 0 : 0;
            };

            const meHsfo = consumptionBreakdown?.meHsfo ?? findValue(['ME HSFO', 'ME HFO', 'ME LSFO', 'ME FO']);
            const aeHsfo = consumptionBreakdown?.aeHsfo ?? findValue(['AE HSFO', 'AE HFO', 'AE LSFO', 'AE FO']);
            const boilerHsfo = consumptionBreakdown?.boilerHsfo ?? findValue(['Boiler HSFO', 'Boiler HFO', 'Boiler FO']);
            const meMgo = consumptionBreakdown?.meMgo ?? findValue(['ME MGO', 'ME MDO', 'ME Diesel']);
            const aeMgo = consumptionBreakdown?.aeMgo ?? findValue(['AE MGO', 'AE MDO', 'AE Diesel']);
            const boilerMgo = consumptionBreakdown?.boilerMgo ?? findValue(['Boiler MGO', 'Boiler MDO', 'Boiler Diesel']);

            const totalHsfo = meHsfo + aeHsfo + boilerHsfo;
            const totalMgo = meMgo + aeMgo + boilerMgo;
            const fuelBreakdown = consumptionBreakdown?.fuels;
            const dynamicTotalFuel = fuelBreakdown
                ? Object.values(fuelBreakdown).reduce((acc, fuel) => acc + getFuelTotal(fuel), 0)
                : 0;
            const dynamicTotalCo2 = computeCo2FromFuelBreakdown(fuelBreakdown);
            const totalFuel = dynamicTotalFuel || totalHsfo + totalMgo || (dprDay?.foConsumptionRows?.length > 0 ? parseFloat(dprDay.foConsumptionRows[0].totalFoCons) || 0 : 0);
            const totalCo2 = dynamicTotalCo2 || (totalHsfo * CARBON_FACTORS.HSFO) + (totalMgo * CARBON_FACTORS.MGO) || (totalFuel * CARBON_FACTORS.HSFO);

            let bunkerReceivedHfo = 0;
            let bunkerReceivedMgo = 0;
            let hfoRob = consumptionBreakdown?.hfoRob || 0;
            let mgoRob = consumptionBreakdown?.mgoRob || 0;
            let bunkerReportName = '';

            const bunkerReport = fullReportsForDay.find(r => isBunkerReport(r));
            if (bunkerReport) {
                bunkerReportName = bunkerReport.reportType || 'Bunker Report';
                const bunkerRowsField = bunkerReport.values.find(v => v.field.label === 'Bunker Received Rows');
                // Do not override ROB from bunker report fields; ROB should come from the current report's Consumption/ROB.

                if (bunkerRowsField?.valueText) {
                    try {
                        const rows = JSON.parse(bunkerRowsField.valueText);
                        if (Array.isArray(rows)) {
                              rows.forEach((row: any) => {
                                  const genericSulphurValue = parseFloat(String(row?.sulfurPercentage ?? ''));
                                  bunkerReceivedHfo += parseFloat(row.hfo) || 0;
                                  bunkerReceivedMgo += parseFloat(row.mgo) || 0;
                                  if (!isNaN(genericSulphurValue)) {
                                      // sulphur content is now provided directly in CII voyage rows
                                  }
                              });
                          }
                    } catch (e) {
                        console.error('Failed to parse bunker received rows', e);
                    }
                }
            }

            if (bunkerReceivedHfo === 0 && bunkerReceivedMgo === 0) {
                bunkerReceivedHfo = dprDay?.robStatus?.filter((r: any) => {
                    const c = r.commodity?.toLowerCase() || '';
                    return c.includes('fuel') || c.includes('hsfo') || c.includes('hfo') || c.includes('lsfo');
                }).reduce((acc: number, r: any) => acc + (parseFloat(r.received) || 0), 0) || 0;

                bunkerReceivedMgo = dprDay?.robStatus?.filter((r: any) => {
                    const c = r.commodity?.toLowerCase() || '';
                    return c.includes('mgo') || c.includes('diesel') || c.includes('mdo');
                }).reduce((acc: number, r: any) => acc + (parseFloat(r.received) || 0), 0) || 0;
            }

            if (hfoRob === 0 && mgoRob === 0 && dprDay?.robStatus) {
                hfoRob = dprDay.robStatus.filter((r: any) => {
                    const c = r.commodity?.toLowerCase() || '';
                    return c.includes('fuel') || c.includes('hsfo') || c.includes('hfo');
                }).reduce((acc: number, r: any) => acc + (parseFloat(r.rob) || 0), 0) || 0;
                mgoRob = dprDay.robStatus.filter((r: any) => {
                    const c = r.commodity?.toLowerCase() || '';
                    return c.includes('mgo') || c.includes('diesel') || c.includes('mdo');
                }).reduce((acc: number, r: any) => acc + (parseFloat(r.rob) || 0), 0) || 0;
            }

            const cargoWeight = getCargoWeightFromReport(voyageSourceReport);
            const vesselCondition = getVesselConditionFromReport(voyageSourceReport, matchingVoyage?.loadingCondition);
            const rawEventType = getEventTypeFromReport(voyageSourceReport);
            const reportKind = getReportKindFromReport(voyageSourceReport);
            const resolvedState = resolveRowAndNextEventState(
                reportKind,
                currentEventState,
                voyageSourceReport,
                rawEventType
            );
            const primaryReportDateTime =
                buildReportUtcDateTime(voyageSourceReport) ||
                reportDateTime ||
                reportDate ||
                '';
            const departureBoundaryDateTime = primaryReportDateTime;
            const arrivalBoundaryDateTime = primaryReportDateTime;
            let rowState: VoyageEventState =
                reportKind === 'COSP'
                    ? getDeparturePrevStateFromEventFlags(voyageSourceReport, currentEventState)
                    : resolvedState.rowState;
            let nextState: VoyageEventState =
                reportKind === 'COSP'
                    ? 'SAILING'
                    : resolvedState.nextState;

            if (pendingDepartureResolution && reportKind !== 'DEPARTURE') {
                if (reportKind === 'ARRIVAL') {
                    const arrivalState = getArrivalNextStateFromEventFlags(
                        voyageSourceReport,
                        rawEventType,
                        currentEventState
                    );
                    rowState = arrivalState;
                    nextState = arrivalState;
                } else {
                    rowState = 'SAILING';
                    nextState = 'SAILING';
                }
                pendingDepartureResolution = false;
            }

            if (reportKind === 'DEPARTURE') {
                rowState = currentEventState;
                nextState = 'SAILING';
                pendingDepartureResolution = false;
            }

                const departurePortLocation =
                    getPortValueFromReport(voyageSourceReport.values || [], ['departure port', 'from location', 'port name', 'location']) ||
                    dprDay?.fromLocation ||
                    dprDay?.dlLocation ||
                    matchingVoyage?.departurePort ||
                    '';
                const arrivalPortLocation =
                    getPortValueFromReport(voyageSourceReport.values || [], ['arrival port', 'to location', 'port name', 'location']) ||
                    dprDay?.toLocation ||
                    matchingVoyage?.arrivalPort ||
                    departurePortLocation;

            const portNameLocation =
                rowState === 'SAILING'
                    ? departurePortLocation
                    : rowState === 'AT_BERTH' || rowState === 'AT_ANCHOR'
                        ? arrivalPortLocation
                        : departurePortLocation;

            const foundVoyageNo =
                voyageNumberFromReport ||
                dprDay?.voyageNo ||
                activeVoyageNo ||
                matchingVoyage?.voyageNumber ||
                '';
            const reportVoyageNo = foundVoyageNo || activeVoyageNo || '';
            const previousVoyageFallback =
                activeVoyageNo && activeVoyageNo !== reportVoyageNo
                    ? activeVoyageNo
                    : '';
            const previousVoyageNo = reportVoyageNo
                ? (prevByVoyageNo.get(reportVoyageNo) || previousVoyageFallback || '')
                : '';
            const rowVoyageNo =
                reportKind === 'COSP'
                    ? (previousVoyageNo || '')
                    : (reportVoyageNo || '');

            if (reportKind === 'COSP') {
                if (reportVoyageNo) {
                    activeVoyageNo = reportVoyageNo;
                }
            } else if (reportVoyageNo) {
                activeVoyageNo = reportVoyageNo;
            }

            const isBoundaryReport = reportKind === 'COSP' || reportKind === 'ARRIVAL' || reportKind === 'DEPARTURE';
            const metricsAllowed = !(reportKind === 'COSP' && !previousVoyageNo);
            if (metricsAllowed && rowVoyageNo) {
                const rowToDateTime = primaryReportDateTime || reportDateTime || reportDate;
                let metricsDateTime = lastReportDateTime || rowToDateTime;
                let metricsToDateTime = rowToDateTime;

                if (reportKind === 'ARRIVAL' && arrivalBoundaryDateTime) {
                    metricsToDateTime = arrivalBoundaryDateTime;
                } else if ((reportKind === 'DEPARTURE' || reportKind === 'COSP' || reportKind === 'EOSP') && departureBoundaryDateTime) {
                    metricsToDateTime = departureBoundaryDateTime;
                }

                const computedHours = getHoursByEventState(rowState, metricsDateTime, metricsToDateTime);
                    processed.push({
                        fromDate: metricsDateTime || '',
                        toDate: metricsToDateTime || '',
                        sourceDateTime: metricsDateTime || '',
                        distance: getDistanceTravelledFromNoonReport(voyageSourceReport) ?? getDistanceTravelledForDay(dayReports),
                        hoursAtSea: computedHours.hoursAtSea,
                        hoursAtAnchor: computedHours.hoursAtAnchor,
                        cargoCarried: isBoundaryReport ? 0 : (cargoWeight ?? 0),
                        cargoWeightProvided: !isBoundaryReport && cargoWeight !== null,
                        meHsfo,
                        aeHsfo,
                        boilerHsfo,
                        meMgo,
                        aeMgo,
                        boilerMgo,
                        totalFuel,
                        totalCo2,
                        bunkerReceivedHfo,
                        bunkerReceivedMgo,
                        fuelBreakdown,
                        correctionBreakdown: correctionData.corrections,
                        correctionSourceByFuel: correctionData.sources,
                        unmappedFuelTypes: consumptionBreakdown?.unmappedFuelTypes || [],
                        reportName: voyageSourceReport.reportType || '--',
                        bunkerReportName: bunkerReportName || '--',
                        hfoRob,
                        mgoRob,
                        cargoHandled: 0,
                        fcVoyageHsfo:
                            persistedFcVoyageHsfo ??
                            (voyageSourceReport ? (parseFloat(findFieldTextExact(voyageSourceReport.values || [], ['FC voyage HSFO'])) || undefined) : undefined),
                        fcVoyageMgo:
                            persistedFcVoyageMgo ??
                            (voyageSourceReport ? (parseFloat(findFieldTextExact(voyageSourceReport.values || [], ['FC voyage MGO'])) || undefined) : undefined),
                        fcElectricalHsfo: correctionData.corrections.HSFO?.fcElectrical,
                        fcElectricalMgo: correctionData.corrections.MGO?.fcElectrical,
                        fcBoilerHsfo: correctionData.corrections.HSFO?.fcBoiler,
                        fcBoilerMgo: correctionData.corrections.MGO?.fcBoiler,
                        fcOtherHsfo: correctionData.corrections.HSFO?.fcOther,
                        fcOtherMgo: correctionData.corrections.MGO?.fcOther,
                        tfHsfo: parseOptionalNumber(persistedHsfoFuelDetail?.fcVoyageAdjustment),
                        tfMgo: parseOptionalNumber(persistedMgoFuelDetail?.fcVoyageAdjustment),
                        distanceAdjustment: persistedDistanceAdjustment,
                        reeferKwh: parseOptionalNumber(persistedCiiRow?.reeferKwh),
                        reeferSfoc: parseOptionalNumber(persistedCiiRow?.reeferSfoc),
                        reeferFuelCode: persistedCiiRow?.reeferFuelCode || '',
                        reeferContainersOnboard: hasFieldWithExactLabel(voyageSourceReport?.values || [], ['Refrigerated Containers Onboard'])
                            ? getCheckboxFieldState(voyageSourceReport, ['Refrigerated Containers Onboard'])
                            : true,
                        cargoCoolingSystemInUse: hasFieldWithExactLabel(voyageSourceReport?.values || [], ['Cargo Cooling System In Use'])
                            ? getCheckboxFieldState(voyageSourceReport, ['Cargo Cooling System In Use'])
                            : false,
                        vesselCondition,
                        portNameLocation,
                        eventType: toVoyageEventLabel(rowState),
                        reportKind,
                        reportDateTime: reportDateTime || reportDate,
                        departureDateTime: departureBoundaryDateTime || '',
                        arrivalDateTime: arrivalBoundaryDateTime || '',
                        departurePortLocation,
                        arrivalPortLocation,
                        anchorArrival: getArrivalNextStateFromEventFlags(voyageSourceReport, rawEventType, currentEventState) === 'AT_ANCHOR',
                        departureFromState: getDeparturePrevStateFromEventFlags(voyageSourceReport, currentEventState),
                        vesselId: matchingVoyage?.vessel?.id,
                        stsVoyage: persistedStsVoyage || findFieldText(voyageSourceReport.values || [], ['sts voyage']) || '',
                        boilerUsed: persistedBoilerUsed || '',
                        remarks: persistedRemarks,
                        voyageNo: rowVoyageNo,
                        leg: resolveLegDisplay(voyageSourceReport, matchingVoyage, persistedCiiRow),
                        avgSulphurHsfo: persistedHsfoSulphur,
                        avgSulphurMgo: persistedMgoSulphur,
                        reportId: voyageSourceReport.id,
                    reportAssignmentId: voyageSourceReport.assignment?.id,
                    reportVoyageId: voyageSourceReport.voyage,
                    reportTypeName: voyageSourceReport.reportType,
                    reportStatus: voyageSourceReport.status,
                    reportHasWarnings: voyageSourceReport.hasWarnings,
                    reportNoOfWarnings: voyageSourceReport.noOfWarnings,
                });
            }

            const shouldCreateNextEventRow =
                (reportKind === 'COSP' || reportKind === 'ARRIVAL' || reportKind === 'DEPARTURE') &&
                rowState !== nextState;

            if (shouldCreateNextEventRow) {
                const nextVoyageNo = reportVoyageNo || '';
                if (nextVoyageNo) {
                    const boundaryState = nextState;
                    const boundaryDateTime =
                        (boundaryState === 'SAILING' ? departureBoundaryDateTime : arrivalBoundaryDateTime) ||
                        primaryReportDateTime ||
                        reportDateTime ||
                        reportDate ||
                        '';
                    const boundarySourceTime = boundaryDateTime || primaryReportDateTime || reportDateTime || reportDate;

                    processed.push({
                        fromDate: boundaryDateTime,
                        toDate: '',
                        sourceDateTime: boundarySourceTime || '',
                        distance: 0,
                        hoursAtSea: 0,
                        hoursAtAnchor: 0,
                        cargoCarried: cargoWeight ?? 0,
                        cargoWeightProvided: cargoWeight !== null,
                        meHsfo: 0,
                        aeHsfo: 0,
                        boilerHsfo: 0,
                        meMgo: 0,
                        aeMgo: 0,
                        boilerMgo: 0,
                        totalFuel: 0,
                        totalCo2: 0,
                        bunkerReceivedHfo: 0,
                        bunkerReceivedMgo: 0,
                        reportName: voyageSourceReport.reportType || '--',
                        bunkerReportName: '--',
                        hfoRob: 0,
                        mgoRob: 0,
                        cargoHandled: 0,
                        vesselCondition: getVesselConditionFromReport(voyageSourceReport, matchingVoyage?.loadingCondition),
                        portNameLocation:
                            boundaryState === 'SAILING'
                                ? departurePortLocation
                                : arrivalPortLocation,
                        eventType: toVoyageEventLabel(boundaryState),
                        reportKind,
                        stsVoyage: '',
                        boilerUsed: '',
                        voyageNo: nextVoyageNo,
                        leg: resolveLegDisplay(voyageSourceReport, matchingVoyage, persistedCiiRow),
                        avgSulphurHsfo: undefined,
                        avgSulphurMgo: undefined,
                        isBoundaryRow: true,
                    } as AggregatedVoyageData);
                }
            }

            if (primaryReportDateTime) {
                lastReportDateTime = primaryReportDateTime;
            } else if (reportDateTime) {
                lastReportDateTime = reportDateTime;
            }

            currentEventState = nextState;
        }

        // Average sulphur content is provided directly in CII voyage rows.

        processed.forEach((row) => {
            row.cargoHandled = 0;
        });

        (cargoData || []).forEach((cargo: any) => {
            const cargoDateTimeRaw = cargo?.createdDateTime || cargo?.date || '';
            const cargoTime = new Date(cargoDateTimeRaw).getTime();
            if (Number.isNaN(cargoTime)) return;

            const cargoQty = Number(cargo?.totalCargoQtyMt) || 0;
            if (cargoQty <= 0) return;

            const matchingRow = processed.find((row, index) => {
                const fromTime = new Date(row.fromDate).getTime();
                if (Number.isNaN(fromTime)) return false;

                const toCandidate = row.toDate || processed[index + 1]?.fromDate || '';
                const toTime = toCandidate ? new Date(toCandidate).getTime() : NaN;

                if (!Number.isNaN(toTime)) {
                    return cargoTime >= fromTime && cargoTime <= toTime;
                }

                return toDateOnly(cargoDateTimeRaw) === toDateOnly(row.fromDate);
            });

            if (matchingRow) {
                matchingRow.cargoHandled += cargoQty;
            }
        });

        // ROB should reflect the current report's last ROB at the row's TO time.
        // Do not recompute rolling ROB from previous rows.

        const mergedRows = mergeVoyageRowsByEvent(processed);
        const splitRows = splitRowsOnQuarterBoundaries(mergedRows, reports, selectedYear);
        const voyageStartByNo = new Map<string, string>();
        (voyages || []).forEach((v: any) => {
            const voyageNo = String(v?.voyageNumber || '').trim();
            if (!voyageNo) return;
            if (!voyageStartByNo.has(voyageNo)) {
                voyageStartByNo.set(voyageNo, String(v?.startDate || ''));
            }
        });

        const orderedRows = [...splitRows].sort((a, b) => {
            const aKey = voyageStartByNo.get(a.voyageNo || '') || '';
            const bKey = voyageStartByNo.get(b.voyageNo || '') || '';
            if (aKey !== bKey) {
                return aKey.localeCompare(bKey);
            }
            const aTime = toDateTimeMs(a.fromDate);
            const bTime = toDateTimeMs(b.fromDate);
            if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
                return aTime - bTime;
            }
            return (a.fromDate || '').localeCompare(b.fromDate || '');
        });

        setAggregatedData(orderedRows);
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '--';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return dateStr;
        }
    };

    const formatDateTime = (dateStr: string) => {
        if (!dateStr) return '--';
        try {
            const date = new Date(toDateTimeMs(dateStr));
            if (isNaN(date.getTime())) return dateStr;

            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'UTC'
            }).replace(',', '');
        } catch (e) {
            return dateStr;
        }
    };

    const renderTable = () => {
        switch (activeTab) {
            case 'shipData': {
                const selectedVesselObj = vessels.find(v => v.id === selectedVesselId);
                const { rule, capacity, reduction, referenceCii, requiredCii, boundaries } = getCiiRuleMetrics();
                const annualMetrics = buildAnnualOverviewMetrics(`${selectedYear}-01-01`, `${selectedYear}-12-31`);
                const calculatedRating = getCiiRatingFromRequired(annualMetrics.attainedCii, requiredCii, rule);
                return (
                    <div className="table-responsive mt-4">
                        <div className="d-flex justify-content-end mb-3 gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={saveShipDataSettings}
                                disabled={!selectedVesselId || !selectedCiiShipType || isSavingShipData}
                            >
                                {isSavingShipData ? 'Saving...' : 'Save Ship Data Settings'}
                            </button>
                        </div>
                        <table className="table table-bordered align-middle gs-4 gy-3">
                            <thead className="bg-light-primary">
                                <tr className="fw-bold text-gray-800 border-bottom border-gray-200">
                                    <th colSpan={3} className="text-center bg-dark text-white py-3">PARTICULARS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="fw-bold w-50px text-center">1</td>
                                    <td className="fw-bold w-400px">NAME OF THE VESSEL :</td>
                                    <td className="text-primary fw-bold">{selectedVesselObj?.fleet_name || '--'}</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold text-center">2</td>
                                    <td className="fw-bold">IMO NUMBER :</td>
                                    <td>{selectedVesselObj?.imoNumber || '--'}</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold text-center">3</td>
                                    <td className="fw-bold bg-dark text-white" colSpan={2}>TECHNICAL CHARACTERISTICS :</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">a) SHIP TYPE</td>
                                    <td>
                                        <SearchableSelect
                                            className="form-select form-select-sm"
                                            value={selectedCiiShipType}
                                            onChange={(e) => setSelectedCiiShipType(e.target.value)}
                                        >
                                            <option value="">Select CII Ship Type</option>
                                            {getUniqueShipTypeRules().map((shipRule) => (
                                                <option key={shipRule.shipTypeCode} value={shipRule.shipTypeCode}>
                                                    {shipRule.displayName}
                                                </option>
                                            ))}
                                        </SearchableSelect>
                                        <div className="text-muted fs-8 mt-1">
                                            Vessel master type: {selectedVesselObj?.vesselType || '--'}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">b) GROSS TONNAGE (GT)</td>
                                    <td>{generalInfo?.grossTonnage || '--'}</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">c) NET TONNAGE (NT)</td>
                                    <td>{generalInfo?.netTonnage || '--'}</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">d) SUMMER DEADWEIGHT (DT)</td>
                                    <td>{generalInfo?.summerDeadweight || '--'}</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8 fw-bold bg-dark text-white" colSpan={2}>e) POWER OUTPUT (RATED POWER):</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-12">MAIN ENGINE</td>
                                    <td>{propulsion?.mainEngineCapacity || '--'} kW</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-12">AUXILIARY ENGINE</td>
                                    <td>{propulsion?.auxEngineCapacity || '--'} kW</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold text-center">6</td>
                                    <td className="fw-bold">SFOC (t/kWh) :</td>
                                    <td>{propulsion?.sfoc || '--'}</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold text-center">7</td>
                                    <td className="fw-bold">EEDI / Calculated CII Rating:</td>
                                    <td>
                                        {propulsion?.eediRating ? `EEDI: ${propulsion.eediRating}` : ''}
                                        {propulsion?.eediRating ? ' / ' : ''}
                                        CII: {calculatedRating}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="fw-bold text-center">8</td>
                                    <td className="fw-bold bg-dark text-white" colSpan={2}>CII RULE CALCULATIONS :</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">a) IMO Rule Band</td>
                                    <td>{rule?.bandLabel || '--'}</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">b) Capacity Used</td>
                                    <td>{capacity ? `${capacity.toFixed(3)} ${rule?.capacityBasis || ''}` : '--'}</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">c) Reference Line</td>
                                    <td>
                                        {rule ? `a=${Number(rule.referenceA)}, c=${Number(rule.referenceC)}` : '--'}
                                        {referenceCii > 0 ? ` => ${referenceCii.toFixed(4)}` : ''}
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">d) Required CII ({selectedYear})</td>
                                    <td>{requiredCii > 0 ? `${requiredCii.toFixed(4)} (Z=${Number(reduction?.reductionPercent || 0)}%)` : '--'}</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">e) Rating Boundaries A/B/C/D</td>
                                    <td>
                                        {boundaries
                                            ? `${boundaries.a.toFixed(4)} / ${boundaries.b.toFixed(4)} / ${boundaries.c.toFixed(4)} / ${boundaries.d.toFixed(4)}`
                                            : '--'}
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">f) Annual Attained CII</td>
                                    <td>{annualMetrics.attainedCii > 0 ? annualMetrics.attainedCii.toFixed(4) : '--'}</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="ps-8">g) Final Calculated Rating</td>
                                    <td className="fw-bold">{calculatedRating}</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold text-center">9</td>
                                    <td className="fw-bold bg-dark text-white" colSpan={2}>CII CORRECTION FACTORS :</td>
                                </tr>
                                {[
                                    ['fi', 'Ice class / capacity correction factor', 'factorFi'],
                                    ['fm', 'Ship-specific correction factor', 'factorFm'],
                                    ['fc', 'Capacity correction factor', 'factorFc'],
                                    ['fiVSE', 'VSE correction factor', 'factorFiVse'],
                                ].map(([label, description, key]) => (
                                    <tr key={key}>
                                        <td></td>
                                        <td className="ps-8">{label} - {description}</td>
                                        <td>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                className="form-control form-control-sm"
                                                value={(vesselCiiSetting as any)?.[key] ?? ''}
                                                placeholder="1.000000"
                                                onChange={(e) => {
                                                    const cleaned = sanitizeNumericInput(e.target.value);
                                                    setVesselCiiSetting((prev) => ({
                                                        ...(prev || { vesselId: Number(selectedVesselId), shipTypeCode: selectedCiiShipType }),
                                                        [key]: cleaned,
                                                    } as CiiVesselSettingDto));
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                {/* <tr>
                                    <td></td>
                                    <td className="ps-8">Corrected annual CII formula used</td>
                                    <td className="small">
                                        sum_j CF[j] x {'{'}FC[j] - [FCvoyage[j] + TF[j] + (0.75 - 0.03 x yi) x (FCelectrical[j] + FCboiler[j] + FCothers[j])]{'}'} /
                                        [fi x fm x fc x fiVSE x Capacity x (Dt - Dx)]
                                    </td>
                                </tr> */}
                            </tbody>
                        </table>
                    </div>
                );
            }
            case 'voyageData': {
                const totalVoyagePages = Math.max(1, Math.ceil(aggregatedData.length / voyageRowsPerPage));
                const safeVoyagePage = Math.min(voyagePage, totalVoyagePages);
                const { rule: ciiRule, capacity: configuredCapacity, requiredCii: configuredRequiredCii, referenceCii: configuredReferenceCii } = getCiiRuleMetrics();
                const capacity = configuredCapacity || Number(generalInfo?.summerDeadweight) || 50000;
                const requiredCii = configuredRequiredCii || configuredReferenceCii || Number((propulsion as any)?.referenceCii) || Number((propulsion as any)?.requiredCii) || DEFAULT_REFERENCE_CII;
                const dirtyCount = dirtyRowIds.size;
                let cumulativeCorrectedCo2 = 0;
                let cumulativeDistance = 0;
                const progressiveCiiMetrics: ProgressiveCiiRowMetrics[] = aggregatedData.map((row) => {
                    const correctedCo2 = computeCorrectedCo2ForRow(row);

                    cumulativeCorrectedCo2 += correctedCo2;
                    cumulativeDistance += Math.max(0, row.distance - numericValue(row.distanceAdjustment));

                    const attainedCii = capacity > 0 && cumulativeDistance > 0
                        ? (cumulativeCorrectedCo2 * 1000000) / (getCorrectionFactorProduct() * capacity * cumulativeDistance)
                        : 0;
                    const configuredRating = cumulativeDistance > 0 ? getCiiRatingFromRequired(attainedCii, requiredCii, ciiRule) : '--';
                    const ciiRating = cumulativeDistance > 0
                        ? (configuredRating !== '--' ? configuredRating : getCiiRatingFromAttained(attainedCii, requiredCii))
                        : '--';
                    const ciiRatingClass =
                        ciiRating === 'A' || ciiRating === 'B'
                            ? 'text-success fw-bold'
                            : ciiRating === 'C'
                                ? 'text-warning fw-bold'
                                : ciiRating === 'D' || ciiRating === 'E'
                                    ? 'text-danger fw-bold'
                                    : '';

                    return {
                        attainedCii,
                        ciiRating,
                        ciiRatingClass,
                        cumulativeDistance,
                    };
                });
                const paginatedVoyageData = aggregatedData.slice(
                    (safeVoyagePage - 1) * voyageRowsPerPage,
                    safeVoyagePage * voyageRowsPerPage
                );
                const activeDynamicFuelCodes = Array.from(new Set(
                    aggregatedData.flatMap((row) => [
                        ...Object.keys(row.fuelBreakdown || {}),
                        ...Object.keys(row.correctionBreakdown || {}),
                        ...(getConfiguredFuelCode(row.reeferFuelCode) ? [getConfiguredFuelCode(row.reeferFuelCode)] : []),
                    ])
                )).filter((fuelCode) => fuelCode !== 'HSFO' && fuelCode !== 'MGO');
                const unmappedFuelTypes = Array.from(new Set(
                    aggregatedData.flatMap((row) => row.unmappedFuelTypes || [])
                ));

                return (
                    <div className="mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="text-muted fs-7">
                                {dirtyCount > 0 ? `${dirtyCount} unsaved row(s)` : 'All changes saved'}
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={saveDirtyVoyageRows}
                                disabled={dirtyCount === 0 || isSavingVoyageData || !hasUpdateVoyageRowPermission}
                            >
                                {isSavingVoyageData ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                        {(activeDynamicFuelCodes.length > 0 || unmappedFuelTypes.length > 0) && (
                            <div className="alert alert-warning py-2">
                                {activeDynamicFuelCodes.length > 0 && (
                                    <div>
                                        Additional configured fuels used in CII CO2 calculations: {activeDynamicFuelCodes.join(', ')}.
                                    </div>
                                )}
                                {unmappedFuelTypes.length > 0 && (
                                    <div>
                                        Unmapped fuels excluded from CII until configured by Superadmin: {unmappedFuelTypes.join(', ')}.
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="table-responsive" style={{ overflowX: 'auto' }}>
                            <table className="table table-bordered align-middle text-center gs-4 gy-2" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                <thead className="bg-light">
                                    <tr className="fw-bold text-gray-800">
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '50px' }}>Voyage No</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '60px' }}>Leg</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '80px' }}>Vessel Condition<br />"L" or "B" only</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '100px' }}>Port Name / Location</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '100px' }}>Event Type</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '100px' }}>FROM<br />Date & Time in UTC</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '100px' }}>TO<br />Date & Time in UTC</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '80px' }}>STS Voyage<br />Select "Y" or "N"</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '80px' }}>Distance Travelled (nm)</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '80px', backgroundColor: '#ffffcc' }}>Hours at Sea</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '80px', backgroundColor: '#ffffcc' }}>Hours at Anchor / Berth</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '80px' }}>Cargo Carried (MT's)</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '90px' }}>Dx Distance<br />Adjustment (nm)</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '90px' }}>Reefer kWh</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '110px' }}>Reefer Fuel</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '90px' }}>Reefer SFOC<br />(t/kWh)</th>

                                        <th colSpan={2} rowSpan={2} className="align-middle text-center bg-secondary">Average Sulphur Content (%)</th>
                                        <th colSpan={21 + (activeDynamicFuelCodes.length * 8)} className="text-center bg-primary text-white">CONSUMPTION (MT's)</th>

                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '200px' }}>
                                            Remarks
                                            <br />
                                            (To include operations such as 'Cargo Heating', 'Bunkering Operations', 'STS Operations with' vessel's name (irrespective if it is an STS Voyage or not), etc)
                                        </th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '100px', backgroundColor: '#ffffcc' }}>Attained CII after correction<br />(Progressive)</th>
                                        <th rowSpan={3} className="align-middle" style={{ minWidth: '100px', backgroundColor: '#ffffcc' }}>CII Rating after correction<br />(Progressive)</th>
                                    </tr>
                                    <tr className="fw-bold text-gray-800">
                                        <th colSpan={3} className="text-center bg-info text-white">HSFO CONSUMPTION</th>
                                        <th colSpan={3} className="text-center bg-info text-dark" style={{ backgroundColor: '#e0f7fa' }}>MGO CONSUMPTION</th>
                                        {activeDynamicFuelCodes.map((fuelCode) => (
                                            <th key={fuelCode} colSpan={8} className="text-center bg-warning text-dark">{fuelCode} CONSUMPTION / CORRECTION</th>
                                        ))}
                                        <th colSpan={2} className="text-center bg-success text-white">R.O.B.</th>
                                        <th colSpan={2} className="text-center bg-primary text-white">FC (T)</th>
                                        <th colSpan={2} className="text-center bg-primary text-white">FC voyage (T)</th>
                                        <th colSpan={2} className="text-center bg-primary text-white">TF voyage adjustment (T)</th>
                                        <th colSpan={2} className="text-center bg-primary text-white">FC Electrical / Cargo Ops (T)</th>
                                        <th colSpan={3} className="text-center bg-primary text-white">FC Boiler (T)</th>
                                        <th colSpan={2} className="text-center bg-primary text-white">FC Other (T)<br />(MR Tankers Only)</th>
                                    </tr>
                                    <tr className="fw-bold text-gray-800" style={{ fontSize: '0.8rem' }}>
                                        <th className="bg-secondary">HSFO<br />S&lt;0.5%</th>
                                        <th className="bg-secondary">MGO/MDO<br />S&lt;0.1%</th>

                                        <th className="bg-info text-white">M.E.</th>
                                        <th className="bg-info text-white">A.E.</th>
                                        <th className="bg-info text-white">BOILER</th>

                                        <th className="bg-info text-dark" style={{ backgroundColor: '#e0f7fa' }}>M.E.</th>
                                        <th className="bg-info text-dark" style={{ backgroundColor: '#e0f7fa' }}>A.E.</th>
                                        <th className="bg-info text-dark" style={{ backgroundColor: '#e0f7fa' }}>BOILER</th>

                                        {activeDynamicFuelCodes.flatMap((fuelCode) => [
                                            <th key={`${fuelCode}-me`} className="bg-warning text-dark">M.E.</th>,
                                            <th key={`${fuelCode}-ae`} className="bg-warning text-dark">A.E.</th>,
                                            <th key={`${fuelCode}-boiler`} className="bg-warning text-dark">BOILER</th>,
                                            <th key={`${fuelCode}-rob`} className="bg-warning text-dark">ROB</th>,
                                            <th key={`${fuelCode}-fc`} className="bg-warning text-dark">FC</th>,
                                            <th key={`${fuelCode}-fce`} className="bg-warning text-dark">FC Electrical</th>,
                                            <th key={`${fuelCode}-fcb`} className="bg-warning text-dark">FC Boiler</th>,
                                            <th key={`${fuelCode}-fco`} className="bg-warning text-dark">FC Other</th>,
                                        ])}

                                        <th className="bg-success text-white">HSFO</th>
                                        <th className="bg-success text-white">MGO</th>

                                        <th className="bg-primary text-white">HSFO</th>
                                        <th className="bg-primary text-white">MGO</th>

                                        <th className="bg-primary text-white">HSFO</th>
                                        <th className="bg-primary text-white">MGO</th>

                                        <th className="bg-primary text-white">HSFO</th>
                                        <th className="bg-primary text-white">MGO</th>

                                        <th className="bg-primary text-white">HSFO</th>
                                        <th className="bg-primary text-white">MGO</th>

                                        <th className="bg-primary text-white" style={{ minWidth: '100px', whiteSpace: 'normal' }}>Was the Boiler Used for Cargo Heating...</th>
                                        <th className="bg-primary text-white">HSFO</th>
                                        <th className="bg-primary text-white">MGO</th>

                                        <th className="bg-primary text-white">HSFO</th>
                                        <th className="bg-primary text-white">MGO/MDO</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {aggregatedData.length > 0 ? (
                                        paginatedVoyageData.map((row, i) => {
                                            const actualIndex = ((safeVoyagePage - 1) * voyageRowsPerPage) + i;
                                            const progressiveMetrics = progressiveCiiMetrics[actualIndex] || {
                                                attainedCii: 0,
                                                ciiRating: '--',
                                                ciiRatingClass: '',
                                            };
                                            const displayFromDate = row.fromDate;
                                            // FC (T) should be the direct total of the row's consumption buckets.
                                            const fcHsfo = row.meHsfo + row.aeHsfo + row.boilerHsfo;
                                            const fcMgo = row.meMgo + row.aeMgo + row.boilerMgo;
                                            const eventTypeIsB = isBerthEventType(row.eventType);
                                            const fcOtherDisabled = isSailingEventType(row.eventType) || isAnchorEventType(row.eventType);
                                            const stsVoyageIsY = resolveStsVoyageValue(row.stsVoyage) === 'Y';
                                            const boilerDisabledStyle = row.boilerUsed === 'N'
                                                ? { backgroundColor: '#e5e7eb', color: '#6b7280' }
                                                : undefined;
                                            const eventTypeDisabledStyle = eventTypeIsB
                                                ? { backgroundColor: '#e5e7eb', color: '#6b7280' }
                                                : undefined;
                                            const stsVoyageDisabledStyle = stsVoyageIsY
                                                ? { backgroundColor: '#e5e7eb', color: '#6b7280' }
                                                : undefined;
                                            const fcOtherDisabledStyle = fcOtherDisabled
                                                ? { backgroundColor: '#e5e7eb', color: '#6b7280' }
                                                : undefined;

                                            return (
                                                <tr key={`${row.voyageNo || 'voyage'}-${actualIndex}`}>
                                                    <td>{row.voyageNo || (actualIndex + 1)}</td>
                                                    <td>{row.leg || '--'}</td>
                                                    <td>{row.vesselCondition || '--'}</td>
                                                    <td>{row.portNameLocation || '--'}</td>
                                                    <td>{formatEventTypeDisplay(row.eventType)}</td>
                                                    <td>{formatDateTime(displayFromDate)}</td>
                                                    <td>{formatDateTime(row.toDate)}</td>
                                                    <td>
                                                        <SearchableSelect
                                                            className="form-select form-select-sm"
                                                            value={row.stsVoyage ?? ''}
                                                            style={{ minWidth: '90px' }}
                                                            onChange={(e) => {
                                                                const nextSts = e.target.value;
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].stsVoyage = nextSts;
                                                                if (nextSts === 'Y') {
                                                                    const dwt = Number(generalInfo?.summerDeadweight) || 0;
                                                                    const afTankerSts = dwt > 0 ? Math.min(1, 6.1742 * Math.pow(dwt, -0.246)) : 1;
                                                                    const r = newData[actualIndex];
                                                                    const rowHsfo = r.meHsfo + r.aeHsfo + r.boilerHsfo;
                                                                    const rowMgo = r.meMgo + r.aeMgo + r.boilerMgo;
                                                                    const autoTfHsfo = rowHsfo > 0 ? ((1 - afTankerSts) * rowHsfo).toFixed(3) : undefined;
                                                                    const autoTfMgo = rowMgo > 0 ? ((1 - afTankerSts) * rowMgo).toFixed(3) : undefined;

                                                                    newData[actualIndex].fcVoyageHsfo = undefined;
                                                                    newData[actualIndex].fcVoyageMgo = undefined;
                                                                    newData[actualIndex].fcElectricalHsfo = undefined;
                                                                    newData[actualIndex].fcElectricalMgo = undefined;
                                                                    newData[actualIndex].fcOtherHsfo = undefined;
                                                                    newData[actualIndex].fcOtherMgo = undefined;
                                                                    newData[actualIndex].fcBoilerHsfo = undefined;
                                                                    newData[actualIndex].fcBoilerMgo = undefined;
                                                                    newData[actualIndex].reeferKwh = undefined;
                                                                    newData[actualIndex].reeferSfoc = undefined;
                                                                    newData[actualIndex].reeferFuelCode = '';
                                                                    newData[actualIndex].tfHsfo = autoTfHsfo;
                                                                    newData[actualIndex].tfMgo = autoTfMgo;
                                                                    newData[actualIndex].boilerUsed = 'N';
                                                                } else if (nextSts === 'N') {
                                                                    newData[actualIndex].tfHsfo = undefined;
                                                                    newData[actualIndex].tfMgo = undefined;
                                                                }
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        >
                                                            <option value="">Select</option>
                                                            <option value="Y">Y</option>
                                                            <option value="N">N</option>
                                                        </SearchableSelect>
                                                    </td>
                                                    <td>{row.distanceDisplay ?? row.distance.toFixed(1)}</td>
                                                    <td style={{ backgroundColor: '#ffffcc' }}>{row.hoursAtSea.toFixed(1)}</td>
                                                    <td style={{ backgroundColor: '#ffffcc' }}>{row.hoursAtAnchor.toFixed(1)}</td>
                                                    <td style={eventTypeDisabledStyle}>{row.cargoCarried.toFixed(1)}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.distanceAdjustment ?? ''}
                                                            style={{ minWidth: '90px' }}
                                                        onChange={(e) => {
                                                            const cleaned = sanitizeNumericInput(e.target.value);
                                                            const newData = [...aggregatedData];
                                                            newData[actualIndex].distanceAdjustment = cleaned === '' ? undefined : cleaned;
                                                            setAggregatedData(newData);
                                                            markRowDirty(newData[actualIndex]);
                                                        }}
                                                    />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.reeferKwh ?? ''}
                                                            style={{ minWidth: '90px', ...(stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission || stsVoyageIsY}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].reeferKwh = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <SearchableSelect
                                                            className="form-select form-select-sm"
                                                            value={row.reeferFuelCode ?? ''}
                                                            style={{ minWidth: '110px', ...(stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission || stsVoyageIsY}
                                                            onChange={(e) => {
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].reeferFuelCode = e.target.value;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        >
                                                            <option value="">Select</option>
                                                            {(ciiRuleSet?.fuelFactors || []).map((fuel) => (
                                                                <option key={fuel.fuelCode} value={fuel.fuelCode}>{fuel.fuelCode}</option>
                                                            ))}
                                                        </SearchableSelect>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.reeferSfoc ?? ''}
                                                            placeholder="0.0002"
                                                            style={{ minWidth: '90px', ...(stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission || stsVoyageIsY}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].reeferSfoc = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                    </td>

                                                    {/* Average Sulphur Content */}
                                                    <td>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.avgSulphurHsfo ?? ''}
                                                            style={{ minWidth: '90px' }}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].avgSulphurHsfo = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.avgSulphurMgo ?? ''}
                                                            style={{ minWidth: '90px' }}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].avgSulphurMgo = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                    </td>

                                                    {/* Consumption HSFO */}
                                                    <td>{row.meHsfo.toFixed(3)}</td>
                                                    <td>{row.aeHsfo.toFixed(3)}</td>
                                                    <td>{row.boilerHsfo.toFixed(3)}</td>

                                                    {/* Consumption MGO */}
                                                    <td>{row.meMgo.toFixed(3)}</td>
                                                    <td>{row.aeMgo.toFixed(3)}</td>
                                                    <td>{row.boilerMgo.toFixed(3)}</td>

                                                    {activeDynamicFuelCodes.flatMap((fuelCode) => {
                                                        const fuel = row.fuelBreakdown?.[fuelCode];
                                                        const electrical = getCorrectionDisplay(row, fuelCode, 'fcElectrical');
                                                        const boiler = getCorrectionDisplay(row, fuelCode, 'fcBoiler');
                                                        const other = getCorrectionDisplay(row, fuelCode, 'fcOther');
                                                        const renderCorrectionCell = (key: string, item: { value: number; source: string }) => (
                                                            <td key={key} className="fw-bold" style={{ backgroundColor: '#eef6ff' }}>
                                                                {item.value ? item.value.toFixed(3) : '--'}
                                                                {item.source ? <div className="badge badge-light-primary mt-1">{item.source}</div> : null}
                                                            </td>
                                                        );
                                                        return [
                                                            <td key={`${fuelCode}-me`}>{fuel ? fuel.me.toFixed(3) : '--'}</td>,
                                                            <td key={`${fuelCode}-ae`}>{fuel ? fuel.ae.toFixed(3) : '--'}</td>,
                                                            <td key={`${fuelCode}-boiler`}>{fuel ? fuel.boiler.toFixed(3) : '--'}</td>,
                                                            <td key={`${fuelCode}-rob`}>{fuel ? fuel.rob.toFixed(2) : '--'}</td>,
                                                            <td key={`${fuelCode}-fc`} className="fw-bold" style={{ backgroundColor: '#ffffcc' }}>{fuel ? getFuelTotal(fuel).toFixed(3) : '--'}</td>,
                                                            renderCorrectionCell(`${fuelCode}-fce`, electrical),
                                                            renderCorrectionCell(`${fuelCode}-fcb`, boiler),
                                                            renderCorrectionCell(`${fuelCode}-fco`, other),
                                                        ];
                                                    })}

                                                    {/* ROB */}
                                                    <td>{row.hfoRob?.toFixed(2) || '--'}</td>
                                                    <td>{row.mgoRob?.toFixed(2) || '--'}</td>

                                                    {/* FC (T) */}
                                                    <td className="fw-bold" style={{ backgroundColor: '#ffffcc' }}>{fcHsfo.toFixed(3)}</td>
                                                    <td className="fw-bold" style={{ backgroundColor: '#ffffcc' }}>{fcMgo.toFixed(3)}</td>

                                                    {/* FC voyage (T) */}
                                                      <td>
                                                          <input
                                                              type="text"
                                                              inputMode="decimal"
                                                              className="form-control form-control-sm"
                                                              value={row.fcVoyageHsfo ?? ''}
                                                              style={{ minWidth: '90px', ...(stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                              disabled={!hasUpdateVoyageRowPermission || stsVoyageIsY}
                                                              onChange={(e) => {
                                                                  const cleaned = sanitizeNumericInput(e.target.value);
                                                                  const newData = [...aggregatedData];
                                                                  newData[actualIndex].fcVoyageHsfo = cleaned === '' ? undefined : cleaned;
                                                                  setAggregatedData(newData);
                                                                  markRowDirty(newData[actualIndex]);
                                                              }}
                                                          />
                                                      </td>
                                                      <td>
                                                          <input
                                                              type="text"
                                                              inputMode="decimal"
                                                              className="form-control form-control-sm"
                                                              value={row.fcVoyageMgo ?? ''}
                                                              style={{ minWidth: '90px', ...(stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                              disabled={!hasUpdateVoyageRowPermission || stsVoyageIsY}
                                                              onChange={(e) => {
                                                                  const cleaned = sanitizeNumericInput(e.target.value);
                                                                  const newData = [...aggregatedData];
                                                                  newData[actualIndex].fcVoyageMgo = cleaned === '' ? undefined : cleaned;
                                                                  setAggregatedData(newData);
                                                                  markRowDirty(newData[actualIndex]);
                                                              }}
                                                          />
                                                      </td>

                                                    {/* TF voyage adjustment (T) */}
                                                    <td>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.tfHsfo ?? ''}
                                                            style={{ minWidth: '90px', ...(stsVoyageIsY ? { backgroundColor: '#f0fdf4' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].tfHsfo = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.tfMgo ?? ''}
                                                            style={{ minWidth: '90px', ...(stsVoyageIsY ? { backgroundColor: '#f0fdf4' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].tfMgo = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                    </td>

                                                    {/* FC Electrical / Cargo Ops (T) */}
                                                    <td>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.fcElectricalHsfo ?? ''}
                                                            style={{ minWidth: '90px', ...(stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission || stsVoyageIsY}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].fcElectricalHsfo = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                        {getCorrectionSource(row, 'HSFO', 'fcElectrical') ? (
                                                            <div className="badge badge-light-primary mt-1">{getCorrectionSource(row, 'HSFO', 'fcElectrical')}</div>
                                                        ) : null}
                                                        {getReeferElectricalForFuel(row, 'HSFO') > 0 ? (
                                                            <div className="small text-muted">Total {getCorrectionValueForFuel(row, 'HSFO', 'fcElectrical').toFixed(3)}</div>
                                                        ) : null}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.fcElectricalMgo ?? ''}
                                                            style={{ minWidth: '90px', ...(stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission || stsVoyageIsY}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].fcElectricalMgo = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                        {getCorrectionSource(row, 'MGO', 'fcElectrical') ? (
                                                            <div className="badge badge-light-primary mt-1">{getCorrectionSource(row, 'MGO', 'fcElectrical')}</div>
                                                        ) : null}
                                                        {getReeferElectricalForFuel(row, 'MGO') > 0 ? (
                                                            <div className="small text-muted">Total {getCorrectionValueForFuel(row, 'MGO', 'fcElectrical').toFixed(3)}</div>
                                                        ) : null}
                                                    </td>

                                                    {/* FC Boiler (T) */}
                                                    <td>
                                                        <SearchableSelect
                                                            className="form-select form-select-sm"
                                                            value={row.boilerUsed || ''}
                                                            style={{ minWidth: '90px', ...(stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission || stsVoyageIsY}
                                                            onChange={(e) => {
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].boilerUsed = e.target.value;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        >
                                                            <option value="">Select</option>
                                                            <option value="Y">Y</option>
                                                            <option value="N">N</option>
                                                        </SearchableSelect>
                                                    </td>
                                                    <td style={boilerDisabledStyle || stsVoyageDisabledStyle}>
                                                        {getCorrectionValueForFuel(row, 'HSFO', 'fcBoiler').toFixed(3)}
                                                        {getCorrectionSource(row, 'HSFO', 'fcBoiler') ? (
                                                            <div className="badge badge-light-primary mt-1">{getCorrectionSource(row, 'HSFO', 'fcBoiler')}</div>
                                                        ) : null}
                                                    </td>
                                                    <td style={boilerDisabledStyle || stsVoyageDisabledStyle}>
                                                        {getCorrectionValueForFuel(row, 'MGO', 'fcBoiler').toFixed(3)}
                                                        {getCorrectionSource(row, 'MGO', 'fcBoiler') ? (
                                                            <div className="badge badge-light-primary mt-1">{getCorrectionSource(row, 'MGO', 'fcBoiler')}</div>
                                                        ) : null}
                                                    </td>

                                                    {/* FC Other (T) */}
                                                    <td style={fcOtherDisabledStyle}>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.fcOtherHsfo ?? ''}
                                                            style={{ minWidth: '90px', ...(fcOtherDisabled || stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission || fcOtherDisabled || stsVoyageIsY}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].fcOtherHsfo = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                        {getCorrectionSource(row, 'HSFO', 'fcOther') ? (
                                                            <div className="badge badge-light-primary mt-1">{getCorrectionSource(row, 'HSFO', 'fcOther')}</div>
                                                        ) : null}
                                                    </td>
                                                    <td style={fcOtherDisabledStyle}>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="form-control form-control-sm"
                                                            value={row.fcOtherMgo ?? ''}
                                                            style={{ minWidth: '90px', ...(fcOtherDisabled || stsVoyageIsY ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}) }}
                                                            disabled={!hasUpdateVoyageRowPermission || fcOtherDisabled || stsVoyageIsY}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeNumericInput(e.target.value);
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].fcOtherMgo = cleaned === '' ? undefined : cleaned;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                        {getCorrectionSource(row, 'MGO', 'fcOther') ? (
                                                            <div className="badge badge-light-primary mt-1">{getCorrectionSource(row, 'MGO', 'fcOther')}</div>
                                                        ) : null}
                                                    </td>

                                                    {/* Remarks & Attained CII */}
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            value={row.remarks ?? ''}
                                                            style={{ minWidth: '180px' }}
                                                            disabled={!hasUpdateVoyageRowPermission}
                                                            onChange={(e) => {
                                                                const newData = [...aggregatedData];
                                                                newData[actualIndex].remarks = e.target.value;
                                                                setAggregatedData(newData);
                                                                markRowDirty(newData[actualIndex]);
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="fw-bold" style={{ backgroundColor: '#ffffcc' }}>{(progressiveMetrics.cumulativeDistance ?? 0) > 0 ? progressiveMetrics.attainedCii.toFixed(4) : '--'}</td>
                                                    <td className={progressiveMetrics.ciiRatingClass} style={{ backgroundColor: '#ffffcc' }}>{progressiveMetrics.ciiRating}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={42 + (activeDynamicFuelCodes.length * 8)} className="text-center py-10 text-muted">No voyage data available for the selected period.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {aggregatedData.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <div className="text-muted fs-7">
                                    Showing {((safeVoyagePage - 1) * voyageRowsPerPage) + 1}
                                    {' '}to{' '}
                                    {Math.min(safeVoyagePage * voyageRowsPerPage, aggregatedData.length)}
                                    {' '}of {aggregatedData.length} rows
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light-primary"
                                        onClick={() => setVoyagePage(prev => Math.max(1, prev - 1))}
                                        disabled={safeVoyagePage === 1}
                                    >
                                        Previous
                                    </button>
                                    <span className="fw-semibold">
                                        Page {safeVoyagePage} of {totalVoyagePages}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light-primary"
                                        onClick={() => setVoyagePage(prev => Math.min(totalVoyagePages, prev + 1))}
                                        disabled={safeVoyagePage === totalVoyagePages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
            case 'bunkerReport': {
                const bunkerFuelCodes = Array.from(new Set(
                    bunkerTabRows.flatMap((row) => Object.keys(row.fuelReceived || {}))
                ));
                return (
                    <div className="mt-4">
                        <table className='table table-bordered align-middle gs-4 gy-3'>
                            <thead className="bg-light">
                                <tr className="fw-bold text-gray-800 border-bottom border-gray-200">
                                    <th rowSpan={2} className="align-middle">DATE</th>
                                    <th colSpan={Math.max(1, bunkerFuelCodes.length)} className="text-center bg-light-success">BUNKER RECEIVED</th>
                                </tr>
                                <tr className="fw-bold text-gray-800">
                                    {bunkerFuelCodes.length > 0 ? (
                                        bunkerFuelCodes.map((fuelCode) => <th key={fuelCode} className="text-center">{fuelCode} (MT's)</th>)
                                    ) : (
                                        <th className="text-center">Fuel (MT's)</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {bunkerTabRows.length > 0 ? (
                                    bunkerTabRows.map((data, index) => (
                                        <tr key={index}>
                                            <td>{formatDate(data.date)}</td>
                                            {bunkerFuelCodes.length > 0 ? (
                                                bunkerFuelCodes.map((fuelCode) => (
                                                    <td key={fuelCode} className="text-center">{Number(data.fuelReceived?.[fuelCode] || 0).toFixed(2)}</td>
                                                ))
                                            ) : (
                                                <td className="text-center">0.00</td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={1 + Math.max(1, bunkerFuelCodes.length)} className="text-center py-10 text-muted">No bunker data found for this period.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                );
            }
            case 'cargoReceived':
                return (
                    <div className="mt-4">
                        <h4 className="fw-bold mb-3">CARGO RECEIVED</h4>
                        <table className="table table-bordered align-middle gs-4 gy-3">
                            <thead className="bg-light">
                                <tr className="fw-bold text-gray-800">
                                    <th>DATE (dd-mmm-yy)</th>
                                    <th>PORT</th>
                                    <th>CARGO NAME</th>
                                    <th>QUANTITY (MT's)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cargoOps.length > 0 ? (
                                    cargoOps.map((op, i) => (
                                        <tr key={i}>
                                            <td>{formatDate(op.createdDateTime)}</td>
                                            <td>{op.loadPorts || op.dischargePorts || '--'}</td>
                                            <td>{op.cargoName || '--'}</td>
                                            <td>{op.totalCargoQtyMt?.toFixed(2) || '0.00'}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={4} className="text-center py-10 text-muted">No cargo operation data found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                );
            case 'quarterlySummary': {
                const quarters = [
                    { key: 'q1', name: '1st Quarter', start: '01-01', end: '03-31' },
                    { key: 'q2', name: '2nd Quarter', start: '04-01', end: '06-30' },
                    { key: 'q3', name: '3rd Quarter', start: '07-01', end: '09-30' },
                    { key: 'q4', name: '4th Quarter', start: '10-01', end: '12-31' },
                ];
                const quarterlyMetrics = quarters.map((q) => ({
                    ...q,
                    metrics: buildQuarterlyMetrics(`${selectedYear}-${q.start}`, `${selectedYear}-${q.end}`),
                    rows: aggregatedData.filter(row => row.fromDate >= `${selectedYear}-${q.start}` && row.fromDate <= `${selectedYear}-${q.end}`),
                }));
                const quarterlyDynamicFuelCodes = getFuelCodesInRows(aggregatedData).filter((fuelCode) => fuelCode !== 'HSFO' && fuelCode !== 'MGO');

                const renderQuarterValue = (
                    selector: (metrics: QuarterlyMetrics) => number,
                    digits = 1
                ) => quarterlyMetrics.map((quarter) => (
                    <td key={quarter.key} style={{ backgroundColor: '#d9ff33', border: '1.5px solid #1f2937', padding: '12px 14px' }}>
                        {selector(quarter.metrics).toFixed(digits)}
                    </td>
                ));

                const sectionLabelStyle = {
                    backgroundColor: '#0b2c6b',
                    border: '2px solid #111827',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
                    padding: '18px 16px',
                    letterSpacing: '0.02em',
                } as const;

                const subLabelStyle = {
                    backgroundColor: '#7a7a7a',
                    border: '2px solid #111827',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
                    padding: '14px 16px',
                    letterSpacing: '0.02em',
                } as const;

                const quarterHeaderStyle = {
                    border: '2px solid #111827',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
                    padding: '12px 14px',
                } as const;

                const spacedRowStyle = {
                    height: '58px',
                } as const;

                const sectionStartStyle = {
                    borderTop: '10px solid #ffffff',
                } as const;

                return (
                    <div className="mt-4">
                        <div className="table-responsive">
                            <table className="table table-bordered align-middle text-center" style={{ minWidth: '980px', border: '2px solid #111827' }}>
                                <thead>
                                    <tr>
                                        <th colSpan={2} className="fw-bold text-start bg-white" style={{ border: '2px solid #111827' }}>
                                            YEAR : <span className="text-primary">{selectedYear}</span>
                                        </th>
                                        <th colSpan={4} className="fw-bold text-start bg-white" style={{ border: '2px solid #111827' }}>
                                            VESSEL : <span className="text-primary">{currentUser?.vessel?.fleet_name || vessels.find(v => v.id === selectedVesselId)?.fleet_name || '--'}</span>
                                        </th>
                                    </tr>
                                    <tr>
                                        <th colSpan={6} className="text-center fw-bold text-primary" style={{ backgroundColor: '#ffffff', fontSize: '1.3rem', border: '2px solid #111827' }}>
                                            S.E.E.M.P. QUARTERLY DATA OVERVIEW
                                        </th>
                                    </tr>
                                    <tr className="fw-bold text-white">
                                        <th className="bg-primary" style={quarterHeaderStyle}></th>
                                        <th className="bg-primary" style={quarterHeaderStyle}></th>
                                        {quarterlyMetrics.map((quarter) => (
                                            <th key={quarter.key} className="bg-secondary" style={quarterHeaderStyle}>{quarter.name}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={sectionStartStyle}>
                                        <td rowSpan={2} className="fw-bold text-white align-middle" style={sectionLabelStyle}>DISTANCE (nm)</td>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>LOADED</td>
                                        {renderQuarterValue((metrics) => metrics.loadedDistance, 1)}
                                    </tr>
                                    <tr style={spacedRowStyle}>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>BALLAST</td>
                                        {renderQuarterValue((metrics) => metrics.ballastDistance, 1)}
                                    </tr>
                                    <tr style={sectionStartStyle}>
                                        <td rowSpan={2} className="fw-bold text-white align-middle" style={sectionLabelStyle}>FUEL CONSUMED (MT&apos;s)</td>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>HSFO S &lt; 0.5%</td>
                                        {renderQuarterValue((metrics) => metrics.fuelConsumedHsfo, 1)}
                                    </tr>
                                    <tr style={spacedRowStyle}>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>MGO/MDO S&lt;0.1%</td>
                                        {renderQuarterValue((metrics) => metrics.fuelConsumedMgo, 1)}
                                    </tr>
                                    {quarterlyDynamicFuelCodes.map((fuelCode) => (
                                        <tr key={fuelCode} style={spacedRowStyle}>
                                            <td className="fw-bold text-white align-middle" style={sectionLabelStyle}>FUEL CONSUMED (MT&apos;s)</td>
                                            <td className="fw-bold text-white text-start" style={subLabelStyle}>{fuelCode}</td>
                                            {quarterlyMetrics.map((quarter) => (
                                                <td key={quarter.key} style={{ backgroundColor: '#d9ff33', border: '1.5px solid #1f2937', padding: '12px 14px' }}>
                                                    {sumFuelConsumptionForRows(quarter.rows, fuelCode).toFixed(1)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    <tr style={sectionStartStyle}>
                                        <td rowSpan={2} className="fw-bold text-white align-middle" style={sectionLabelStyle}>TOTAL DAYS</td>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>SAILING &amp; DRIFTING</td>
                                        {renderQuarterValue((metrics) => metrics.totalDaysSea, 1)}
                                    </tr>
                                    <tr style={spacedRowStyle}>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>AT BERTH &amp; AT ANCHOR</td>
                                        {renderQuarterValue((metrics) => metrics.totalDaysAnchor, 1)}
                                    </tr>
                                    <tr style={sectionStartStyle}>
                                        <td colSpan={2} className="fw-bold text-white text-start" style={sectionLabelStyle}>CARGO CARRIED (t)</td>
                                        {renderQuarterValue((metrics) => metrics.cargoCarried, 2)}
                                    </tr>
                                    <tr style={sectionStartStyle}>
                                        <td colSpan={2} className="fw-bold text-white text-start" style={sectionLabelStyle}>TRANSPORT WORK (t.nm):</td>
                                        {renderQuarterValue((metrics) => metrics.transportWork, 0)}
                                    </tr>
                                    <tr style={sectionStartStyle}>
                                        <td rowSpan={5} className="fw-bold text-white align-middle" style={sectionLabelStyle}>EMMISSIONS</td>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>Sox</td>
                                        {renderQuarterValue((metrics) => metrics.sox, 1)}
                                    </tr>
                                    <tr style={spacedRowStyle}>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>PM</td>
                                        {renderQuarterValue((metrics) => metrics.pm, 1)}
                                    </tr>
                                    <tr style={spacedRowStyle}>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>Nox</td>
                                        {renderQuarterValue((metrics) => metrics.nox, 1)}
                                    </tr>
                                    <tr style={spacedRowStyle}>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>CO</td>
                                        {renderQuarterValue((metrics) => metrics.co, 1)}
                                    </tr>
                                    <tr style={spacedRowStyle}>
                                        <td className="fw-bold text-white text-start" style={subLabelStyle}>CO2</td>
                                        {renderQuarterValue((metrics) => metrics.totalCo2, 1)}
                                    </tr>
                                    <tr style={sectionStartStyle}>
                                        <td colSpan={2} className="fw-bold text-white text-start" style={sectionLabelStyle}>EEOI (gCO2 / t.nm):</td>
                                        {renderQuarterValue((metrics) => metrics.eeoi, 3)}
                                    </tr>
                                    <tr style={sectionStartStyle}>
                                        <td colSpan={2} className="fw-bold text-white text-start" style={sectionLabelStyle}>Attained CII (gCO2 / dwt.nm):</td>
                                        {renderQuarterValue((metrics) => metrics.attainedCii, 3)}
                                    </tr>
                                    <tr style={sectionStartStyle}>
                                        <td colSpan={2} className="fw-bold text-white text-start" style={sectionLabelStyle}>Reference CII (gCO2 / dwt.nm):</td>
                                        {renderQuarterValue((metrics) => metrics.referenceCii, 3)}
                                    </tr>
                                    <tr style={sectionStartStyle}>
                                        <td colSpan={2} className="fw-bold text-white text-start" style={sectionLabelStyle}>
                                            CII Rating per quarter:
                                            <div className="fw-normal small">(before any correction)</div>
                                        </td>
                                        {quarterlyMetrics.map((quarter) => (
                                            <td key={quarter.key} style={{ backgroundColor: '#d9ff33', border: '1.5px solid #1f2937' }} className="fw-bold text-primary">
                                                {quarter.metrics.quarterlyRating}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            }
            case 'annualSummary': {
                const annualMetrics = buildAnnualOverviewMetrics(`${selectedYear}-01-01`, `${selectedYear}-12-31`);
                const annualRows = aggregatedData.filter(row => row.fromDate >= `${selectedYear}-01-01` && row.fromDate <= `${selectedYear}-12-31`);
                const annualDynamicFuelCodes = getFuelCodesInRows(annualRows).filter((fuelCode) => fuelCode !== 'HSFO' && fuelCode !== 'MGO');
                const vesselName = currentUser?.vessel?.fleet_name || vessels.find(v => v.id === selectedVesselId)?.fleet_name || '--';
                const annualHeaderStyle = {
                    backgroundColor: '#0b2c6b',
                    color: '#ffffff',
                    border: '2px solid #111827',
                    padding: '16px 18px',
                    letterSpacing: '0.02em',
                } as const;
                const annualLabelStyle = {
                    backgroundColor: '#7a7a7a',
                    color: '#ffffff',
                    border: '2px solid #111827',
                    padding: '16px 18px',
                    textAlign: 'left' as const,
                    letterSpacing: '0.02em',
                };
                const annualValueStyle = {
                    backgroundColor: '#d9ff33',
                    border: '1.5px solid #1f2937',
                    padding: '16px 18px',
                    fontWeight: 700,
                } as const;
                const annualSectionStartStyle = {
                    borderTop: '10px solid #ffffff',
                    height: '62px',
                } as const;
                const annualSpacedRowStyle = {
                    height: '58px',
                } as const;

                return (
                    <div className="mt-4">
                        <div className="table-responsive">
                            <table className="table table-bordered align-middle text-center" style={{ minWidth: '980px', border: '2px solid #111827' }}>
                                <thead>
                                    <tr>
                                        <th colSpan={2} className="fw-bold text-start bg-white" style={{ border: '2px solid #111827' }}>
                                            YEAR : <span className="text-primary">{selectedYear}</span>
                                        </th>
                                        <th colSpan={3} className="fw-bold text-start bg-white" style={{ border: '2px solid #111827' }}>
                                            VESSEL : <span className="text-primary">{vesselName}</span>
                                        </th>
                                    </tr>
                                    <tr>
                                        <th colSpan={5} className="text-center fw-bold text-primary" style={{ backgroundColor: '#ffffff', fontSize: '1.3rem', border: '2px solid #111827' }}>
                                            S.E.E.M.P. ANNUAL DATA OVERVIEW
                                        </th>
                                    </tr>
                                    <tr>
                                        <th colSpan={5} style={annualHeaderStyle}>TOTAL AGGREGATED AMOUNT OF EACH FUEL TYPE CONSUMED :</th>
                                    </tr>
                                    <tr className="fw-bold">
                                        <th style={annualLabelStyle}></th>
                                        <th style={annualLabelStyle}>FC</th>
                                        <th style={annualLabelStyle}>FC Voyage</th>
                                        <th style={annualLabelStyle}>FC Boiler</th>
                                        <th style={annualLabelStyle}>FC Other</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={annualSectionStartStyle}>
                                        <td style={annualLabelStyle}>HSFO (S &lt; 0.5%)</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalFcHsfo.toFixed(2)}</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalFcVoyageHsfo.toFixed(2)}</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalFcBoilerHsfo.toFixed(2)}</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalFcOtherHsfo.toFixed(2)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td style={annualLabelStyle}>MGO/MDO (S &lt; 0.1%)</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalFcMgo.toFixed(2)}</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalFcVoyageMgo.toFixed(2)}</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalFcBoilerMgo.toFixed(2)}</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalFcOtherMgo.toFixed(2)}</td>
                                    </tr>
                                    {annualDynamicFuelCodes.map((fuelCode) => (
                                        <tr key={fuelCode} style={annualSpacedRowStyle}>
                                            <td style={annualLabelStyle}>{fuelCode}</td>
                                            <td style={annualValueStyle}>{sumFuelConsumptionForRows(annualRows, fuelCode).toFixed(2)}</td>
                                            <td style={annualValueStyle}>--</td>
                                            <td style={annualValueStyle}>{annualRows.reduce((acc, row) => acc + (row.fuelBreakdown?.[fuelCode]?.boiler || 0), 0).toFixed(2)}</td>
                                            <td style={annualValueStyle}>--</td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <th colSpan={5} style={annualHeaderStyle}>TOTAL EMMISION REPORT</th>
                                    </tr>
                                    <tr className="fw-bold">
                                        <th style={annualLabelStyle}>TOTAL</th>
                                        <th style={annualLabelStyle}>CO2</th>
                                        <th style={annualLabelStyle}>SOx</th>
                                        <th style={annualLabelStyle}>Nox</th>
                                        <th style={annualLabelStyle}>CO</th>
                                    </tr>
                                    <tr style={annualSectionStartStyle}>
                                        <td style={annualLabelStyle}>TOTAL</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalCo2.toFixed(2)}</td>
                                        <td style={annualValueStyle}>{annualMetrics.sox.toFixed(2)}</td>
                                        <td style={annualValueStyle}>{annualMetrics.nox.toFixed(2)}</td>
                                        <td style={annualValueStyle}>{annualMetrics.co.toFixed(2)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>PM</td>
                                        <td style={annualValueStyle}>{annualMetrics.pm.toFixed(2)}</td>
                                    </tr>
                                    <tr style={annualSectionStartStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>TOTAL DISTANCE TRAVELLED (nm):</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalDistance.toFixed(1)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>TOTAL DAYS SPENT AT SEA:</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalDaysSea.toFixed(2)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>TOTAL DAYS SPENT IN PORT:</td>
                                        <td style={annualValueStyle}>{annualMetrics.totalDaysPort.toFixed(2)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>TOTAL TRANSPORT WORK (t.nm):</td>
                                        <td style={annualValueStyle}>{annualMetrics.transportWork.toFixed(0)}</td>
                                    </tr>
                                    <tr>
                                        <th colSpan={5} style={annualHeaderStyle}>AVERAGE ENERGY EFFICIENCY</th>
                                    </tr>
                                    <tr style={annualSectionStartStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>FUEL CONSUMPTION PER DISTANCE (kg./nm):</td>
                                        <td style={annualValueStyle}>{annualMetrics.fuelPerDistance.toFixed(2)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>FUEL CONSUMPTION PER TRANSPORT WORK (g./t.nm):</td>
                                        <td style={annualValueStyle}>{annualMetrics.fuelPerTransportWork.toFixed(2)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>CO2 EMISSION PER TRANSPORT WORK (kg./nm):</td>
                                        <td style={annualValueStyle}>{annualMetrics.co2PerDistance.toFixed(2)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>EEOI (gCO2 / t.nm):</td>
                                        <td style={annualValueStyle}>{annualMetrics.eeoi.toFixed(3)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>CII (gCO2 / dwt.nm):</td>
                                        <td style={annualValueStyle}>{annualMetrics.attainedCii.toFixed(3)}</td>
                                    </tr>
                                    <tr style={annualSpacedRowStyle}>
                                        <td colSpan={4} style={annualLabelStyle}>CII Rating:</td>
                                        <td style={annualValueStyle}>{annualMetrics.ciiRating}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            }
            case 'ciiRules': {
                const canEditRules = isSuperAdmin();
                const items = getRuleEditorItems();
                const editor = getRuleEditorObject();
                return (
                    <div className="mt-4">
                        <div className="alert alert-info">
                            <div className="fw-bold mb-1">CII Rules Admin</div>
                            <div>
                                These values drive Ship Data reference CII, yearly required CII, rating boundaries, fuel CO2 factors,
                                dynamic fuel matching, and correction year index. Save changes here, then revisit Ship Data/Voyage Data.
                            </div>
                        </div>
                        {!canEditRules && (
                            <div className="alert alert-light-warning border-warning">
                                Viewing only. CII rule editing is reserved for Super Admin users, but the loaded rules still drive the
                                calculations on the rest of this page.
                            </div>
                        )}
                        <div className="row g-4">
                            <div className="col-lg-5">
                                <div className="card border">
                                    <div className="card-body">
                                        <label className="form-label fw-bold text-gray-800" style={{ color: '#1f2937' }}>Rule Type</label>
                                        <SearchableSelect
                                            className="form-select mb-3"
                                            value={ruleEditorType}
                                            onChange={(e) => setRuleEditorType(e.target.value as 'fuel' | 'year' | 'ship')}
                                            disabled={!canEditRules}
                                        >
                                            <option value="fuel">Fuel Factors</option>
                                            <option value="year">Yearly Reduction Factors</option>
                                            <option value="ship">Ship Type Rules</option>
                                        </SearchableSelect>
                                        <label className="form-label fw-bold text-gray-800" style={{ color: '#1f2937' }}>Existing Rule</label>
                                        <SearchableSelect
                                            className="form-select"
                                            value={selectedRuleId}
                                            onChange={(e) => loadRuleEditorItem(e.target.value)}
                                            disabled={!canEditRules}
                                        >
                                            <option value="">Create New / Select Existing</option>
                                            {items.map((item: any) => (
                                                <option key={item.id} value={item.id}>
                                                    {ruleEditorType === 'fuel'
                                                        ? `${item.fuelCode} - ${item.displayName}`
                                                        : ruleEditorType === 'year'
                                                            ? `${item.reportingYear} - Z ${item.reductionPercent}%`
                                                            : `${item.displayName} / ${item.bandLabel}`}
                                                </option>
                                            ))}
                                        </SearchableSelect>
                                        {canEditRules && ruleEditorType !== 'ship' ? (
                                            <button type="button" className="btn btn-sm btn-light-primary mt-3" onClick={startNewRule}>
                                                New Rule
                                            </button>
                                        ) : canEditRules ? (
                                            <>
                                                <button type="button" className="btn btn-sm btn-light-primary mt-3" onClick={duplicateSelectedShipRuleBand}>
                                                    Duplicate Selected Band
                                                </button>
                                                <div className="text-muted fs-8 mt-2">
                                                    To add another DWT band, select that ship type's existing rule, duplicate it, then enter a new Band Key and band values.
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="card border mt-4">
                                    <div className="card-body">
                                        <div className="fw-bold mb-2">Formula Reference</div>
                                        <div className="small text-muted">
                                            <div>Reference CII = a x Capacity^(-c)</div>
                                            <div>Required CII = Reference CII x (1 - Z/100)</div>
                                            <div>Rating boundaries = Required CII x d1/d2/d3/d4</div>
                                            <div>Corrected CII numerator = sum_j CF[j] x corrected fuel[j]</div>
                                            <div>Corrected fuel[j] = FC[j] - FCvoyage[j] - TF[j] - ((0.75 - 0.03 x yi) x (FCelectrical[j] + FCboiler[j] + FCothers[j]))</div>
                                            <div>Corrected CII denominator = fi x fm x fc x fiVSE x Capacity x (Dt - Dx)</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="card border">
                                    <div className="card-body">
                                        <div className="fw-bold mb-3">Rule Details</div>
                                        {ruleEditorType === 'fuel' && (
                                            <div className="row g-3">
                                                        {[
                                                            ['fuelCode', 'Fuel Code', 'VLSFO'],
                                                            ['displayName', 'Display Name', 'Very Low Sulphur Fuel Oil'],
                                                            ['aliases', 'Accepted Report Names', 'VLSFO,VLS FO'],
                                                            ['co2Factor', 'CO2 Factor (tCO2/t fuel)', '3.114'],
                                                            ['pmFactor', 'PM Factor', '6.7'],
                                                        ].map(([field, label, placeholder]) => (
                                                            <div className="col-md-6" key={field}>
                                                                <label className="form-label fw-semibold text-gray-800" style={{ color: '#1f2937' }}>{label}</label>
                                                                <input className="form-control" value={editor[field] ?? ''} placeholder={placeholder} onChange={(e) => setRuleEditorField(field, e.target.value)} disabled={!canEditRules} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {ruleEditorType === 'year' && (
                                            <div className="row g-3">
                                                        {[
                                                            ['reportingYear', 'Reporting Year', '2027'],
                                                            ['reductionPercent', 'Reduction Z %', '13.625'],
                                                            ['correctionYearIndex', 'Correction Year Index yi', '4'],
                                                        ].map(([field, label, placeholder]) => (
                                                            <div className="col-md-4" key={field}>
                                                                <label className="form-label fw-semibold text-gray-800" style={{ color: '#1f2937' }}>{label}</label>
                                                                <input className="form-control" value={editor[field] ?? ''} placeholder={placeholder} onChange={(e) => setRuleEditorField(field, e.target.value)} disabled={!canEditRules} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {ruleEditorType === 'ship' && (
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold text-gray-800" style={{ color: '#1f2937' }}>Ship Type Code</label>
                                                    <input className="form-control bg-light" value={editor.shipTypeCode ?? ''} readOnly />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold text-gray-800" style={{ color: '#1f2937' }}>Display Name</label>
                                                    <input className="form-control bg-light" value={editor.displayName ?? ''} readOnly />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold text-gray-800" style={{ color: '#1f2937' }}>Band Key</label>
                                                    <input className="form-control" value={editor.bandKey ?? ''} placeholder="GE_279000" onChange={(e) => setRuleEditorField('bandKey', e.target.value)} disabled={!canEditRules} />
                                                    <div className="form-text">Unique short name for this DWT band, for example GE_279000 or LT_65000.</div>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold text-gray-800" style={{ color: '#1f2937' }}>Band Label</label>
                                                    <input className="form-control" value={editor.bandLabel ?? ''} placeholder="279,000 DWT and above" onChange={(e) => setRuleEditorField('bandLabel', e.target.value)} disabled={!canEditRules} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold text-gray-800" style={{ color: '#1f2937' }}>Capacity Basis</label>
                                                    <SearchableSelect className="form-select" value={editor.capacityBasis ?? 'DWT'} onChange={(e) => setRuleEditorField('capacityBasis', e.target.value)} disabled={!canEditRules}>
                                                        <option value="DWT">Use vessel DWT</option>
                                                        <option value="GT">Use vessel GT</option>
                                                        <option value="FIXED">Use fixed capacity below</option>
                                                    </SearchableSelect>
                                                </div>
                                                {[
                                                    ['minDwt', 'Minimum DWT (inclusive)', '65000'],
                                                    ['maxDwt', 'Maximum DWT (exclusive)', '279000'],
                                                    ['fixedCapacity', 'Fixed Capacity', '279000'],
                                                    ['referenceA', 'Reference a', '5247'],
                                                    ['referenceC', 'Reference c', '0.610'],
                                                    ['ratingD1', 'A Boundary Multiplier d1', '0.82'],
                                                    ['ratingD2', 'B Boundary Multiplier d2', '0.93'],
                                                    ['ratingD3', 'D Boundary Multiplier d3', '1.08'],
                                                    ['ratingD4', 'E Boundary Multiplier d4', '1.28'],
                                                ].map(([field, label, placeholder]) => (
                                                    <div className="col-md-6" key={field}>
                                                        <label className="form-label fw-semibold text-gray-800" style={{ color: '#1f2937' }}>{label}</label>
                                                        <input className="form-control" value={editor[field] ?? ''} placeholder={placeholder} onChange={(e) => setRuleEditorField(field, e.target.value)} disabled={!canEditRules} />
                                                    </div>
                                                ))}
                                                <div className="col-12">
                                                    <div className="alert alert-light border mb-0">
                                                        Leave Minimum DWT empty for "below max". Leave Maximum DWT empty for "min and above".
                                                        Use Capacity Basis FIXED only when IMO says capacity is capped at a fixed value.
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="d-flex justify-content-end mt-3">
                                    {canEditRules && (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={saveRuleEditorItem}
                                            disabled={isSavingRuleEditor}
                                        >
                                            {isSavingRuleEditor ? 'Saving...' : 'Save Rule'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }
            default:
                return null;
        }
    };

    return (
        <div className="card shadow-sm">
            <ToastContainer position="top-center" autoClose={2000} hideProgressBar={false} newestOnTop style={{ top: '5rem' }} />
            <div className="card-header border-0 pt-5">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">SEEMP Data Reporting (CII / EEOI)</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">Quarterly and Annual compliance tracking</span>
                    </h3>
                    <div className="d-flex gap-3">
                        <div className="d-flex align-items-center">
                            <label className="fw-bold me-2">Year:</label>
                            <SearchableSelect
                                className="form-select form-select-solid w-120px"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                            >
                                {years.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </SearchableSelect>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body py-3">
                {loading && (
                    <div className="d-flex justify-content-center my-4">
                        <div className="spinner-border text-primary" role="status">
                            <div className="visually-hidden">Loading...</div>
                        </div>
                    </div>
                )}
                <ul className="nav nav-tabs nav-line-tabs mb-5 fs-6">
                    {tabs.map((tab) => (
                        <li className="nav-item" key={tab.id}>
                            <a
                                className={`nav-link text-active-primary cursor-pointer ${activeTab === tab.id ? 'active fw-bold' : ''
                                    }`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </a>
                        </li>
                    ))}
                </ul>
                <div className="tab-content">
                    {renderTable()}
                </div>
            </div>
        </div>
    );
};

export default CiiSummary;
